/**
 * ElonRouter Admin — Gerenciamento de chaves LLM + roteamento inteligente
 * 
 * Features:
 * - CRUD de chaves LLM (OpenAI, Anthropic, Google, Mistral, Groq, etc)
 * - Configurações padrão de roteamento
 * - Configurações por usuário (override LLM + chaves)
 * - Inteligência de roteamento (menor custo, melhor resultado por ação)
 * - Dashboard de uso e custos
 * 
 * UI/UX Redesign v2 — March 2026
 * - Responsive-first layout
 * - Toast notifications (no more alert())
 * - Skeleton loading states
 * - Better hierarchy & spacing
 * - Collapsible token creation
 * - Mobile-friendly tabs (scroll + icons)
 * - Improved empty states & feedback
 */

import { useState, useEffect, useRef } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../api/client';
import { BridgeTab } from './BridgeConfig';
import { BridgeMonitoringTab } from './BridgeMonitoring';
import { useTabRoute } from '../hooks/useTabRoute';
import { T } from '../styles/tokens';
import { Button, Input, Select, ListSkeleton, Card, SectionHeader, EmptyState as UiEmptyState, Modal, StatusBadge, Badge, MetricCard, SkeletonBar } from '../components/ui';

// ═══ Types ═══

interface KeyModel {
  id: string;
  key_id: string;
  model_prefix: string;
  is_selected: boolean;
  created_at: string;
}

interface LLMKey {
  id: string;
  provider: string;
  api_key_masked: string;
  label: string;
  is_active: boolean;
  priority: number;
  cost_per_1k_input: number;
  cost_per_1k_output: number;
  max_rpm: number;
  max_tokens: number;
  best_for: string[];
  created_at: string;
  total_requests?: number;
  total_cost?: number;
  // New: provider-first model selection
  models?: KeyModel[];
  model_count?: number;
  selected_model_count?: number;
  // Auth type: 'api' (default) or 'oauth' (requires bridge)
  auth_type?: string;
  // Legacy compat
  model_prefix?: string;
}

interface RoutingConfig {
  strategy: 'cost' | 'quality' | 'balanced' | 'speed' | 'random';
  auto_routing_profile: 'performance' | 'balanced' | 'economy';
  fallback_enabled: boolean;
  fallback_provider: string;
  max_retries: number;
  timeout_ms: number;
  budget_limit_daily: number;
  budget_limit_monthly: number;
  max_history_messages: number;
  oauth_mode: 'v1_bridge' | 'v2_worker' | 'v2_with_fallback';
  default_max_output_tokens: number;
}

interface UserOverride {
  id: string;
  customer_id: string;
  customer_name: string;
  provider_override: string | null;
  model_override: string | null;
  key_id_override: string | null;
  strategy_override: string | null;
  budget_limit: number | null;
  notes: string;
  created_at: string;
}

// ═══ Design Tokens ═══

const theme = {
  bg: T.bg,
  card: T.bgSecondary,
  cardHover: T.bgSecondary,
  cardElevated: T.bgSecondary,
  primary: T.primary,
  indigo: T.accent,
  cyan: T.primary,
  text: T.text,
  muted: T.textSecondary,
  dimmed: T.textMuted,
  success: T.success,
  warning: T.warning,
  error: T.danger,
  border: T.border,
  borderSolid: T.border,
  gradient: 'linear-gradient(135deg, #2F6BFF 0%, #6D4CFF 100%)',
  gradientSubtle: `linear-gradient(135deg, ${T.primaryBg} 0%, ${T.accentBg} 100%)`,
  font: "'Manrope', system-ui, sans-serif",
  // Spacing scale
  sp1: 4, sp2: 8, sp3: 12, sp4: 16, sp5: 20, sp6: 24, sp7: 32, sp8: 40, sp9: 48,
  // Radius
  r1: 6, r2: 8, r3: 10, r4: 12, r5: 14, r6: 16, r7: 20,
  // Shadows
  shadow1: `0 1px 3px ${T.shadowSm}`,
  shadow2: `0 4px 12px ${T.bgOverlay}`,
  shadow3: `0 8px 24px ${T.bgOverlay}`,
  shadowGlow: `0 4px 15px ${T.primaryBg}`,
};

// ═══ Provider Metadata ═══

const PROVIDERS: Record<string, { name: string; icon: string; color: string; models: string[] }> = {
  openai: {
    name: 'OpenAI',
 icon: '',
    color: T.success,
    models: ['gpt-5', 'gpt-5-mini', 'gpt-4.1', 'gpt-4.1-mini', 'gpt-4.1-nano', 'gpt-4o', 'gpt-4o-mini', 'o3', 'o3-mini', 'o4-mini'],
  },
  anthropic: {
    name: 'Anthropic',
 icon: '',
    color: T.warningHover,
    models: [
      'claude-opus-4.6',
      'claude-sonnet-4.5',
      'claude-haiku-4.5',
      'claude-opus-4',
      'claude-sonnet-4',
      'claude-sonnet-3.5',
      'claude-haiku-3.5',
    ],
  },
  google: {
    name: 'Google',
 icon: '',
    color: T.primary,
    models: ['gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.0-flash', 'gemini-1.5-pro', 'gemini-1.5-flash'],
  },
  mistral: {
    name: 'Mistral',
 icon: '',
    color: T.warning,
    models: ['mistral-large', 'mistral-medium', 'mistral-small', 'codestral', 'mixtral-8x7b'],
  },
  groq: {
    name: 'Groq',
 icon: '',
    color: T.danger,
    models: ['llama-3.3-70b', 'llama-3.1-8b', 'mixtral-8x7b', 'gemma2-9b'],
  },
  deepseek: {
    name: 'DeepSeek',
 icon: '',
    color: T.accent,
    models: ['deepseek-chat', 'deepseek-reasoner', 'deepseek-coder'],
  },
  together: {
    name: 'Together AI',
 icon: '',
    color: T.purple,
    models: ['llama-3.3-70b', 'qwen-2.5-72b', 'mixtral-8x22b'],
  },
  openrouter: {
    name: 'OpenRouter',
 icon: '',
    color: T.primary,
    models: ['auto', 'openai/gpt-4o', 'anthropic/claude-3.5-sonnet', 'google/gemini-pro'],
  },
  xai: {
    name: 'xAI',
 icon: '',
    color: T.text,
    models: ['grok-2', 'grok-2-mini'],
  },
  cohere: {
    name: 'Cohere',
 icon: '',
    color: T.primary,
    models: ['command-r-plus', 'command-r', 'command-light'],
  },
};

const BEST_FOR_OPTIONS = [
 { id: 'code', label: 'Código', color: T.accent },
 { id: 'chat', label: 'Chat', color: T.primary },
 { id: 'analysis', label: 'Análise', color: T.success },
 { id: 'creative', label: 'Criativo', color: T.warning },
 { id: 'fast', label: 'Rápido', color: T.danger },
 { id: 'vision', label: 'Visão', color: T.purple },
 { id: 'reasoning', label: 'Raciocínio', color: T.warningHover },
 { id: 'embedding', label: 'Embedding', color: T.primary },
];

const STRATEGY_OPTIONS = [
 { id: 'cost', label: 'Menor Custo', desc: 'Prioriza chaves mais baratas por ação' },
 { id: 'quality', label: 'Melhor Qualidade', desc: 'Usa o modelo mais capaz disponível' },
 { id: 'balanced', label: 'Balanceado', desc: 'Equilíbrio entre custo e qualidade' },
 { id: 'speed', label: 'Mais Rápido', desc: 'Prioriza latência baixa (Groq, Flash)' },
 { id: 'random', label: 'Aleatório', desc: 'Distribui requests aleatoriamente entre chaves ativas' },
];

// ═══ State ═══

const activeTab = signal<'keys' | 'routing' | 'users' | 'tokens' | 'dashboard' | 'billing' | 'bridge' | 'monitoring' | 'analytics' | 'pricing' | 'aliases' | 'health' | 'logs' | 'settings' | 'pipeline' | 'sticky'>('keys');
const errorLogCount = signal(0);
const keys = signal<LLMKey[]>([]);
const userOverrides = signal<UserOverride[]>([]);

// Token types
interface RouterToken {
  id: string;
  token_prefix: string;
  customer_id: string;
  machine_id: string | null;
  name: string;
  scopes: string[];
  rate_limit_rpm: number;
  budget_limit_monthly: number;
  budget_used_monthly: number;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  status?: string;
  budget_pct?: number;
  total_requests?: number;
  total_cost?: number;
}
const tokens = signal<RouterToken[]>([]);
const newTokenPlaintext = signal('');
const routingConfig = signal<RoutingConfig>({
  strategy: 'balanced',
  auto_routing_profile: 'balanced',
  fallback_enabled: true,
  fallback_provider: 'openai',
  max_retries: 3,
  timeout_ms: 30000,
  budget_limit_daily: 50,
  budget_limit_monthly: 500,
  max_history_messages: 50,
  oauth_mode: 'v1_bridge',
  default_max_output_tokens: 0,
});
const loading = signal(true);
const showAddKey = signal(false);
const showAddUser = signal(false);
const editingKey = signal<LLMKey | null>(null);
const showSelectModels = signal(false);
const pendingKeyData = signal<any>(null);
const editingKeyForModels = signal<LLMKey | null>(null);

// ═══ Toast System ═══

interface ToastItem {
  id: number;
  type: 'success' | 'error' | 'info';
  message: string;
}

const toasts = signal<ToastItem[]>([]);
let toastId = 0;

function showToast(type: ToastItem['type'], message: string) {
  const id = ++toastId;
  toasts.value = [...toasts.value, { id, type, message }];
  setTimeout(() => {
    toasts.value = toasts.value.filter(t => t.id !== id);
  }, 4000);
}

function ToastContainer() {
  if (toasts.value.length === 0) return null;
  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 9999,
      display: 'flex', flexDirection: 'column', gap: 8,
      pointerEvents: 'none',
    }}>
      {toasts.value.map(t => (
        <div key={t.id} style={{
          padding: '14px 20px', borderRadius: theme.r4, pointerEvents: 'auto',
          background: t.type === 'success' ? T.successBg : t.type === 'error' ? T.dangerBg : T.primaryBg,
          border: `1px solid ${t.type === 'success' ? T.successBg : t.type === 'error' ? T.dangerBg : T.primaryBg}`,
          backdropFilter: 'blur(16px)',
          color: t.type === 'success' ? theme.success : t.type === 'error' ? theme.error : theme.primary,
          fontSize: 13, fontWeight: 600, fontFamily: theme.font,
          boxShadow: theme.shadow2,
          animation: 'slideInRight 0.25s ease-out',
          maxWidth: 380,
        }}>
 {t.message}
        </div>
      ))}
    </div>
  );
}

// ═══ Reusable UI Components (usando ui/ library) ═══

function PrimaryButton({ onClick, disabled, children, style: extraStyle }: { onClick: () => void; disabled?: boolean; children: any; style?: any }) {
  return <Button variant="primary" onClick={onClick} disabled={disabled} style={extraStyle}>{children}</Button>;
}

function SecondaryButton({ onClick, children, danger, style: extraStyle }: { onClick: () => void; children: any; danger?: boolean; style?: any }) {
  return <Button variant={danger ? 'danger' : 'ghost'} size="sm" onClick={onClick} style={extraStyle}>{children}</Button>;
}

function InputField({ label, value, onInput, placeholder, type, style: extraStyle, mono }: any) {
  return <Input label={label} value={value ?? ''} onChange={(v: string) => onInput?.({ target: { value: v } })} placeholder={placeholder} type={type} style={{ fontFamily: mono ? 'monospace' : undefined, ...extraStyle }} />;
}

// ═══ API Data Loading ═══

const keyStats = signal<Record<string, { total_requests: number; total_cost: number }>>({});

async function loadData() {
  loading.value = true;
  try {
    const [keysRes, configRes, overridesRes, statsRes] = await Promise.all([
      api.get<{ keys: LLMKey[] }>('/admin/router/keys').catch(() => ({ keys: [] })),
      api.get<{ config: Record<string, string> }>('/admin/router/config').catch(() => ({ config: {} })),
      api.get<{ overrides: UserOverride[] }>('/admin/router/overrides').catch(() => ({ overrides: [] })),
      api.get<{ key_stats: any[]; daily_spend: number }>('/admin/router/stats').catch(() => ({ key_stats: [], daily_spend: 0 })),
    ]);

    const statsMap: Record<string, any> = {};
    for (const s of (statsRes.key_stats || [])) {
      statsMap[s.key_id] = s;
    }
    keyStats.value = statsMap;

    keys.value = (keysRes.keys || []).map((k: any) => ({
      ...k,
      is_active: !!k.is_active,
      best_for: Array.isArray(k.best_for) ? k.best_for : JSON.parse(k.best_for || '[]'),
      total_requests: statsMap[k.id]?.total_requests || 0,
      total_cost: statsMap[k.id]?.total_cost || 0,
    }));

    const cfg = configRes.config || {};
    routingConfig.value = {
      strategy: (cfg.strategy as any) || 'balanced',
      auto_routing_profile: (cfg.auto_routing_profile as any) || 'balanced',
      fallback_enabled: cfg.fallback_enabled !== 'false',
      fallback_provider: cfg.fallback_provider || 'openai',
      max_retries: parseInt(cfg.max_retries || '3'),
      timeout_ms: parseInt(cfg.timeout_ms || '30000'),
      budget_limit_daily: parseFloat(cfg.budget_limit_daily || '50'),
      budget_limit_monthly: parseFloat(cfg.budget_limit_monthly || '500'),
      max_history_messages: parseInt(cfg.max_history_messages || '50'),
      oauth_mode: (cfg.oauth_mode as any) || 'v1_bridge',
      default_max_output_tokens: parseInt(cfg.default_max_output_tokens || '0'),
    };

    userOverrides.value = (overridesRes.overrides || []).map((o: any) => ({
      ...o,
      customer_name: o.customer_id,
    }));

    try {
      const tokensRes = await api.get<{ tokens: RouterToken[] }>('/admin/router/tokens');
      tokens.value = (tokensRes.tokens || []).map((t: any) => ({
        ...t,
        is_active: !!t.is_active,
        scopes: Array.isArray(t.scopes) ? t.scopes : JSON.parse(t.scopes || '["chat"]'),
      }));
    } catch { tokens.value = []; }

    // Fetch error log count for badge
    try {
      const countRes = await api.get<{ count: number }>('/admin/router/error-logs/count');
      errorLogCount.value = countRes.count || 0;
    } catch { errorLogCount.value = 0; }
  } catch (e) {
    console.error('Failed to load router data:', e);
  } finally {
    loading.value = false;
  }
}

// ═══ Components ═══

function TabButton({ id, label, icon, count }: { id: typeof activeTab.value; label: string; icon: string; count?: number }) {
  const active = activeTab.value === id;
  return (
    <Button variant="ghost" onClick={() => activeTab.value = id} style={{
      padding: '10px 16px', borderRadius: theme.r3, border: 'none', cursor: 'pointer',
      background: active ? T.primaryBg : 'transparent',
      color: active ? theme.primary : theme.muted, fontWeight: active ? 700 : 500, fontSize: 13,
      transition: 'all 0.15s', fontFamily: theme.font,
      display: 'flex', alignItems: 'center', gap: 6,
      whiteSpace: 'nowrap', flexShrink: 0,
      borderBottom: active ? `2px solid ${theme.primary}` : '2px solid transparent',
    }}>
      <span style={{ fontSize: 15 }}>{icon}</span>
      <span className="tab-label">{label}</span>
      {count !== undefined && count > 0 && (
        <span style={{
          background: active ? T.primaryBg : T.border,
          padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700,
          minWidth: 18, textAlign: 'center',
        }}>{count}</span>
      )}
    </Button>
  );
}

function BestForBadge({ id }: { id: string }) {
  const opt = BEST_FOR_OPTIONS.find(o => o.id === id);
  if (!opt) return null;
  return <Badge color={opt.color}><span style={{fontSize:10}}>{opt.label}</span></Badge>;
}

// ═══ Keys Tab ═══

function KeysTab() {
  const activeKeys = keys.value.filter(k => k.is_active);
  const inactiveKeys = keys.value.filter(k => !k.is_active);
  const totalCost = keys.value.reduce((sum, k) => sum + (k.total_cost || 0), 0);
  const totalReqs = keys.value.reduce((sum, k) => sum + (k.total_requests || 0), 0);
  const [viewMode, setViewMode] = useState<'provider' | 'model'>('provider');

  // Build flat model list for "By Model" view
  const modelRows = (() => {
    const rows: Array<{ model: string; provider: string; provMeta: typeof PROVIDERS[string] | undefined; keyLabel: string; keyId: string; isActive: boolean; priority: number; totalRequests: number; totalCost: number }> = [];
    for (const k of keys.value) {
      const prov = PROVIDERS[k.provider];
      if (k.models && k.models.length > 0) {
        for (const m of k.models) {
          if (!m.is_selected) continue;
          rows.push({ model: m.model_prefix, provider: k.provider, provMeta: prov, keyLabel: k.label, keyId: k.id, isActive: k.is_active, priority: k.priority, totalRequests: k.total_requests || 0, totalCost: k.total_cost || 0 });
        }
      } else {
        // Key without models — show as single entry
        rows.push({ model: k.model_prefix || k.provider, provider: k.provider, provMeta: prov, keyLabel: k.label, keyId: k.id, isActive: k.is_active, priority: k.priority, totalRequests: k.total_requests || 0, totalCost: k.total_cost || 0 });
      }
    }
    rows.sort((a, b) => a.provider.localeCompare(b.provider) || a.model.localeCompare(b.model));
    return rows;
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp5 }}>
      {/* Summary strip */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: theme.sp3,
      }}>
        {[
          { label: 'Total de chaves', value: keys.value.length, color: theme.text, icon: '' },
          { label: 'Ativas', value: activeKeys.length, color: theme.success, icon: '' },
          { label: 'Provedores', value: new Set(keys.value.map(k => k.provider)).size, color: theme.indigo, icon: '' },
          { label: 'Modelos ativos', value: keys.value.reduce((sum, k) => sum + (k.selected_model_count ?? k.models?.filter(m => m.is_selected).length ?? 0), 0), color: theme.cyan, icon: '' },
          { label: 'Requests totais', value: totalReqs.toLocaleString(), color: theme.primary, icon: '' },
          { label: 'Gasto total', value: `$${totalCost.toFixed(2)}`, color: theme.warning, icon: '' },
        ].map((s, i) => (
          <div key={i} style={{
            padding: '14px 16px', borderRadius: theme.r4,
            background: theme.card, border: `1px solid ${theme.borderSolid}`,
          }}>
            <div style={{ fontSize: 11, color: theme.dimmed, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>{s.icon}</span> {s.label}
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Header + view toggle + add button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: theme.sp3 }}>
        <SectionHeader title="Chaves LLM" subtitle={`${activeKeys.length} ativas de ${keys.value.length} total`} />
        <div style={{ display: 'flex', gap: theme.sp3, alignItems: 'center' }}>
          {/* View mode toggle */}
          <div style={{ display: 'flex', borderRadius: theme.r3, overflow: 'hidden', border: `1px solid ${theme.borderSolid}` }}>
            {([
              { id: 'provider' as const, label: 'Por Provedor' },
              { id: 'model' as const, label: 'Por Modelo' },
            ]).map(v => (
              <Button variant="ghost" key={v.id} onClick={() => setViewMode(v.id)} style={{
                padding: '8px 14px', border: 'none', cursor: 'pointer',
                background: viewMode === v.id ? T.primaryBg : 'transparent',
                color: viewMode === v.id ? theme.primary : theme.muted,
                fontSize: 12, fontWeight: viewMode === v.id ? 700 : 500,
                fontFamily: theme.font, transition: 'all 0.15s',
              }}>{v.label}</Button>
            ))}
          </div>
          <PrimaryButton onClick={() => showAddKey.value = true}>
            + Nova Chave
          </PrimaryButton>
        </div>
      </div>

      {/* Content based on view mode */}
      {keys.value.length === 0 ? (
        <UiEmptyState title="Nenhuma chave cadastrada" description="Adicione sua primeira chave LLM para começar a rotear" />
      ) : viewMode === 'provider' ? (
        /* Provider view — card list */
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp3 }}>
          {keys.value.map(k => <KeyCard key={k.id} k={k} />)}
        </div>
      ) : (
        /* Model view — flat table */
        <Card>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.border}`, position: 'sticky', top: 0, background: theme.card, zIndex: 1 }}>
                  {['Status', 'Modelo', 'Provedor', 'Chave', 'Prioridade', 'Requests', 'Custo'].map(h => (
                    <th key={h} style={{
                      textAlign: ['Prioridade', 'Requests', 'Custo'].includes(h) ? 'right' : 'left',
                      padding: '10px 12px', color: theme.muted, fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {modelRows.map((r, i) => (
                  <tr key={`${r.keyId}-${r.model}-${i}`} style={{
                    borderBottom: `1px solid ${theme.border}`,
                    opacity: r.isActive ? 1 : 0.5,
                    transition: 'background 0.1s',
                  }} onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgSubtle}
                     onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                        background: r.isActive ? theme.success : theme.dimmed,
                        boxShadow: r.isActive ? `0 0 6px ${theme.success}` : 'none',
                      }} />
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <code style={{ fontSize: 12, color: theme.cyan, fontFamily: 'monospace' }}>{r.model}</code>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 14 }}>{r.provMeta?.icon || ''}</span>
                        <span style={{ color: r.provMeta?.color || theme.muted, fontWeight: 600, fontSize: 11 }}>{r.provMeta?.name || r.provider}</span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', color: theme.text, fontWeight: 500, fontSize: 12 }}>{r.keyLabel}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <Badge color={r.provMeta?.color || theme.dimmed} style={{fontSize:10}}>{`P${r.priority}`}</Badge>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: theme.muted, fontWeight: 600 }}>{r.totalRequests.toLocaleString()}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: theme.success, fontWeight: 600 }}>${r.totalCost.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: theme.sp3, fontSize: 11, color: theme.dimmed, textAlign: 'right' }}>
            {modelRows.length} modelo{modelRows.length !== 1 ? 's' : ''} em {keys.value.length} chave{keys.value.length !== 1 ? 's' : ''}
          </div>
        </Card>
      )}

      {/* Add Key Modal (Step 1) */}
      {showAddKey.value && <AddKeyModal onClose={() => showAddKey.value = false} />}

      {/* SelectModelsModal removed — model selection is now inline in Add/Edit modals */}
    </div>
  );
}

// ═══ Edit Key Modal ═══

function EditKeyModal({ k, onClose }: { k: LLMKey; onClose: () => void }) {
  const prov = PROVIDERS[k.provider];
  const allModels = prov?.models || [];
  const [label, setLabel] = useState(k.label);
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [priority, setPriority] = useState(k.priority);
  const [authType, setAuthType] = useState(k.auth_type || 'api');
  const [maxRpm, setMaxRpm] = useState(k.max_rpm);
  const [maxTokens, setMaxTokens] = useState(k.max_tokens);
  const [bestFor, setBestFor] = useState<string[]>(k.best_for || []);
  const [isActive, setIsActive] = useState(k.is_active);
  const [saving, setSaving] = useState(false);
  const [selectedModels, setSelectedModels] = useState<Record<string, boolean>>(() => {
    const s: Record<string, boolean> = {};
    allModels.forEach(m => s[m] = false);
    if (k.models) k.models.forEach(m => s[m.model_prefix] = m.is_selected);
    return s;
  });

  const selectedCount = Object.values(selectedModels).filter(Boolean).length;

  async function save() {
    setSaving(true);
    try {
      const payload: any = {
        label, priority, auth_type: authType,
        max_rpm: maxRpm, max_tokens: maxTokens,
        best_for: bestFor, is_active: isActive,
      };
      if (apiKey) payload.api_key = apiKey;
      await api.patch(`/admin/router/keys/${k.id}`, payload);
      // Save models
      const models = Object.entries(selectedModels).map(([model_prefix, is_selected]) => ({
        model_prefix, is_selected,
      }));
      await api.patch(`/admin/router/keys/${k.id}/models`, { models });
      showToast('success', `Chave "${label}" atualizada!`);
      await loadData();
      onClose();
    } catch (e: any) {
      showToast('error', `Erro: ${e?.message || 'desconhecido'}`);
    }
    setSaving(false);
  }

  return (
    <Modal open={true} onClose={onClose} title={`Editar — ${k.label}`} width={600}>
        {/* Provider (read-only) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: theme.sp5, padding: '12px 16px', borderRadius: theme.r3, background: T.bgSubtle, border: `1px solid ${theme.border}` }}>
          <span style={{ fontSize: 24 }}>{prov?.icon}</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: prov?.color || theme.text }}>{prov?.name || k.provider}</div>
            <div style={{ fontSize: 11, color: theme.dimmed }}>Provedor (não editável)</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.sp4 }}>
          {/* Label */}
          <InputField label="Nome / Label" value={label} onInput={(e: any) => setLabel(e.target.value)} />

          {/* Priority */}
          <InputField label="Prioridade (1 = mais alta)" value={priority} onInput={(e: any) => setPriority(Number(e.target.value))} type="number" />
        </div>

        {/* API Key */}
        <div style={{ marginTop: theme.sp4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: theme.muted, display: 'block', marginBottom: 6 }}>API Key</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input value={apiKey} onChange={(v) => setApiKey(v)}
              type={showApiKey ? 'text' : 'password'} placeholder={k.api_key_masked + ' (deixe vazio para manter)'}
              style={{ flex: 1, padding: '11px 14px', borderRadius: theme.r3, fontSize: 13, background: theme.bg, border: `1px solid ${theme.borderSolid}`, color: theme.text, outline: 'none', fontFamily: 'monospace', boxSizing: 'border-box' }} />
            <Button onClick={() => setShowApiKey(!showApiKey)} variant="ghost" size="sm" style={{
              padding: '8px 12px', borderRadius: theme.r2, border: `1px solid ${theme.borderSolid}`,
              background: 'transparent', color: theme.muted, fontSize: 14,
            }}>{showApiKey ? '' : ''}</Button>
            <Button onClick={async () => {
              try {
                const r = await api.get(`/admin/router/keys/${k.id}/reveal`);
                const key = r?.api_key || r?.data?.api_key;
                if (key) {
                  setApiKey(key);
                  setShowApiKey(true);
                  showToast('success', 'Chave revelada');
                } else {
                  showToast('error', 'Chave nao encontrada');
                }
              } catch (e: any) { showToast('error', e.message); }
            }} variant="ghost" size="sm" style={{
              padding: '8px 12px', borderRadius: theme.r2, border: `1px solid ${theme.borderSolid}`,
              background: 'transparent', color: T.accent, fontSize: 11, whiteSpace: 'nowrap',
            }}>Revelar Chave</Button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.sp4, marginTop: theme.sp4 }}>
          {/* RPM */}
          <InputField label="RPM Limit" value={maxRpm} onInput={(e: any) => setMaxRpm(Number(e.target.value))} type="number" />

          {/* Max Tokens */}
          <InputField label="Max Tokens" value={maxTokens} onInput={(e: any) => setMaxTokens(Number(e.target.value))} type="number" />
        </div>

        {/* Auth Type */}
        <div style={{ marginTop: theme.sp4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: theme.muted, display: 'block', marginBottom: 8 }}>Tipo de Autenticação</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { id: 'api', label: 'API Key' },
              { id: 'oauth', label: 'OAuth Token' },
            ].map(opt => (
              <div key={opt.id} onClick={() => setAuthType(opt.id)} style={{
                flex: 1, padding: '10px 14px', borderRadius: theme.r3, cursor: 'pointer', textAlign: 'center',
                background: authType === opt.id ? T.primaryBg : T.bgSubtle,
                border: authType === opt.id ? `2px solid ${T.primaryBgStrong}` : `1px solid ${theme.border}`,
                color: authType === opt.id ? theme.primary : theme.muted, fontSize: 13, fontWeight: 600,
              }}>{opt.label}</div>
            ))}
          </div>
        </div>

        {/* Best For */}
        <div style={{ marginTop: theme.sp4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: theme.muted, display: 'block', marginBottom: 8 }}>Melhor para</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {BEST_FOR_OPTIONS.map(opt => {
              const sel = bestFor.includes(opt.id);
              return (
                <Button variant="ghost" key={opt.id} onClick={() => setBestFor(sel ? bestFor.filter(b => b !== opt.id) : [...bestFor, opt.id])} style={{
                  padding: '6px 12px', borderRadius: theme.r2, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: sel ? `${opt.color}18` : T.bgSubtle,
                  border: sel ? `2px solid ${opt.color}50` : `1px solid ${theme.border}`,
                  color: sel ? opt.color : theme.dimmed, transition: 'all 0.15s',
                }}>{opt.label}</Button>
              );
            })}
          </div>
        </div>

        {/* Active toggle */}
        <div style={{ marginTop: theme.sp4, display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: theme.muted }}>Chave Ativa</label>
          <div onClick={() => setIsActive(!isActive)} style={{
            width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
            background: isActive ? theme.success : theme.dimmed, position: 'relative', transition: 'all 0.2s',
          }}>
            <div style={{ width: 20, height: 20, borderRadius: 10, background: '#fff', position: 'absolute', top: 3, left: isActive ? 25 : 3, transition: 'all 0.2s' }} />
          </div>
          <span style={{ fontSize: 12, color: isActive ? theme.success : theme.dimmed, fontWeight: 600 }}>{isActive ? 'Ativa' : 'Inativa'}</span>
        </div>

        {/* Inline Model Selection */}
        {allModels.length > 0 && (
          <div style={{ marginTop: theme.sp5 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.sp3 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: theme.muted }}>Modelos ({selectedCount}/{allModels.length})</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button variant="ghost" onClick={() => { const s: Record<string, boolean> = {}; allModels.forEach(m => s[m] = true); setSelectedModels(s); }} style={{ fontSize: 11, color: theme.primary, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Todos</Button>
                <Button variant="ghost" onClick={() => { const s: Record<string, boolean> = {}; allModels.forEach(m => s[m] = false); setSelectedModels(s); }} style={{ fontSize: 11, color: theme.dimmed, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Nenhum</Button>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto', borderRadius: theme.r3, border: `1px solid ${theme.border}`, padding: 8 }}>
              {allModels.map(model => {
                const checked = !!selectedModels[model];
                return (
                  <div key={model} onClick={() => setSelectedModels({ ...selectedModels, [model]: !checked })} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    borderRadius: theme.r2, cursor: 'pointer',
                    background: checked ? T.primaryBg : 'transparent',
                    transition: 'all 0.15s',
                  }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                      background: checked ? theme.primary : 'transparent',
                      border: checked ? 'none' : `2px solid ${theme.dimmed}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, color: T.text, fontWeight: 800,
                    }}>{checked ? '' : ''}</div>
                    <code style={{ fontSize: 12, color: checked ? theme.text : theme.dimmed, fontFamily: 'monospace' }}>{model}</code>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: theme.sp3, marginTop: theme.sp6 }}>
          <PrimaryButton onClick={save} disabled={saving} style={{ flex: 1 }}>
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </PrimaryButton>
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
        </div>
      </Modal>
  );
}

// ═══ Key Card (Expandable) ═══

function KeyCard({ k }: { k: LLMKey }) {
  const prov = PROVIDERS[k.provider];
  const [expanded, setExpanded] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [testing, setTesting] = useState(false);
  const [healthStatus, setHealthStatus] = useState<'healthy' | 'error' | 'unknown'>('unknown');
  const [healthLatency, setHealthLatency] = useState<number | null>(null);
  const [keyStats, setKeyStats] = useState<any>(null);
  const [discovering, setDiscovering] = useState(false);
  const [discoveredModels, setDiscoveredModels] = useState<string[] | null>(null);

  async function toggleActive() {
    setToggling(true);
    try {
      await api.patch(`/admin/router/keys/${k.id}`, { is_active: !k.is_active });
      keys.value = keys.value.map(kk => kk.id === k.id ? { ...kk, is_active: !kk.is_active } : kk);
      showToast('success', `Chave "${k.label}" ${!k.is_active ? 'ativada' : 'pausada'}`);
    } catch (e: any) {
      showToast('error', `Erro ao alterar chave: ${e?.message || 'erro desconhecido'}`);
    }
    setToggling(false);
  }

  async function deleteKey() {
    setDeleting(true);
    try {
      await api.delete(`/admin/router/keys/${k.id}`);
      keys.value = keys.value.filter(kk => kk.id !== k.id);
      showToast('success', `Chave "${k.label}" apagada com sucesso`);
      setShowDeleteConfirm(false);
    } catch (e: any) {
      showToast('error', `Erro ao apagar chave: ${e?.message || 'erro desconhecido'}`);
    }
    setDeleting(false);
  }

  async function testKey() {
    setTesting(true);
    try {
      const res = await api.post<any>(`/admin/router/keys/${k.id}/test`, {});
      setHealthStatus(res.status === 'healthy' ? 'healthy' : 'error');
      setHealthLatency(res.latency_ms);
      showToast(res.status === 'healthy' ? 'success' : 'error',
        res.status === 'healthy' ? `Saudável (${res.latency_ms}ms)` : `Erro: ${res.error?.substring(0, 100)}`);
    } catch (e: any) {
      setHealthStatus('error');
      showToast('error', `Erro no teste: ${e?.message}`);
    }
    setTesting(false);
  }

  async function loadKeyStats() {
    try {
      const res = await api.get<any>(`/admin/router/keys/${k.id}/stats`);
      setKeyStats(res);
    } catch {}
  }

  async function discoverModels() {
    setDiscovering(true);
    try {
      const res = await api.post<any>(`/admin/router/keys/${k.id}/discover-models`, {});
      setDiscoveredModels(res.models || []);
      showToast('success', `${(res.models || []).length} modelos encontrados (${res.source})`);
    } catch (e: any) {
      showToast('error', `Erro: ${e?.message}`);
    }
    setDiscovering(false);
  }

  // Load stats when expanded
  useEffect(() => {
    if (expanded && !keyStats) loadKeyStats();
  }, [expanded]);

  const healthBadge = healthStatus === 'healthy' ? { label: 'Saudável', color: theme.success }
    : healthStatus === 'error' ? { label: 'Erro', color: theme.error }
    : { label: 'Não testado', color: theme.dimmed };

  return (
    <>
    <div style={{
      background: theme.card, border: `1px solid ${theme.borderSolid}`,
      borderRadius: theme.r5, overflow: 'hidden',
      opacity: k.is_active ? 1 : 0.55,
      transition: 'all 0.2s',
      borderLeft: `3px solid ${k.is_active ? (prov?.color || theme.primary) : theme.dimmed}`,
    }}>
      {/* Collapsed row — always visible */}
      <div style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: theme.sp3, flexWrap: 'wrap' }}
        onClick={() => setExpanded(!expanded)}>
        {/* Provider icon */}
        <div style={{
          width: 42, height: 42, borderRadius: theme.r4, flexShrink: 0,
          background: `${prov?.color || theme.dimmed}12`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
        }}>{prov?.icon || ''}</div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{k.label}</span>
            <Badge color={k.is_active ? theme.success : theme.dimmed}><span style={{fontSize:10}}>{k.is_active ? '● Ativa' : '○ Inativa'}</span></Badge>
            <Badge color={prov?.color || theme.dimmed} style={{fontSize:10}}>{`P${k.priority}`}</Badge>
            <Badge color={healthBadge.color}><span style={{fontSize:10}}>{healthBadge.label}</span></Badge>
            {k.auth_type === 'oauth' && <Badge color={T.warning}><span style={{fontSize:10}}>OAuth</span></Badge>}
          </div>
          <div style={{ fontSize: 12, color: theme.dimmed, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, color: prov?.color || theme.muted }}>{prov?.name || k.provider}</span>
            <span style={{ opacity: 0.4 }}>•</span>
            <span style={{ color: theme.cyan, fontWeight: 600 }}>{k.selected_model_count ?? k.model_count ?? 0} modelos</span>
            {healthLatency !== null && <><span style={{ opacity: 0.4 }}>•</span><span style={{ color: theme.muted }}>{healthLatency}ms</span></>}
          </div>
          {k.best_for.length > 0 && (
            <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
              {k.best_for.map(bf => <BestForBadge key={bf} id={bf} />)}
            </div>
          )}
        </div>

        {/* Stats (collapsed) */}
        <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 90 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{(k.total_requests || 0).toLocaleString()}</div>
          <div style={{ fontSize: 11, color: theme.dimmed }}>requests</div>
          <div style={{ fontSize: 13, color: theme.success, fontWeight: 700, marginTop: 2 }}>${(k.total_cost || 0).toFixed(2)}</div>
        </div>

        {/* Actions (stop propagation) */}
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={(e: any) => e.stopPropagation()}>
          <SecondaryButton onClick={() => setShowEditModal(true)}>Editar</SecondaryButton>
          <SecondaryButton onClick={toggleActive}>
            {toggling ? '' : k.is_active ? 'Pausar' : 'Ativar'}
          </SecondaryButton>
        </div>

        {/* Expand arrow */}
        <span style={{ fontSize: 12, color: theme.dimmed, transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </div>

      {/* Expanded section */}
      {expanded && (
        <div style={{ padding: '0 20px 20px', borderTop: `1px solid ${theme.border}` }}>
          {/* Metrics strip */}
          <div style={{ display: 'flex', gap: theme.sp5, flexWrap: 'wrap', paddingTop: theme.sp3, marginBottom: theme.sp4 }}>
            {[
              { label: 'RPM', value: `${k.max_rpm}`, icon: '' },
              { label: 'Max Tokens', value: k.max_tokens >= 1000000 ? `${(k.max_tokens / 1000000).toFixed(1)}M` : `${(k.max_tokens / 1000).toFixed(0)}K`, icon: '' },
              { label: 'Input', value: `$${k.cost_per_1k_input}/1K`, icon: '' },
              { label: 'Output', value: `$${k.cost_per_1k_output}/1K`, icon: '' },
              { label: 'Auth', value: k.auth_type || 'api', icon: '' },
              { label: 'Criada', value: k.created_at ? new Date(k.created_at + (k.created_at.endsWith('Z') ? '' : 'Z')).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—', icon: '' },
            ].map((s, i) => (
              <div key={i} style={{ fontSize: 11, color: theme.dimmed, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span>{s.icon}</span>
                <span style={{ color: theme.muted, fontWeight: 600 }}>{s.value}</span>
                <span>{s.label}</span>
              </div>
            ))}
          </div>

          {/* API Key with reveal + copy */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: theme.sp4, padding: '8px 12px', borderRadius: theme.r3, background: T.bgSubtle, border: `1px solid ${theme.border}` }}>
            <code id={`key-${k.id}`} style={{ fontSize: 12, color: theme.dimmed, flex: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>{k.api_key_masked}</code>
            <Button onClick={async () => {
              try {
                const r = await api.get(`/admin/router/keys/${k.id}/reveal`);
                const key = r?.api_key || r?.data?.api_key;
                if (key) {
                  const el = document.getElementById(`key-${k.id}`);
                  if (el) el.textContent = key;
                  navigator.clipboard.writeText(key);
                  showToast('success', 'Chave copiada!');
                }
              } catch (e: any) { showToast('error', e.message); }
            }} variant="ghost" size="sm" style={{
              background: 'transparent', border: 'none', color: T.accent, fontSize: 11, whiteSpace: 'nowrap' }}>Revelar e Copiar</Button>
          </div>

          {/* Stats per key */}
          {keyStats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: theme.sp3, marginBottom: theme.sp4 }}>
              {[
                { label: 'Requests 7d', value: keyStats.stats_7d?.total_requests || 0, color: theme.primary },
                { label: 'Custo 7d', value: `$${(keyStats.stats_7d?.total_cost || 0).toFixed(4)}`, color: theme.warning },
                { label: 'Requests 30d', value: keyStats.stats_30d?.total_requests || 0, color: theme.primary },
                { label: 'Custo 30d', value: `$${(keyStats.stats_30d?.total_cost || 0).toFixed(4)}`, color: theme.warning },
                { label: 'Taxa Sucesso 7d', value: `${keyStats.stats_7d?.success_rate || 0}%`, color: theme.success },
                { label: 'Último uso', value: keyStats.last_used_at ? new Date(keyStats.last_used_at).toLocaleDateString('pt-BR') : '—', color: theme.dimmed },
              ].map((s, i) => (
                <div key={i} style={{ padding: '10px 12px', borderRadius: theme.r3, background: T.bgSubtle, border: `1px solid ${theme.border}` }}>
                  <div style={{ fontSize: 10, color: theme.dimmed, marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Models table with inline toggles */}
          {k.models && k.models.length > 0 && (
            <div style={{ marginBottom: theme.sp4 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.muted, marginBottom: 8 }}>Modelos ({k.models.filter(m => m.is_selected).length}/{k.models.length} ativos)</div>
              <div style={{ borderRadius: theme.r3, border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: T.bgSubtle }}>
                      <th style={{ textAlign: 'left', padding: '8px 12px', color: theme.dimmed, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Modelo</th>
                      <th style={{ textAlign: 'center', padding: '8px 12px', color: theme.dimmed, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', width: 80 }}>Status</th>
                      <th style={{ textAlign: 'center', padding: '8px 12px', color: theme.dimmed, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', width: 60 }}>Toggle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {k.models.map((m, i) => (
                      <tr key={m.id} style={{ borderTop: i > 0 ? `1px solid ${theme.border}` : 'none', transition: 'background 0.15s' }}
                        onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgSubtle}
                        onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '8px 12px' }}>
                          <code style={{ fontFamily: 'monospace', color: m.is_selected ? theme.text : theme.dimmed, fontSize: 12 }}>{m.model_prefix}</code>
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                            background: m.is_selected ? T.successBg : T.bgHover,
                            color: m.is_selected ? theme.success : theme.dimmed,
                          }}>{m.is_selected ? 'Ativo' : 'Inativo'}</span>
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                          <div onClick={async (e: any) => {
                            e.stopPropagation();
                            try {
                              await api.patch(`/admin/router/keys/${k.id}/models`, {
                                models: [{ model_prefix: m.model_prefix, is_selected: !m.is_selected }],
                              });
                              keys.value = keys.value.map(kk => kk.id === k.id ? {
                                ...kk,
                                models: kk.models?.map(mm => mm.id === m.id ? { ...mm, is_selected: !m.is_selected } : mm),
                                selected_model_count: (kk.selected_model_count ?? 0) + (m.is_selected ? -1 : 1),
                              } : kk);
                            } catch (err: any) { showToast('error', `Erro: ${err?.message}`); }
                          }} style={{
                            width: 36, height: 20, borderRadius: 10, cursor: 'pointer',
                            background: m.is_selected ? theme.success : theme.dimmed, position: 'relative',
                            transition: 'all 0.2s', display: 'inline-block',
                          }}>
                            <div style={{ width: 14, height: 14, borderRadius: 7, background: '#fff', position: 'absolute', top: 3, left: m.is_selected ? 19 : 3, transition: 'all 0.2s' }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Discovered models */}
          {discoveredModels && (
            <div style={{ marginBottom: theme.sp4, padding: '12px 16px', borderRadius: theme.r3, background: T.primaryBg, border: `1px solid ${T.primaryBg}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.primary, marginBottom: 8 }}>Modelos Descobertos ({discoveredModels.length})</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxHeight: 120, overflowY: 'auto' }}>
                {discoveredModels.map(m => (
                  <Badge key={m} color={theme.cyan}><span style={{fontSize:10}}>{m}</span></Badge>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <SecondaryButton onClick={testKey}>
              {testing ? 'Testando...' : 'Testar Saúde'}
            </SecondaryButton>
            <SecondaryButton onClick={discoverModels}>
              {discovering ? 'Buscando...' : 'Descobrir Modelos'}
            </SecondaryButton>
            <SecondaryButton onClick={() => setShowDeleteConfirm(true)} danger>
              Apagar
            </SecondaryButton>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <Modal open={true} onClose={() => setShowDeleteConfirm(false)} title="Confirmar">
          <div style={{
            background: theme.card, border: `1px solid ${theme.border}`, borderRadius: theme.r5,
            padding: theme.sp6, maxWidth: 420, width: '100%',
          }}>
            <h3 style={{ margin: 0, fontSize: 18, color: theme.text, marginBottom: theme.sp3 }}>Apagar Chave LLM?</h3>
            <p style={{ margin: 0, fontSize: 13, color: theme.dimmed, marginBottom: theme.sp4, lineHeight: 1.6 }}>
              Tem certeza que deseja apagar a chave <strong style={{ color: theme.text }}>"{k.label}"</strong> ({prov?.name})?
              <br/><br/>
              Esta ação é <strong>irreversível</strong> e todas as referências a esta chave serão removidas.
            </p>
            <div style={{ display: 'flex', gap: theme.sp3 }}>
              <Button onClick={() => setShowDeleteConfirm(false)} variant="ghost" style={{
                flex: 1, padding: '10px 16px', background: theme.border, color: theme.text,
                borderRadius: theme.r3, fontSize: 13, fontWeight: 600,
              }}>Cancelar</Button>
              <Button onClick={deleteKey} disabled={deleting} variant="danger" loading={deleting} style={{
                flex: 1, padding: '10px 16px', borderRadius: theme.r3,
                fontSize: 13, fontWeight: 600,
              }}>{deleting ? 'Apagando...' : 'Confirmar Apagar'}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>

    {/* Edit Key Modal */}
    {showEditModal && <EditKeyModal k={k} onClose={() => setShowEditModal(false)} />}
    </>
  );
}

// ═══ Modais usam Modal do ui/ ═══

// ═══ Add Key Modal ═══

function AddKeyModal({ onClose }: { onClose: () => void }) {
  const [provider, setProvider] = useState('openai');
  const [label, setLabel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [authType, setAuthType] = useState('api');
  const [bestFor, setBestFor] = useState<string[]>([]);
  const [priority, setPriority] = useState(keys.value.length + 1);
  const [saving, setSaving] = useState(false);

  const prov = PROVIDERS[provider];
  const allModels = prov?.models || [];

  // Reset model selection when provider changes
  const [selectedModels, setSelectedModels] = useState<Record<string, boolean>>(() => {
    const s: Record<string, boolean> = {};
    (PROVIDERS['openai']?.models || []).forEach(m => s[m] = true);
    return s;
  });

  // Update selected models when provider changes
  useEffect(() => {
    const models = PROVIDERS[provider]?.models || [];
    const s: Record<string, boolean> = {};
    models.forEach(m => s[m] = true);
    setSelectedModels(s);
  }, [provider]);

  const selectedCount = Object.values(selectedModels).filter(Boolean).length;

  return (
    <Modal open={true} onClose={onClose} title="Adicionar Chave LLM" width={600}>

        {/* Provider selector */}
        <div style={{ marginBottom: theme.sp5 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: theme.muted, display: 'block', marginBottom: 8 }}>Provedor</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 8 }}>
            {Object.entries(PROVIDERS).map(([id, p]) => (
              <Button variant="ghost" key={id} onClick={() => setProvider(id)} style={{
                padding: '10px 8px', borderRadius: theme.r3, cursor: 'pointer', textAlign: 'center',
                background: provider === id ? `${p.color}18` : T.bgSubtle,
                border: provider === id ? `2px solid ${p.color}50` : `1px solid ${theme.border}`,
                transition: 'all 0.15s',
              }}>
                <div style={{ fontSize: 20 }}>{p.icon}</div>
                <div style={{ fontSize: 10, color: provider === id ? p.color : theme.dimmed, fontWeight: 600, marginTop: 4 }}>{p.name}</div>
              </Button>
            ))}
          </div>
        </div>

        {/* Label */}
        <div style={{ marginBottom: theme.sp4 }}>
          <InputField label="Nome / Label" value={label} onInput={(e: any) => setLabel(e.target.value)}
            placeholder={`${prov?.name || provider} Principal`} />
        </div>

        {/* API Key */}
        <div style={{ marginBottom: theme.sp4 }}>
          <InputField label="API Key" value={apiKey} onInput={(e: any) => setApiKey(e.target.value)}
            type="password" placeholder="sk-..." mono />
        </div>

        {/* Priority */}
        <div style={{ marginBottom: theme.sp4 }}>
          <InputField label="Prioridade (1 = mais alta)" value={priority}
            onInput={(e: any) => setPriority(Number(e.target.value))}
            type="number" style={{ width: 100 }} />
        </div>

        {/* Auth Type */}
        <div style={{ marginBottom: theme.sp4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: theme.muted, display: 'block', marginBottom: 8 }}>
            Tipo de Autenticação
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
 { id: 'api', label: 'API Key', desc: 'Header x-api-key (padrão)' },
 { id: 'oauth', label: 'OAuth Token', desc: 'Header Authorization: Bearer (requer Bridge)' },
            ].map(opt => (
              <div key={opt.id} onClick={() => setAuthType(opt.id)} style={{
                flex: 1, padding: '12px 14px', borderRadius: theme.r3, cursor: 'pointer',
                background: authType === opt.id ? T.primaryBg : T.bgSubtle,
                border: authType === opt.id ? `2px solid ${T.primaryBgStrong}` : `1px solid ${theme.border}`,
                transition: 'all 0.15s',
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: authType === opt.id ? theme.primary : theme.text }}>{opt.label}</div>
                <div style={{ fontSize: 10, color: theme.dimmed, marginTop: 2 }}>{opt.desc}</div>
              </div>
            ))}
          </div>
          {authType === 'oauth' && (
            <div style={{
              marginTop: 8, padding: '8px 12px', borderRadius: theme.r2,
              background: T.warningBg, border: `1px solid ${T.warningBg}`,
              fontSize: 11, color: T.warning,
            }}>
 OAuth tokens (sk-ant-oat01-...) requerem Bridge configurada. O Router enviará requests via Bridge tunnel.
            </div>
          )}
        </div>

        {/* Best for */}
        <div style={{ marginBottom: theme.sp6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: theme.muted, display: 'block', marginBottom: 8 }}>
            Melhor para
          </label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {BEST_FOR_OPTIONS.map(opt => {
              const sel = bestFor.includes(opt.id);
              return (
                <Button variant="ghost" key={opt.id} onClick={() => {
                  setBestFor(sel ? bestFor.filter(b => b !== opt.id) : [...bestFor, opt.id]);
                }} style={{
                  padding: '6px 12px', borderRadius: theme.r2, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: sel ? `${opt.color}18` : T.bgSubtle,
                  border: sel ? `2px solid ${opt.color}50` : `1px solid ${theme.border}`,
                  color: sel ? opt.color : theme.dimmed, transition: 'all 0.15s',
                }}>{opt.label}</Button>
              );
            })}
          </div>
        </div>

        {/* Inline Model Selection */}
        {allModels.length > 0 && (
          <div style={{ marginBottom: theme.sp5 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.sp3 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: theme.muted }}>Modelos ({selectedCount}/{allModels.length})</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button variant="ghost" onClick={() => { const s: Record<string, boolean> = {}; allModels.forEach(m => s[m] = true); setSelectedModels(s); }} style={{ fontSize: 11, color: theme.primary, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Todos</Button>
                <Button variant="ghost" onClick={() => { const s: Record<string, boolean> = {}; allModels.forEach(m => s[m] = false); setSelectedModels(s); }} style={{ fontSize: 11, color: theme.dimmed, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Nenhum</Button>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto', borderRadius: theme.r3, border: `1px solid ${theme.border}`, padding: 8 }}>
              {allModels.map(model => {
                const checked = !!selectedModels[model];
                return (
                  <div key={model} onClick={() => setSelectedModels({ ...selectedModels, [model]: !checked })} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    borderRadius: theme.r2, cursor: 'pointer',
                    background: checked ? T.primaryBg : 'transparent',
                    transition: 'all 0.15s',
                  }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                      background: checked ? theme.primary : 'transparent',
                      border: checked ? 'none' : `2px solid ${theme.dimmed}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, color: T.text, fontWeight: 800,
                    }}>{checked ? '' : ''}</div>
                    <code style={{ fontSize: 12, color: checked ? theme.text : theme.dimmed, fontFamily: 'monospace' }}>{model}</code>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: theme.sp3, flexWrap: 'wrap' }}>
          <PrimaryButton onClick={async () => {
            if (!provider) { showToast('error', 'Selecione um provedor'); return; }
            if (!apiKey) { showToast('error', 'Informe a API Key'); return; }
            setSaving(true);
            try {
              const selectedModelsList = Object.entries(selectedModels).filter(([, v]) => v).map(([k]) => k);
              await api.post('/admin/router/keys', {
                provider,
                label: label || `${prov?.name} ${keys.value.length + 1}`,
                api_key: apiKey,
                priority,
                best_for: bestFor,
                auth_type: authType,
                selected_models: selectedModelsList,
              });
              showToast('success', 'Chave criada com sucesso!');
              await loadData();
              onClose();
            } catch (e: any) {
              showToast('error', 'Erro ao criar: ' + (e?.message || JSON.stringify(e)));
            }
            setSaving(false);
          }} disabled={saving} style={{ flex: 1 }}>
            {saving ? 'Criando...' : `Criar Chave (${Object.values(selectedModels).filter(Boolean).length} modelos)`}
          </PrimaryButton>
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
        </div>
      </Modal>
  );
}

// ═══ Select Models Modal (Step 2) ═══

// SelectModelsModal removed — model selection is now inline in Add/Edit modals

// ═══ Routing Tab ═══

function RoutingTab() {
  const config = routingConfig.value;
  const [saving, setSaving] = useState(false);

  async function saveConfig() {
    setSaving(true);
    try {
      await api.put('/admin/router/config', {
        strategy: config.strategy,
        auto_routing_profile: config.auto_routing_profile,
        fallback_enabled: String(config.fallback_enabled),
        fallback_provider: config.fallback_provider,
        max_retries: String(config.max_retries),
        timeout_ms: String(config.timeout_ms),
        budget_limit_daily: String(config.budget_limit_daily),
        budget_limit_monthly: String(config.budget_limit_monthly),
        max_history_messages: String(config.max_history_messages),
        oauth_mode: config.oauth_mode,
        default_max_output_tokens: String(config.default_max_output_tokens),
      });
      showToast('success', 'Configurações de roteamento salvas!');
    } catch (e: any) {
      showToast('error', 'Erro ao salvar: ' + (e.message || 'desconhecido'));
    }
    setSaving(false);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp5 }}>
      {/* Current strategy badge */}
      <div style={{
        padding: '14px 20px', borderRadius: theme.r4,
        background: T.primaryBg, border: `1px solid ${T.primaryBg}`,
        display: 'flex', alignItems: 'center', gap: theme.sp3, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 13, color: theme.muted }}>Estratégia atual:</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: theme.primary }}>
          {STRATEGY_OPTIONS.find(s => s.id === config.strategy)?.label || config.strategy}
        </span>
        <span style={{ fontSize: 12, color: theme.dimmed }}>•</span>
        <span style={{ fontSize: 12, color: theme.dimmed }}>
          Fallback {config.fallback_enabled ? `ON → ${PROVIDERS[config.fallback_provider]?.name || config.fallback_provider}` : 'OFF'}
        </span>
      </div>

      {/* Strategy selector */}
      <Card>
 <SectionHeader title="Estratégia de Roteamento" subtitle="Defina como as requisições são distribuídas entre os providers" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: theme.sp3 }}>
          {STRATEGY_OPTIONS.map(s => (
            <div key={s.id} onClick={() => {
              routingConfig.value = { ...config, strategy: s.id as any };
            }} style={{
              padding: '16px 18px', borderRadius: theme.r4, cursor: 'pointer',
              background: config.strategy === s.id ? T.primaryBg : T.bgSubtle,
              border: config.strategy === s.id ? `2px solid ${T.primaryBgStrong}` : `1px solid ${theme.border}`,
              transition: 'all 0.15s',
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: config.strategy === s.id ? theme.primary : theme.text }}>
                {s.label}
              </div>
              <div style={{ fontSize: 11, color: theme.dimmed, marginTop: 4 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Auto Routing Profile */}
      <Card>
 <SectionHeader title="Perfil de Routing Auto" subtitle="Defina como o modelo 'auto' resolve para cada tipo de requisição" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: theme.sp3 }}>
          {([
 { id: 'performance', label: 'Performance', desc: 'Opus para tudo, Sonnet para chat simples' },
 { id: 'balanced', label: 'Balanceado', desc: 'Sonnet para coding/reasoning, Haiku para chat' },
 { id: 'economy', label: 'Economia', desc: 'Haiku para tudo, Sonnet só para reasoning' },
          ] as const).map(p => (
            <div key={p.id} onClick={() => {
              routingConfig.value = { ...config, auto_routing_profile: p.id };
            }} style={{
              padding: '16px 18px', borderRadius: theme.r4, cursor: 'pointer',
              background: config.auto_routing_profile === p.id ? T.primaryBg : T.bgSubtle,
              border: config.auto_routing_profile === p.id ? `2px solid ${T.primaryBgStrong}` : `1px solid ${theme.border}`,
              transition: 'all 0.15s',
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: config.auto_routing_profile === p.id ? theme.primary : theme.text }}>
                {p.label}
              </div>
              <div style={{ fontSize: 11, color: theme.dimmed, marginTop: 4 }}>{p.desc}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: theme.sp3, padding: '10px 14px', borderRadius: theme.r3, background: T.bgSubtle, border: `1px solid ${theme.border}`, fontSize: 11, color: theme.dimmed }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: theme.muted }}>Resolução por tipo de ação:</div>
          {config.auto_routing_profile === 'performance' && <span>Chat → Sonnet 4.5 &nbsp;|&nbsp; Coding → Opus 4.6 &nbsp;|&nbsp; Reasoning → Opus 4.6 &nbsp;|&nbsp; Analysis → Opus 4.6</span>}
          {config.auto_routing_profile === 'balanced' && <span>Chat → Haiku 4.5 &nbsp;|&nbsp; Coding → Sonnet 4.5 &nbsp;|&nbsp; Reasoning → Sonnet 4.5 &nbsp;|&nbsp; Analysis → Sonnet 4.5</span>}
          {config.auto_routing_profile === 'economy' && <span>Chat → Haiku 4.5 &nbsp;|&nbsp; Coding → Haiku 4.5 &nbsp;|&nbsp; Reasoning → Sonnet 4.5 &nbsp;|&nbsp; Analysis → Haiku 4.5</span>}
        </div>
      </Card>

      {/* How routing works */}
      <Card style={{ background: theme.gradientSubtle }}>
 <SectionHeader title="Como o Roteamento Funciona" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp3 }}>
          {[
            { step: '1', title: 'Recebe requisição', desc: 'Usuário faz chamada à API do Elon Router' },
            { step: '2', title: 'Classifica ação', desc: 'Detecta tipo: código, chat, análise, criativo, etc' },
            { step: '3', title: 'Filtra chaves', desc: 'Seleciona chaves ativas com "best_for" compatível' },
            { step: '4', title: 'Aplica estratégia', desc: `Ordena por ${STRATEGY_OPTIONS.find(s => s.id === config.strategy)?.label || 'balanceado'}` },
            { step: '5', title: 'Fallback automático', desc: 'Se falhar, tenta próxima chave na fila' },
          ].map(s => (
            <div key={s.step} style={{ display: 'flex', alignItems: 'center', gap: theme.sp3 }}>
              <div style={{
                width: 28, height: 28, borderRadius: theme.r2, flexShrink: 0,
                background: theme.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 800, color: T.text,
              }}>{s.step}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{s.title}</div>
                <div style={{ fontSize: 11, color: theme.dimmed }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Settings grid */}
      <Card>
 <SectionHeader title="Configurações Avançadas" subtitle="Fallback, limites e timeouts" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: theme.sp4 }}>
          {/* Fallback toggle */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: theme.muted, display: 'block', marginBottom: 6 }}>
              Fallback automático
            </label>
            <div onClick={() => {
              routingConfig.value = { ...config, fallback_enabled: !config.fallback_enabled };
            }} style={{
              width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
              background: config.fallback_enabled ? theme.success : theme.dimmed,
              position: 'relative', transition: 'all 0.2s',
            }}>
              <div style={{
                width: 20, height: 20, borderRadius: 10, background: '#fff',
                position: 'absolute', top: 3,
                left: config.fallback_enabled ? 25 : 3, transition: 'all 0.2s',
              }} />
            </div>
          </div>

          {/* Fallback provider */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: theme.muted, display: 'block', marginBottom: 6 }}>
              Provider fallback
            </label>
            <Select value={config.fallback_provider} onChange={(v) => {
              routingConfig.value = { ...config, fallback_provider: v };
            }} options={Object.keys(PROVIDERS).map((o: string) => ({ value: o, label: PROVIDERS[o]?.name || o }))} style={{
              width: '100%', padding: '10px 14px', borderRadius: theme.r3, fontSize: 13,
              background: theme.bg, border: `1px solid ${theme.borderSolid}`, color: theme.text,
              outline: 'none', fontFamily: theme.font, boxSizing: 'border-box',
            }} />
          </div>

          {/* Max retries */}
          <InputField label="Max retries" value={config.max_retries} type="number"
            onInput={(e: any) => { routingConfig.value = { ...config, max_retries: Number(e.target.value) }; }} />

          {/* Timeout */}
          <InputField label="Timeout (ms)" value={config.timeout_ms} type="number"
            onInput={(e: any) => { routingConfig.value = { ...config, timeout_ms: Number(e.target.value) }; }} />

          {/* Budget daily */}
          <InputField label="Budget diário ($)" value={config.budget_limit_daily} type="number"
            onInput={(e: any) => { routingConfig.value = { ...config, budget_limit_daily: Number(e.target.value) }; }} />

          {/* Budget monthly */}
          <InputField label="Budget mensal ($)" value={config.budget_limit_monthly} type="number"
            onInput={(e: any) => { routingConfig.value = { ...config, budget_limit_monthly: Number(e.target.value) }; }} />

          {/* Max history messages */}
          <div>
            <InputField label="Limite de histórico (msgs)" value={config.max_history_messages} type="number"
              onInput={(e: any) => {
                const v = Math.max(10, Math.min(500, Number(e.target.value) || 50));
                routingConfig.value = { ...config, max_history_messages: v };
              }} />
            <div style={{ fontSize: 10, color: theme.dimmed, marginTop: 2 }}>
 Recomendado 50-100 msgs. Evita enviar histórico completo ao LLM.
            </div>
          </div>
        </div>
      </Card>

      {/* Save button */}
      <div>
        <PrimaryButton onClick={saveConfig} disabled={saving}>
 {saving ? 'Salvando...' : 'Salvar Configurações'}
        </PrimaryButton>
      </div>
    </div>
  );
}

// ═══ Users Tab ═══

interface CustomerPricingItem {
  id: string;
  customer_id: string;
  provider: string;
  model: string;
  markup_pct: number;
  default_markup_pct: number;
  cost_input_per_1m: number;
  cost_output_per_1m: number;
  is_customized: boolean;
  updated_at: string;
}

function CustomerPricingModal({ customerId, customerEmail, onClose }: { customerId: string; customerEmail: string; onClose: () => void }) {
  const [pricing, setPricing] = useState<CustomerPricingItem[]>([]);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({ markup_pct: '' });
  const [resetting, setResetting] = useState(false);

  const loadPricing = async () => {
    setLoadingPricing(true);
    try {
      const res = await api.get<any>(`/admin/router/users/${customerId}/pricing`);
      const data = res.data || res;
      setPricing(data.pricing || []);
    } catch (e: any) {
      showToast('error', `Erro ao carregar pricing: ${e.message}`);
    }
    setLoadingPricing(false);
  };

  useEffect(() => { loadPricing(); }, []);

  const customizedCount = pricing.filter(p => p.is_customized).length;

  const startEdit = (item: CustomerPricingItem) => {
    setEditingId(item.id);
    setEditValues({
      markup_pct: String(item.markup_pct),
    });
  };

  const saveEdit = async (item: CustomerPricingItem) => {
    try {
      await api.put(`/admin/router/users/${customerId}/pricing/${item.id}`, {
        markup_pct: parseFloat(editValues.markup_pct),
        provider: item.provider,
        model: item.model,
      });
      showToast('success', `Markup customizado: ${item.provider}/${item.model}`);
      setEditingId(null);
      await loadPricing();
    } catch (e: any) {
      showToast('error', `Erro: ${e.message}`);
    }
  };

  const resetModel = async (item: CustomerPricingItem) => {
    try {
      await api.post(`/admin/router/users/${customerId}/pricing/reset`, {
        provider: item.provider,
        model: item.model,
      });
      showToast('success', `${item.provider}/${item.model} voltou ao default`);
      await loadPricing();
    } catch (e: any) {
      showToast('error', `Erro: ${e.message}`);
    }
  };

  const resetAll = async () => {
    setResetting(true);
    try {
      await api.post(`/admin/router/users/${customerId}/pricing/reset`, {});
      showToast('success', 'Todos os preços voltaram ao default');
      await loadPricing();
    } catch (e: any) {
      showToast('error', `Erro: ${e.message}`);
    }
    setResetting(false);
  };

  return (
    <Modal open={true} onClose={onClose} title={`Preços — ${customerEmail || customerId.substring(0, 12)}`} width={900}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.sp4, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Badge color={customizedCount > 0 ? theme.success : theme.dimmed}>{`${customizedCount} customizado${customizedCount !== 1 ? 's' : ''}`}</Badge>
            <Badge color={theme.dimmed}>{`${pricing.length - customizedCount} default`}</Badge>
          </div>
          {customizedCount > 0 && (
            <SecondaryButton onClick={resetAll} danger>
 {resetting ? '...' : `Resetar Tudo (${customizedCount})`}
            </SecondaryButton>
          )}
        </div>

        {loadingPricing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1,2,3].map(i => <Skeleton key={i} width="100%" height={32} />)}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', maxHeight: '60vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.border}`, position: 'sticky', top: 0, background: theme.bg, zIndex: 1 }}>
                  {['Provider', 'Modelo', 'Status', 'Custo In', 'Custo Out', 'Markup %', ''].map(h => (
                    <th key={h} style={{ textAlign: ['Provider', 'Modelo', 'Status', ''].includes(h) ? 'left' : 'right', padding: '8px', color: theme.muted, fontWeight: 600, fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pricing.map(p => {
                  const prov = PROVIDERS[p.provider];
                  const isEditing = editingId === p.id;
                  return (
                    <tr key={p.id} style={{
                      borderBottom: `1px solid ${theme.border}`,
                      background: p.is_customized ? T.successBg : 'transparent',
                    }}>
                      <td style={{ padding: '8px', display: 'flex', alignItems: 'center', gap: 6 }}>
 <span style={{ fontSize: 14 }}>{prov?.icon || ''}</span>
                        <span style={{ color: prov?.color || theme.text, fontWeight: 600, fontSize: 11 }}>{prov?.name || p.provider}</span>
                      </td>
                      <td style={{ padding: '8px', color: theme.cyan }}><code style={{ fontSize: 11 }} title={p.model}>{displayModelName(p.model)}</code></td>
                      <td style={{ padding: '8px' }}>
                        {p.is_customized ? (
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: T.successBg, color: theme.success, fontWeight: 600 }}>Customizado</span>
                        ) : (
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: T.bgCard, color: theme.dimmed, fontWeight: 600 }}>Default</span>
                        )}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <span style={{ color: theme.text }}>${p.cost_input_per_1m.toFixed(3)}</span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <span style={{ color: theme.text }}>${p.cost_output_per_1m.toFixed(3)}</span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        {isEditing ? (
                          <Input value={editValues.markup_pct} onChange={(v) => setEditValues(prev => ({ ...prev, markup_pct: v }))}
                            style={{ width: 50, padding: '2px 4px', borderRadius: 4, background: theme.bg, border: `1px solid ${theme.warning}`, color: theme.warning, fontSize: 11, textAlign: 'right' }} />
                        ) : (
                          <span style={{ color: theme.warning }}>{p.markup_pct}%</span>
                        )}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {isEditing ? (
                            <>
 <Button onClick={() => saveEdit(p)} variant="success" size="xs" style={{ padding: '2px 8px', fontSize: 11, borderRadius: 4 }}></Button>
                              <Button onClick={() => setEditingId(null)} variant="ghost" size="xs" style={{ background: theme.dimmed, color: T.text, padding: '2px 8px', fontSize: 11, borderRadius: 4 }}></Button>
                            </>
                          ) : (
                            <>
 <Button onClick={() => startEdit(p)} variant="ghost" size="xs" style={{ background: 'transparent', border: 'none', color: theme.muted, fontSize: 12, padding: 0 }} title="Editar"></Button>
                              {p.is_customized && (
 <Button onClick={() => resetModel(p)} variant="ghost" size="xs" style={{ background: 'transparent', border: 'none', color: theme.dimmed, fontSize: 12, padding: 0 }} title="Resetar para default"></Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
  );
}

function UsersTab() {
  const [selectedCustomer, setSelectedCustomer] = useState<{ id: string; email: string } | null>(null);
  const [customers, setCustomers] = useState<Array<{ id: string; email: string; total_requests: number; total_spent: number }>>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);

  useEffect(() => {
    api.get<any>('/admin/router/billing/customers').then((res: any) => {
      const data = res.data || res;
      setCustomers(data.customers || []);
    }).catch(() => {}).finally(() => setLoadingCustomers(false));
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
 <SectionHeader title="Usuários & Pricing" subtitle="Preços personalizados por cliente — clique para editar" />
        <PrimaryButton onClick={() => showAddUser.value = true}>
          + Adicionar Override
        </PrimaryButton>
      </div>

      {/* Info box */}
      <div style={{
        padding: '14px 18px', borderRadius: theme.r4,
        background: theme.gradientSubtle, border: `1px solid ${theme.borderSolid}`,
        display: 'flex', alignItems: 'center', gap: theme.sp3,
        fontSize: 12, color: theme.muted,
      }}>
 <span style={{ fontSize: 20, flexShrink: 0 }}></span>
        <span>
 Cada usuário tem seus próprios preços de LLM. Clique em <strong style={{ color: theme.text }}>Pricing</strong> para editar os preços individuais.
          Usuários sem personalização usam os preços da aba "Custos LLM" (default).
        </span>
      </div>

      {/* Customer list with pricing button */}
      {loadingCustomers ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[1,2,3].map(i => <Skeleton key={i} width="100%" height={60} />)}
        </div>
      ) : customers.length === 0 ? (
 <UiEmptyState title="Nenhum usuário com uso" description="Usuários aparecerão aqui após usar o router" />
      ) : (
        <Card>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                  {['Usuário', 'Requests', 'Gasto Total', 'Ações'].map(h => (
                    <th key={h} style={{ textAlign: h === 'Ações' ? 'center' : 'left', padding: '8px', color: theme.muted, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ padding: '8px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{(c as any).customer_id?.substring(0, 12) || c.id.substring(0, 12)}...</div>
                    </td>
                    <td style={{ padding: '8px', color: theme.muted }}>{c.total_requests.toLocaleString()}</td>
                    <td style={{ padding: '8px', color: theme.success, fontWeight: 600 }}>${c.total_spent.toFixed(4)}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      <Button onClick={() => setSelectedCustomer({ id: (c as any).customer_id || c.id, email: (c as any).customer_id || c.id })}
                        variant="secondary" size="xs" style={{
                          padding: '6px 14px', borderRadius: theme.r2,
                          fontSize: 12, fontWeight: 600, fontFamily: theme.font,
                        }}>
 Pricing
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* User overrides section */}
      {userOverrides.value.length > 0 && (
        <>
 <SectionHeader title="Overrides de Roteamento" subtitle="Configurações de modelo/estratégia por usuário" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp3 }}>
            {userOverrides.value.map(u => (
              <Card key={u.id}>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.sp3, flexWrap: 'wrap' }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: theme.r4, flexShrink: 0,
                    background: theme.gradientSubtle, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18,
 }}></div>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{u.customer_name}</div>
                    <code style={{ fontSize: 11, color: theme.dimmed }}>{u.customer_id}</code>
                  </div>
 <SecondaryButton onClick={() => setSelectedCustomer({ id: u.customer_id, email: u.customer_name })}>Pricing</SecondaryButton>
 <SecondaryButton onClick={() => {}}>Editar</SecondaryButton>
                </div>
                <div style={{ display: 'flex', gap: theme.sp2, marginTop: theme.sp3, flexWrap: 'wrap' }}>
                  {u.provider_override && <Badge color={theme.primary}>{`Provider: ${PROVIDERS[u.provider_override]?.name || u.provider_override}`}</Badge>}
                  {u.model_override && <Badge color={theme.indigo}>{`Modelo: ${u.model_override}`}</Badge>}
                  {u.strategy_override && <Badge color={theme.success}>{`Estratégia: ${STRATEGY_OPTIONS.find(s => s.id === u.strategy_override)?.label || u.strategy_override}`}</Badge>}
                  {u.budget_limit !== null && <Badge color={theme.warning}>{`Budget: $${u.budget_limit}/mês`}</Badge>}
                </div>
 {u.notes && <div style={{ fontSize: 12, color: theme.dimmed, marginTop: theme.sp3, fontStyle: 'italic' }}> {u.notes}</div>}
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Customer Pricing Modal */}
      {selectedCustomer && (
        <CustomerPricingModal
          customerId={selectedCustomer.id}
          customerEmail={selectedCustomer.email}
          onClose={() => setSelectedCustomer(null)}
        />
      )}
    </div>
  );
}

// ═══ Tokens Tab ═══

function TokensTab() {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [machineId, setMachineId] = useState('');
  const [budget, setBudget] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editBudget, setEditBudget] = useState(0);
  const [editMachine, setEditMachine] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  async function createToken() {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const res = await api.post<{ token: RouterToken; plaintext_token: string }>('/admin/router/tokens', {
        name: name.trim(),
        machine_id: machineId.trim() || undefined,
        budget_limit_monthly: budget,
      });
      newTokenPlaintext.value = res.plaintext_token;
      tokens.value = [{ ...res.token, is_active: true, scopes: res.token.scopes || ['chat'] }, ...tokens.value];
      setName('');
      setMachineId('');
      setBudget(0);
      setShowCreateForm(false);
      showToast('success', 'Token criado! Copie-o agora — ele não será exibido novamente.');
    } catch (e: any) {
      showToast('error', e.message || 'Erro ao criar token');
    } finally {
      setCreating(false);
    }
  }

  async function revokeToken(id: string) {
    if (!confirm('Revogar este token? Ele não poderá mais ser usado.')) return;
    try {
      await api.delete(`/admin/router/tokens/${id}`);
      tokens.value = tokens.value.map(t => t.id === id ? { ...t, is_active: false, revoked_at: new Date().toISOString() } : t);
      showToast('success', 'Token revogado.');
    } catch (e: any) {
      showToast('error', e.message || 'Erro ao revogar');
    }
  }

  async function saveEdit(id: string) {
    try {
      const res = await api.patch<RouterToken>(`/admin/router/tokens/${id}`, {
        name: editName.trim() || undefined,
        budget_monthly: editBudget,
        machine_id: editMachine.trim() || null,
      });
      tokens.value = tokens.value.map(t => t.id === id ? { ...t, ...res } : t);
      setEditingId(null);
      showToast('success', 'Token atualizado!');
    } catch (e: any) {
      showToast('error', e.message || 'Erro ao atualizar');
    }
  }

  function startEdit(t: RouterToken) {
    setEditingId(t.id);
    setEditName(t.name);
    setEditBudget(t.budget_limit_monthly);
    setEditMachine(t.machine_id || '');
  }

  function tokenStatus(t: RouterToken): { label: string; color: string; icon: string } {
 if (!t.is_active || t.revoked_at) return { label: 'Revogado', color: T.danger, icon: '' };
 if (t.budget_limit_monthly > 0 && t.budget_used_monthly >= t.budget_limit_monthly) return { label: 'Limite atingido', color: T.warning, icon: '' };
 if (t.expires_at && new Date(t.expires_at) < new Date()) return { label: 'Expirado', color: T.warning, icon: '' };
 return { label: 'Ativo', color: T.success, icon: '' };
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp5 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
 <SectionHeader title="Meus Tokens" subtitle={`${tokens.value.filter(t => t.is_active).length} ativos de ${tokens.value.length} total`} />
        {!showCreateForm && (
          <PrimaryButton onClick={() => setShowCreateForm(true)}>
            + Novo Token
          </PrimaryButton>
        )}
      </div>

      {/* New Token Created Banner */}
      {newTokenPlaintext.value && (
        <div style={{
          padding: theme.sp5, borderRadius: theme.r5,
          background: T.primaryBg, border: `1px solid ${T.primaryBg}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.sp3 }}>
 <span style={{ fontSize: 14, fontWeight: 700, color: theme.primary }}>Novo Token Criado</span>
            <Button onClick={() => newTokenPlaintext.value = ''} variant="ghost" size="xs" style={{
              background: T.border, color: theme.muted,
              fontSize: 14, width: 28, height: 28, borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
            }}></Button>
          </div>
          <div style={{
            fontSize: 12, color: theme.warning, marginBottom: theme.sp3,
            padding: '8px 12px', borderRadius: theme.r2,
            background: T.warningBg, border: `1px solid ${T.warningBg}`,
          }}>
 Copie agora! Este token só é exibido <strong>uma vez</strong> e não poderá ser recuperado.
          </div>
          <div style={{
            background: T.bg, borderRadius: theme.r3, padding: theme.sp3,
            display: 'flex', alignItems: 'center', gap: theme.sp3,
            border: `1px solid ${T.bgActive}`,
          }}>
            <code style={{ fontSize: 13, color: theme.success, fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
              {newTokenPlaintext.value}
            </code>
            <SecondaryButton onClick={() => {
              navigator.clipboard.writeText(newTokenPlaintext.value);
              showToast('success', 'Token copiado!');
 }}>Copiar</SecondaryButton>
          </div>
        </div>
      )}

      {/* Create Token Form (collapsible) */}
      {showCreateForm && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.sp4 }}>
 <SectionHeader title="Criar Novo Token" />
            <Button onClick={() => setShowCreateForm(false)} variant="ghost" size="xs" style={{
              background: T.bgCard, color: theme.dimmed,
              fontSize: 14, width: 28, height: 28, borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
            }}></Button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: theme.sp3 }}>
            <InputField label="Nome" value={name} onInput={(e: any) => setName(e.target.value)} placeholder="Ex: Máquina Prod 1" />
            <InputField label="Máquina (opcional)" value={machineId} onInput={(e: any) => setMachineId(e.target.value)} placeholder="ID da máquina" mono />
            <InputField label={<>Budget Mensal ($) <span style={{ color: theme.dimmed, fontWeight: 400 }}>(0 = sem limite)</span></>}
              value={budget} onInput={(e: any) => setBudget(Number(e.target.value))} type="number" />
          </div>
          <div style={{ marginTop: theme.sp4 }}>
            <PrimaryButton onClick={createToken} disabled={creating || !name.trim()}>
 {creating ? 'Criando...' : 'Gerar Token'}
            </PrimaryButton>
          </div>
        </Card>
      )}

      {/* Token List */}
      {tokens.value.length === 0 ? (
 <UiEmptyState title="Nenhum token criado" description="Gere um token para conectar suas aplicações ao Elon Router" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp3 }}>
          {tokens.value.map(t => {
            const status = tokenStatus(t);
            const isEditing = editingId === t.id;
            return (
              <Card key={t.id} style={{
                opacity: t.is_active ? 1 : 0.6,
                borderLeft: `3px solid ${status.color}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: theme.sp3 }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp2 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: theme.sp2 }}>
                          <Input value={editName} onChange={(v) => setEditName(v)} placeholder="Nome"
                            style={{ padding: '8px 12px', borderRadius: theme.r2, border: `1px solid ${theme.borderSolid}`, background: theme.bg, color: theme.text, fontSize: 13, fontFamily: theme.font }} />
                          <Input value={editMachine} onChange={(v) => setEditMachine(v)} placeholder="Machine ID"
                            style={{ padding: '8px 12px', borderRadius: theme.r2, border: `1px solid ${theme.borderSolid}`, background: theme.bg, color: theme.text, fontSize: 13, fontFamily: 'monospace' }} />
                          <Input type="number" value={editBudget} onChange={(v) => setEditBudget(Number(v))}
                            style={{ padding: '8px 12px', borderRadius: theme.r2, border: `1px solid ${theme.borderSolid}`, background: theme.bg, color: theme.text, fontSize: 13, fontFamily: theme.font }} />
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
 <PrimaryButton onClick={() => saveEdit(t.id)} style={{ padding: '6px 14px', fontSize: 12 }}>Salvar</PrimaryButton>
                          <SecondaryButton onClick={() => setEditingId(null)}>Cancelar</SecondaryButton>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{t.name}</span>
                          <Badge color={status.color} style={{fontSize:10}}>{`${status.icon} ${status.label}`}</Badge>
                        </div>
                        <code style={{ fontSize: 12, color: theme.muted, fontFamily: 'monospace', background: T.bgCard, padding: '2px 8px', borderRadius: 4 }}>
                          {t.token_prefix}••••••••
                        </code>
                        <div style={{ display: 'flex', gap: theme.sp4, marginTop: 8, fontSize: 12, color: theme.dimmed, flexWrap: 'wrap' }}>
 <span> ${t.budget_used_monthly.toFixed(2)}{t.budget_limit_monthly > 0 ? ` / $${t.budget_limit_monthly}` : ' (sem limite)'}</span>
 {t.machine_id && <span> {t.machine_id.substring(0, 12)}...</span>}
                        </div>
                        {t.budget_limit_monthly > 0 && (
                          <div style={{ marginTop: 8, height: 4, borderRadius: 2, background: T.border, overflow: 'hidden' }}>
                            <div style={{
                              height: '100%', borderRadius: 2,
                              width: `${Math.min(100, (t.budget_used_monthly / t.budget_limit_monthly) * 100)}%`,
                              background: t.budget_used_monthly >= t.budget_limit_monthly ? theme.error : t.budget_used_monthly >= t.budget_limit_monthly * 0.8 ? theme.warning : theme.success,
                              transition: 'width 0.3s',
                            }} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {!isEditing && (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {t.is_active && (
                        <>
 <SecondaryButton onClick={() => startEdit(t)}>Editar</SecondaryButton>
                          <SecondaryButton onClick={() => revokeToken(t.id)} danger>Revogar</SecondaryButton>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══ Billing Tab (Provider Credits) ═══

const PROVIDER_ICONS: Record<string, string> = {
 anthropic: '', openai: '', google: '', groq: '', deepseek: '',
 mistral: '', together: '', openrouter: '', xai: '', cohere: '',
};

const PROVIDER_COLORS: Record<string, string> = {
  anthropic: '#D4A574', openai: '#10A37F', google: T.primary, groq: '#F55036',
  deepseek: '#0066FF', mistral: '#FF6F00', together: T.accent, openrouter: T.purple,
  xai: '#1DA1F2', cohere: '#9333EA',
};

interface BillingInfo {
  provider: string;
  key_id: string;
  key_label: string;
  status: string;
  credits_remaining: number | null;
  credits_total: number | null;
  credits_used: number | null;
  usage_pct: number | null;
  quota_reset_at: string | null;
  spend_rate_daily: number | null;
  estimated_days_left: number | null;
  raw: any;
  fetched_at: string;
  cached: boolean;
  error?: string;
}

function BillingTab() {
  const [billing, setBilling] = useState<Record<string, BillingInfo[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState('');
  const refreshRef = useRef<any>(null);

  async function loadBilling() {
    try {
      const res = await api.get<{ billing: Record<string, BillingInfo[]> }>('/admin/router/providers/billing/all');
      setBilling(res.billing || {});
      setLastRefresh(new Date().toLocaleTimeString());
      setError('');
    } catch (e: any) {
      setError(e.message || 'Failed to load billing data');
    }
    setLoading(false);
  }

  useEffect(() => {
    loadBilling();
    // Auto-refresh every 30s
    refreshRef.current = setInterval(loadBilling, 30000);
    return () => { if (refreshRef.current) clearInterval(refreshRef.current); };
  }, []);

  const allProviders = Object.keys(billing);
  const hasAnyData = allProviders.some(p => billing[p]?.length > 0);

  // Count providers with credits info
  const withCredits = allProviders.filter(p =>
    billing[p]?.some(b => b.credits_remaining !== null)
  ).length;

  // Count low credit alerts
  const lowCredit = allProviders.filter(p =>
    billing[p]?.some(b => b.usage_pct !== null && b.usage_pct > 90)
  ).length;

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp4 }}>
        {[1, 2, 3, 4].map(i => (
          <Card key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp3 }}>
              <SkeletonBar width="30%" height={20} />
              <SkeletonBar width="60%" />
              <SkeletonBar width="100%" height={8} />
              <SkeletonBar width="45%" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp5 }}>
      {/* Header */}
      <Card style={{ padding: '14px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
 <span style={{ fontSize: 20 }}></span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>Provider Billing Dashboard</div>
              <div style={{ fontSize: 11, color: theme.dimmed }}>
 {allProviders.length} providers • {withCredits} com créditos • {lowCredit > 0 ? ` ${lowCredit} baixo` : 'OK'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
 {lastRefresh && <span style={{ fontSize: 10, color: theme.dimmed }}> {lastRefresh}</span>}
            <Button onClick={() => { setLoading(true); loadBilling(); }} variant="secondary" size="xs" style={{
              padding: '6px 14px', borderRadius: 6,
              fontSize: 12, fontWeight: 600, fontFamily: theme.font,
            }}>
              Refresh
            </Button>
          </div>
        </div>
      </Card>

      {error && (
        <Card style={{ borderLeft: `3px solid ${T.danger}`, padding: '12px 16px' }}>
 <div style={{ color: T.danger, fontSize: 13 }}> {error}</div>
        </Card>
      )}

      {/* Provider Cards Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: theme.sp4 }}>
        {allProviders.map(provider => {
          const entries = billing[provider] || [];
 const icon = PROVIDER_ICONS[provider] || '';
          const color = PROVIDER_COLORS[provider] || theme.primary;

          return entries.map((entry, idx) => {
            const isOk = entry.status === 'ok' || entry.status === 'cached';
            const hasCredits = entry.credits_remaining !== null;
            const isLow = entry.usage_pct !== null && entry.usage_pct > 90;
            const statusColor = entry.status === 'error' ? T.danger : entry.status === 'unsupported' ? theme.dimmed : isLow ? T.warning : theme.success;
 const statusIcon = entry.status === 'error' ? '' : entry.status === 'unsupported' ? '' : isLow ? '' : '';
            const usagePct = entry.usage_pct ?? 0;

            return (
              <Card key={`${provider}-${idx}`} style={{
                borderLeft: `3px solid ${color}`,
                transition: 'transform 0.15s',
              }}>
                <div style={{ padding: '4px 0' }}>
                  {/* Provider Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.sp3 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 18 }}>{icon}</span>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: theme.text, textTransform: 'capitalize' }}>{provider}</div>
                        <div style={{ fontSize: 11, color: theme.dimmed }}>{entry.key_label}</div>
                      </div>
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '3px 8px', borderRadius: 12,
                      background: `${statusColor}15`, fontSize: 11, fontWeight: 600, color: statusColor,
                    }}>
                      {statusIcon} {entry.status}
                    </div>
                  </div>

                  {/* Credits Info */}
                  {hasCredits ? (
                    <div style={{ marginBottom: theme.sp3 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: theme.muted }}>Créditos</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: theme.text }}>
                          ${entry.credits_remaining?.toFixed(2)} {entry.credits_total ? `/ $${entry.credits_total.toFixed(2)}` : ''}
                        </span>
                      </div>
                      {/* Progress Bar */}
                      <div style={{ height: 8, borderRadius: 4, background: T.border, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 4,
                          width: `${Math.min(100, usagePct)}%`,
                          background: isLow ? 'linear-gradient(90deg, #F59E0B, #EF4444)' : `linear-gradient(90deg, ${color}, ${color}99)`,
                          transition: 'width 0.5s ease',
                        }} />
                      </div>
                      {entry.usage_pct !== null && (
                        <div style={{ fontSize: 10, color: isLow ? T.warning : theme.dimmed, marginTop: 4, textAlign: 'right' }}>
                          {usagePct.toFixed(1)}% usado
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ padding: '8px 0', marginBottom: theme.sp3 }}>
                      <div style={{ fontSize: 12, color: theme.dimmed, fontStyle: 'italic' }}>
 {isOk ? 'Key válida — billing não disponível via API' : entry.error || 'Sem dados de billing'}
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${theme.borderSolid}`, paddingTop: 8 }}>
                    <div style={{ fontSize: 10, color: theme.dimmed }}>
 {entry.cached ? 'Cache' : 'Live'} • {new Date(entry.fetched_at).toLocaleTimeString()}
                    </div>
                    {entry.quota_reset_at && (
                      <div style={{ fontSize: 10, color: theme.dimmed }}>
                        Reset: {new Date(entry.quota_reset_at).toLocaleDateString()}
                      </div>
                    )}
                    {entry.key_id && (
                      <div style={{ fontSize: 10, color: theme.dimmed, fontFamily: 'monospace' }}>
                        {entry.key_id.substring(0, 8)}...
                      </div>
                    )}
                  </div>

                  {/* Low Credit Alert */}
                  {isLow && (
                    <div style={{
                      marginTop: 8, padding: '6px 10px', borderRadius: 6,
                      background: T.warningBg, border: `1px solid ${T.warningBg}`,
                      fontSize: 11, color: T.warning, fontWeight: 600,
                    }}>
 Crédito baixo! Menos de 10% restante.
                    </div>
                  )}
                </div>
              </Card>
            );
          });
        })}
      </div>

      {!hasAnyData && !error && (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
 <span style={{ fontSize: 40 }}></span>
            <div style={{ fontSize: 14, color: theme.muted, marginTop: 12 }}>Nenhum provedor com chaves configuradas</div>
            <div style={{ fontSize: 12, color: theme.dimmed, marginTop: 4 }}>Adicione chaves na aba "Chaves LLM" primeiro</div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ═══ Analytics Tab ═══

interface AnalyticsData {
  by_model: Array<{ model: string; total_requests: number; total_tokens: number; total_cost: number; pct: number }>;
  by_action: Array<{ action_type: string; total_requests: number; total_tokens: number; total_cost: number; pct: number }>;
  by_customer: Array<{ customer_id: string; customer_email: string; total_requests: number; total_tokens: number; total_cost: number; pct: number }>;
  by_hour: Array<{ hour: string; total_requests: number; total_tokens: number; total_cost: number }>;
  by_token: Array<{ key_id: string; token_name: string; budget_limit: number; total_requests: number; total_tokens: number; total_cost: number; budget_pct: number | null }>;
  total_tokens: number;
  period_hours: number;
}

interface AlertData {
  alerts: Array<{ severity: string; type: string; message: string; timestamp: string }>;
  summary: { critical: number; warning: number; info: number };
}

interface SkippedData {
  skipped_24h: number;
  processed_24h: number;
  tokens_saved_estimate: number;
  cost_saved_estimate: number;
}

interface AttemptItem {
  order: number;
  key_id: string;
  provider: string;
  model: string;
  status: string;
  error?: string;
  error_message?: string;
  error_type?: string;
  latency_ms: number;
  attempt_order?: number;
}

interface RequestItem {
  id: string;
  timestamp: string;
  customer: { id: string; email: string };
  token: string;
  provider: string;
  model: string;
  action: string;
  status: string;
  tokens: { input: number; output: number; context: number; user_message: number };
  message_count: number;
  cost: number;
  latency_ms: number;
  error_message?: string;
  runner_id?: string | null;
  agent_id?: string | null;
  total_attempts?: number;
  successful_key_id?: string | null;
  attempts?: AttemptItem[];
  // MEGA fields
  total_tokens?: number;
  margin?: number;
  total_latency_ms?: number;
  finish_reason?: string | null;
  failure_type?: string | null;
  observations?: string[];
  balance_before?: number | null;
  balance_after?: number | null;
  request_model?: string;
  cost_provider?: number;
  price_charged?: number;
  ip_address?: string | null;
  charge_status?: string | null;
}

function fmtTokens(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function fmtCost(n: number): string {
  // Show 4 decimals for small values, 2 for larger
  if (Math.abs(n) < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

const MODEL_DISPLAY_NAMES: Record<string, string> = {
  'claude-opus-4-20250514': 'claude-opus-4.6',
  'claude-sonnet-4-20250514': 'claude-sonnet-4.5',
  'claude-haiku-4-5-20250514': 'claude-haiku-4.5',
  'claude-haiku-4-5-20241022': 'claude-haiku-4.5',
  'claude-3-5-sonnet-20241022': 'claude-3.5-sonnet',
  'claude-3-haiku-20240307': 'claude-3-haiku',
  'claude-3-opus-20240229': 'claude-3-opus',
};

function displayModelName(model: string): string {
  return MODEL_DISPLAY_NAMES[model] || model;
}

function fmtModel(model: string): { display: string; full: string; isMultiple: boolean } {
  if (!model) return { display: '—', full: '', isMultiple: false };
  if (model.includes(',')) {
    const models = model.split(',').map(m => m.trim()).filter(Boolean);
    return { display: `Fallback (${models.length})`, full: models.join('\n'), isMultiple: true };
  }
  const nice = displayModelName(model);
  return { display: nice, full: model, isMultiple: nice !== model };
}

function humanizeTime(ms: number): string {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms/1000).toFixed(1)}s`;
  return `${Math.floor(ms/60000)}m ${Math.round((ms%60000)/1000)}s`;
}

function fmtTotalTokens(n: number): string {
  if (!n) return '—';
  return fmtTokens(n);
}

const obsIcons: Record<string, {icon: string, color: string, label: string}> = {
 bridge_used: { icon: '', color: T.warning, label: 'Bridge' },
 fallback_model: { icon: '', color: T.warning, label: 'Fallback' },
 multiple_attempts: { icon: '', color: T.warning, label: 'Multi-try' },
 insufficient_balance: { icon: '', color: T.danger, label: 'Sem crédito' },
 no_keys: { icon: '', color: T.danger, label: 'Sem chave' },
 budget_exceeded: { icon: '', color: T.danger, label: 'Limite' },
 truncated: { icon: '', color: T.textMuted, label: 'Truncado' },
 heartbeat_skipped: { icon: '', color: T.textMuted, label: 'Skip' },
 all_keys_failed: { icon: '', color: T.danger, label: 'Keys failed' },
 router_error: { icon: '', color: T.danger, label: 'Router error' },
 reconciled: { icon: '', color: T.textMuted, label: 'Reconciliado' },
};

const CHART_COLORS = [T.primary, T.accent, T.success, T.warning, T.danger, T.purple, T.primary, T.danger, '#14B8A6', T.warning];

// ═══ Analytics Summary Cards + Line Chart ═══

function fmtCompact(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(Math.round(n));
}

function AnalyticsSummaryChart({ reqFilters, chartPeriod, setChartPeriod }: { reqFilters: { model: string; action_type: string; status: string; customer_id: string; runner_id: string; agent_id: string; start_date: string; end_date: string; [k: string]: string }; chartPeriod: string; setChartPeriod: (p: string) => void }) {
  const [summary, setSummary] = useState<any>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartMetrics, setChartMetrics] = useState<Set<string>>(new Set(['tokens']));
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingChart, setLoadingChart] = useState(true);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; point: any } | null>(null);

  const metricColors: Record<string, string> = { tokens: T.primary, cost_usd: T.danger, billed: T.success, profit: T.purple };
  const metricLabels: Record<string, string> = { tokens: 'Tokens', cost_usd: 'Custo', billed: 'Faturamento', profit: 'Lucro' };

  const loadSummary = async () => {
    try {
      const params = new URLSearchParams();
      params.set('period', chartPeriod);
      if (reqFilters.model) params.set('model', reqFilters.model);
      if (reqFilters.customer_id) params.set('customer_id', reqFilters.customer_id);
      if (reqFilters.action_type) params.set('action_type', reqFilters.action_type);
      if (reqFilters.status) params.set('status', reqFilters.status);
      if (reqFilters.runner_id) params.set('runner_id', reqFilters.runner_id);
      if (reqFilters.agent_id) params.set('agent_id', reqFilters.agent_id);
      if (reqFilters.start_date) params.set('start_date', reqFilters.start_date);
      if (reqFilters.end_date) params.set('end_date', reqFilters.end_date);
      const res = await api.get(`/admin/router/analytics/summary?${params}`) as any;
      setSummary((res.data || res));
    } catch { /* non-fatal */ }
    setLoadingSummary(false);
  };

  /** Fill time gaps in chart data with zero-value entries so the X axis is continuous */
  const fillChartGaps = (points: any[], period: string) => {
    if (!points.length || period === 'total') return points;
    const empty = { tokens: 0, cost_usd: 0, billed: 0, profit: 0, requests: 0 };
    // Determine step in ms
    let stepMs: number;
    switch (period) {
      case '1h': stepMs = 60_000; break;        // 1 min
      case '24h': stepMs = 3_600_000; break;     // 1 hour
      case '7d': stepMs = 21_600_000; break;     // 6 hours
      case '30d': stepMs = 86_400_000; break;    // 1 day
      default: return points;
    }
    // Build a map of existing points by timestamp
    const map = new Map<string, any>();
    for (const p of points) map.set(p.timestamp, p);
    // Determine start/end from period (use now as end)
    const now = new Date();
    let start: Date;
    switch (period) {
      case '1h': start = new Date(now.getTime() - 3_600_000); break;
      case '24h': start = new Date(now.getTime() - 86_400_000); break;
      case '7d': start = new Date(now.getTime() - 7 * 86_400_000); break;
      case '30d': start = new Date(now.getTime() - 30 * 86_400_000); break;
      default: start = new Date(now.getTime() - 86_400_000);
    }
    // Generate all expected timestamps
    const result: any[] = [];
    let t = start.getTime();
    // Align to step boundary
    t = Math.floor(t / stepMs) * stepMs;
    while (t <= now.getTime()) {
      const d = new Date(t);
      let key: string;
      if (period === '1h') {
        key = d.toISOString().slice(0, 16).replace('T', ' '); // YYYY-MM-DD HH:MM
      } else if (period === '24h') {
        key = d.toISOString().slice(0, 13).replace('T', ' ') + ':00'; // YYYY-MM-DD HH:00
      } else if (period === '7d') {
        const h = d.getUTCHours();
        const aligned = Math.floor(h / 6) * 6;
        key = d.toISOString().slice(0, 10) + ' ' + String(aligned).padStart(2, '0') + ':00';
      } else {
        key = d.toISOString().slice(0, 10); // YYYY-MM-DD
      }
      result.push(map.get(key) || { timestamp: key, ...empty });
      t += stepMs;
    }
    return result;
  };

  const loadChart = async () => {
    setLoadingChart(true);
    try {
      const params = new URLSearchParams({ period: chartPeriod });
      if (reqFilters.model) params.set('model', reqFilters.model);
      if (reqFilters.customer_id) params.set('customer_id', reqFilters.customer_id);
      if (reqFilters.action_type) params.set('action_type', reqFilters.action_type);
      if (reqFilters.status) params.set('status', reqFilters.status);
      if (reqFilters.runner_id) params.set('runner_id', reqFilters.runner_id);
      if (reqFilters.agent_id) params.set('agent_id', reqFilters.agent_id);
      if (reqFilters.start_date) params.set('start_date', reqFilters.start_date);
      if (reqFilters.end_date) params.set('end_date', reqFilters.end_date);
      const res = await api.get(`/admin/router/analytics/chart?${params}`) as any;
      const rawPoints = (res.data || res).data_points || [];
      // Fill gaps: generate zero-value entries for missing time slots
      setChartData(fillChartGaps(rawPoints, chartPeriod));
    } catch { setChartData([]); }
    setLoadingChart(false);
  };

  useEffect(() => { loadSummary(); }, [chartPeriod, reqFilters.model, reqFilters.customer_id, reqFilters.action_type, reqFilters.status, reqFilters.runner_id, reqFilters.agent_id, reqFilters.start_date, reqFilters.end_date]);
  useEffect(() => { loadChart(); }, [chartPeriod, reqFilters.model, reqFilters.customer_id, reqFilters.action_type, reqFilters.status, reqFilters.runner_id, reqFilters.agent_id, reqFilters.start_date, reqFilters.end_date]);

  // Auto-refresh real-time: 15s para 1h, 60s para 24h
  useEffect(() => {
    const intervalMs = chartPeriod === '1h' ? 15000 : chartPeriod === '24h' ? 60000 : 0;
    if (!intervalMs) return;
    const iv = setInterval(() => { loadChart(); loadSummary(); }, intervalMs);
    return () => clearInterval(iv);
  }, [chartPeriod, reqFilters.model, reqFilters.customer_id, reqFilters.action_type, reqFilters.status, reqFilters.runner_id, reqFilters.agent_id, reqFilters.start_date, reqFilters.end_date]);

  const toggleMetric = (m: string) => {
    setChartMetrics(prev => {
      const next = new Set(prev);
      if (next.has(m)) { if (next.size > 1) next.delete(m); } else next.add(m);
      return next;
    });
  };

  // SVG chart rendering
  const svgW = 800, svgH = 260, padL = 60, padR = 20, padT = 20, padB = 40;
  const chartW = svgW - padL - padR, chartH = svgH - padT - padB;
  const activeMetrics = Array.from(chartMetrics);

  const getMaxVal = () => {
    if (!chartData.length) return 1;
    let max = 0;
    for (const d of chartData) {
      for (const m of activeMetrics) {
        if ((d as any)[m] > max) max = (d as any)[m];
      }
    }
    return max || 1;
  };
  const maxVal = getMaxVal();

  const getPoints = (metric: string) => {
    if (!chartData.length) return '';
    return chartData.map((d: any, i: number) => {
      const x = padL + (chartData.length === 1 ? chartW / 2 : (i / (chartData.length - 1)) * chartW);
      const y = padT + chartH - ((d[metric] || 0) / maxVal) * chartH;
      return `${x},${y}`;
    }).join(' ');
  };

  const fmtTimestamp = (ts: string) => {
    if (!ts) return '';
    if (ts.includes('W')) return ts; // weekly
    try {
      // Timestamps vêm em UTC do D1 — converter pra Brasília
      const utc = ts.endsWith('Z') ? ts : ts + 'Z';
      const d = new Date(utc);
      if (isNaN(d.getTime())) throw '';
      if (ts.length === 10) return d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', month: '2-digit', day: '2-digit' });
      return d.toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' });
    } catch {
      if (ts.length === 10) return ts.substring(5);
      if (ts.length >= 16) return ts.substring(11, 16);
      return ts.substring(5);
    }
  };

  const pillStyle = (active: boolean): any => ({
    padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 20, cursor: 'pointer', border: 'none',
    background: active ? T.primaryBg : T.bgCard,
    color: active ? T.primary : theme.muted,
    transition: 'all 0.15s ease',
  });

  return (
    <>
      {/* Summary Cards */}
      <style>{`
        .analytics-summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
        @media (max-width: 1024px) { .analytics-summary-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .analytics-summary-grid { grid-template-columns: 1fr; } }
        @keyframes livePulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.8); } }
      `}</style>
      {loadingSummary ? (
        <div className="analytics-summary-grid">
          {[1,2,3,4].map(i => <div key={i} style={{ background: T.bgCard, borderRadius: 12, padding: 16, height: 80 }}><SkeletonBar width="60%" height={16} /><SkeletonBar width="40%" height={28} /></div>)}
        </div>
      ) : summary && (
        <div className="analytics-summary-grid">
          <div style={{ background: T.primaryBg, borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
 <div style={{ fontSize: 11, color: theme.muted, marginBottom: 4 }}>Tokens Gastos</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: T.primary }}>{fmtCompact(summary.total_tokens)}</div>
            <div style={{ fontSize: 10, color: theme.dimmed }}>{summary.total_requests.toLocaleString()} requests</div>
          </div>
          <div style={{ background: T.dangerBg, borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
 <div style={{ fontSize: 11, color: theme.muted, marginBottom: 4 }}>Custo Provedor</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: T.danger }}>${summary.total_cost_usd.toFixed(2)}</div>
 <div style={{ fontSize: 10, color: theme.dimmed }}> {summary.success_count} / {summary.error_count}</div>
          </div>
          <div style={{ background: T.successBg, borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
 <div style={{ fontSize: 11, color: theme.muted, marginBottom: 4 }}>Faturamento</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: T.success }}>${summary.total_billed.toFixed(2)}</div>
          </div>
          <div style={{ background: summary.total_profit >= 0 ? T.purpleBg : T.dangerBg, borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
 <div style={{ fontSize: 11, color: theme.muted, marginBottom: 4 }}>Lucro</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: summary.total_profit >= 0 ? T.purple : T.danger }}>
              ${summary.total_profit.toFixed(2)}
              <span style={{ fontSize: 12, marginLeft: 4 }}>({summary.total_profit >= 0 ? '+' : ''}{summary.profit_margin_pct.toFixed(1)}%)</span>
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {(chartPeriod === '1h' || chartPeriod === '24h') && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 9, fontWeight: 700, color: '#22c55e', marginRight: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', animation: 'livePulse 1.5s ease-in-out infinite' }} />
                LIVE {chartPeriod === '1h' ? '15s' : '60s'}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {Object.entries(metricLabels).map(([k, label]) => (
              <Button variant="ghost" key={k} onClick={() => toggleMetric(k)} style={{ ...pillStyle(chartMetrics.has(k)), color: chartMetrics.has(k) ? metricColors[k] : theme.muted, background: chartMetrics.has(k) ? `${metricColors[k]}20` : T.bgCard }}>{label}</Button>
            ))}
          </div>
        </div>

        {loadingChart ? (
          <div style={{ height: 260, background: T.bgSubtle, borderRadius: 8, animation: 'pulse 1.5s infinite' }}>
            <style>{`@keyframes pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.8; } }`}</style>
          </div>
        ) : chartData.length === 0 ? (
          <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.dimmed, fontSize: 13 }}>
            Sem dados para o período selecionado
          </div>
        ) : (
          <div style={{ position: 'relative', width: '100%' }} onMouseLeave={() => setTooltip(null)}>
            <svg viewBox={`0 0 ${svgW} ${svgH}`} style={{ width: '100%', height: 'auto', maxHeight: 300 }}>
              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map(pct => {
                const y = padT + chartH * (1 - pct);
                return <g key={pct}>
                  <line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke={T.border} strokeDasharray="4" />
                  <text x={padL - 8} y={y + 4} textAnchor="end" fill={theme.dimmed} fontSize={9}>{fmtCompact(maxVal * pct)}</text>
                </g>;
              })}
              {/* X axis labels */}
              {chartData.map((d: any, i: number) => {
                if (chartData.length > 12 && i % Math.ceil(chartData.length / 8) !== 0) return null;
                const x = padL + (chartData.length === 1 ? chartW / 2 : (i / (chartData.length - 1)) * chartW);
                return <text key={i} x={x} y={svgH - 8} textAnchor="middle" fill={theme.dimmed} fontSize={9}>{fmtTimestamp(d.timestamp)}</text>;
              })}
              {/* Lines */}
              {activeMetrics.map(m => (
                <polyline key={m} points={getPoints(m)} fill="none" stroke={metricColors[m]} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              ))}
              {/* Dots */}
              {activeMetrics.map(m => chartData.map((d: any, i: number) => {
                const x = padL + (chartData.length === 1 ? chartW / 2 : (i / (chartData.length - 1)) * chartW);
                const y = padT + chartH - ((d[m] || 0) / maxVal) * chartH;
                return <circle key={`${m}-${i}`} cx={x} cy={y} r={chartData.length > 30 ? 1.5 : 3} fill={metricColors[m]} />;
              }))}
              {/* Hover areas */}
              {chartData.map((d: any, i: number) => {
                const x = padL + (chartData.length === 1 ? chartW / 2 : (i / (chartData.length - 1)) * chartW);
                return <rect key={`hover-${i}`} x={x - chartW / chartData.length / 2} y={padT} width={chartW / chartData.length} height={chartH}
                  fill="transparent" onMouseEnter={(e: any) => {
                    const rect = (e.target as SVGElement).closest('svg')!.getBoundingClientRect();
                    setTooltip({ x: (x / svgW) * rect.width, y: 10, point: d });
                  }} />;
              })}
            </svg>
            {/* Tooltip */}
            {tooltip && (
              <div style={{
                position: 'absolute', left: Math.min(tooltip.x, 600), top: tooltip.y,
                background: T.bgOverlay, border: `1px solid ${theme.border}`, borderRadius: 8, padding: '8px 12px',
                fontSize: 11, color: theme.text, pointerEvents: 'none', zIndex: 10, minWidth: 120,
              }}>
                <div style={{ fontWeight: 600, marginBottom: 4, color: theme.muted }}>{tooltip.point.timestamp}</div>
                {activeMetrics.map(m => (
                  <div key={m} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ color: metricColors[m] }}>● {metricLabels[m]}</span>
                    <span>{m === 'tokens' ? fmtCompact(tooltip.point[m]) : `$${tooltip.point[m].toFixed(4)}`}</span>
                  </div>
                ))}
                <div style={{ marginTop: 4, color: theme.dimmed }}>{tooltip.point.requests} reqs</div>
              </div>
            )}
          </div>
        )}
      </Card>
    </>
  );
}

function AnalyticsTab() {
  const [realtime, setRealtime] = useState<AnalyticsData | null>(null);
  const [alerts, setAlerts] = useState<AlertData | null>(null);
  const [skipped, setSkipped] = useState<SkippedData | null>(null);
  const [requests, setRequests] = useState<RequestItem[]>([]);
  const [reqPagination, setReqPagination] = useState({ total: 0, page: 1, pages: 0 });
  const [reqOffset, setReqOffset] = useState(0);
  const [reqFilters, setReqFilters] = useState({ model: '', action_type: '', status: '', customer_id: '', runner_id: '', agent_id: '', start_date: '', end_date: '' });
  const [chartPeriod, setChartPeriod] = useState('24h');
  const [expandedReqIds, setExpandedReqIds] = useState<Set<string>>(new Set());
  const [advancedOpenIds, setAdvancedOpenIds] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expandedAttempts, setExpandedAttempts] = useState<Record<string, AttemptItem[]>>({});
  const [requestDetails, setRequestDetails] = useState<Record<string, any>>({});
  const [viewMode, setViewMode] = useState<Record<string, 'compact' | 'full'>>({});
  const [turnGrouping, setTurnGrouping] = useState(false);
  const [expandedTurnGroups, setExpandedTurnGroups] = useState<Set<string>>(new Set());
  const [loadingAttempts, setLoadingAttempts] = useState<string | null>(null);
  const [loadingState, setLoadingState] = useState(true);
  const [filterOptions, setFilterOptions] = useState<{ models: string[]; actions: string[]; customers: Array<{ id: string; email: string }>; agents: Array<{ agent_name: string; agent_id: string | null }> }>({ models: [], actions: [], customers: [], agents: [] });
  const filtersInitialized = useRef(false);

  const loadData = async () => {
    try {
      const [rtRes, alRes, skRes] = await Promise.all([
        api.get('/admin/router/analytics/realtime'),
        api.get('/admin/router/analytics/alerts'),
        api.get('/admin/router/analytics/skipped'),
      ]) as any[];
      if (rtRes.data?.data) setRealtime(rtRes.data.data);
      if (alRes.data?.data) setAlerts(alRes.data.data);
      if (skRes.data?.data) setSkipped(skRes.data.data);
    } catch (e: any) {
      showToast('error', `Analytics load failed: ${e.message}`);
    }
    setLoadingState(false);
  };

  const [loadingMore, setLoadingMore] = useState(false);
  const [visibleGroups, setVisibleGroups] = useState(50);

  const loadRequests = async (offset = 0, append = false) => {
    if (append) setLoadingMore(true);
    try {
      const fetchLimit = turnGrouping ? '1000' : '50';
      const params = new URLSearchParams({ limit: fetchLimit, offset: String(offset), period: chartPeriod });
      if (reqFilters.model) params.set('model', reqFilters.model);
      if (reqFilters.action_type) params.set('action_type', reqFilters.action_type);
      if (reqFilters.status) params.set('status', reqFilters.status);
      if (reqFilters.customer_id) params.set('customer_id', reqFilters.customer_id);
      if (reqFilters.runner_id) params.set('runner_id', reqFilters.runner_id);
      if (reqFilters.agent_id) params.set('agent_id', reqFilters.agent_id);
      if (reqFilters.start_date) params.set('start_date', reqFilters.start_date);
      if (reqFilters.end_date) params.set('end_date', reqFilters.end_date);
      const res = await api.get(`/admin/router/requests?${params}`) as any;
      const data = res.data || res;
      const newRequests = data.requests || [];
      const pagination = data.pagination || { total: 0, page: 1, pages: 0 };
      if (append) {
        setRequests(prev => [...prev, ...newRequests]);
      } else {
        setRequests(newRequests);
      }
      setReqPagination(pagination);
      setAllRequestsLoaded(false);
    } catch (e: any) {
      showToast('error', `Requests load failed: ${e.message}`);
    }
    setLoadingMore(false);
  };


  const loadAttempts = async (requestId: string) => {
    if (expandedAttempts[requestId]) return; // already loaded
    setLoadingAttempts(requestId);
    try {
      const res = await api.get(`/admin/router/attempts/${requestId}`) as any;
      const data = res.data || res;
      const attempts = (data.attempts || []).map((a: any) => ({
        order: a.attempt_order || a.order,
        key_id: a.key_id,
        provider: a.provider,
        model: a.model,
        status: a.status,
        error: a.error_message || a.error,
        error_type: a.error_type,
        latency_ms: a.latency_ms,
      }));
      setExpandedAttempts(prev => ({ ...prev, [requestId]: attempts }));
    } catch {
      // Fallback to inline attempts_json
    }
    setLoadingAttempts(null);
  };

  const loadRequestDetail = async (requestId: string) => {
    if (requestDetails[requestId]) return;
    try {
      const res = await api.get(`/admin/router/requests/${requestId}`) as any;
      const data = res.data || res;
      setRequestDetails(prev => ({ ...prev, [requestId]: data }));
    } catch (err: any) {
      console.error('[ElonRouter] loadRequestDetail failed:', err);
      // Store error state so UI shows error instead of infinite loading
      setRequestDetails(prev => ({ ...prev, [requestId]: { _error: true, error_message: err?.message || 'Falha ao carregar detalhes' } }));
    }
  };

  const toggleAdvanced = (requestId: string) => {
    setAdvancedOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(requestId)) next.delete(requestId); else next.add(requestId);
      return next;
    });
  };

  const copyWithFeedback = (text: string, id: string) => {
    try { navigator.clipboard.writeText(text); } catch {}
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const toggleExpand = (requestId: string) => {
    setExpandedReqIds(prev => {
      const next = new Set(prev);
      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
        loadAttempts(requestId);
        loadRequestDetail(requestId);
      }
      return next;
    });
  };

  // ─── Turn Grouping Logic ───
  interface TurnGroup {
    id: string;
    requests: RequestItem[];
    totalTokens: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCost: number;
    totalCostProvider: number;
    totalPriceCharged: number;
    totalLatencyMs: number;
    firstTimestamp: string;
    model: string;
    customer: { id: string; email: string };
    lastStatus: string;
    lastFinishReason: string | null;
    agentName: string | null;
    keyLabel: string | null;
    provider: string | null;
    userMessage: string | null;
  }

  const computeTurnGroups = (reqs: RequestItem[]): (TurnGroup | RequestItem)[] => {
    if (!reqs.length) return [];
    // Sort by timestamp ASC for grouping
    const sorted = [...reqs].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    const groups: RequestItem[][] = [];
    let current: RequestItem[] = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];
      const gap = new Date(curr.timestamp).getTime() - new Date(prev.timestamp).getTime();
      const sameCustomer = curr.customer.id === prev.customer.id;
      const prevEnded = prev.finish_reason === 'stop' || prev.finish_reason === 'end_turn';

      if (sameCustomer && gap < 60000 && !prevEnded) {
        current.push(curr);
      } else {
        groups.push(current);
        current = [curr];
      }
    }
    groups.push(current);

    // Convert to display items — single requests stay as-is, groups become TurnGroup
    const result: (TurnGroup | RequestItem)[] = [];
    for (const group of groups) {
      if (group.length === 1) {
        result.push(group[0]);
      } else {
        const totalTokens = group.reduce((s, r) => s + (r.total_tokens || r.tokens.input + r.tokens.output), 0);
        const totalInputTokens = group.reduce((s, r) => s + (r.tokens.input || 0), 0);
        const totalOutputTokens = group.reduce((s, r) => s + (r.tokens.output || 0), 0);
        const totalCost = group.reduce((s, r) => s + (r.price_charged || r.cost || 0), 0);
        const totalCostProvider = group.reduce((s, r) => s + (r.cost_provider > 0 ? r.cost_provider : (r.cost > 0 ? r.cost / 1.2 : 0)), 0);
        const totalPriceCharged = group.reduce((s, r) => s + (r.price_charged > 0 ? r.price_charged : r.cost || 0), 0);
        const totalLatencyMs = group.reduce((s, r) => s + (r.total_latency_ms || r.latency_ms || 0), 0);
        const last = group[group.length - 1];
        const agentReq = group.find(r => (r as any).detected_agent_name || (r as any).agent_name);
        const keyReq = group.find(r => (r as any).key_label);
        const provReq = group.find(r => r.provider);
        result.push({
          id: group[0].id,
          requests: group,
          totalTokens,
          totalInputTokens,
          totalOutputTokens,
          totalCost,
          totalCostProvider,
          totalPriceCharged,
          totalLatencyMs,
          firstTimestamp: group[0].timestamp,
          model: group[0].model,
          customer: group[0].customer,
          lastStatus: last.status,
          lastFinishReason: last.finish_reason || null,
          agentName: agentReq ? ((agentReq as any).detected_agent_name || (agentReq as any).agent_name) : null,
          keyLabel: keyReq ? (keyReq as any).key_label : null,
          provider: provReq ? provReq.provider : null,
          userMessage: (group[0] as any).user_message || null,
        });
      }
    }
    // Reverse back to DESC for display
    return result.reverse();
  };

  const isTurnGroup = (item: any): item is TurnGroup => 'requests' in item && Array.isArray(item.requests);

  useEffect(() => {
    loadData();
    loadRequests();
    // Load filter options (distinct models, actions, customers)
    api.get('/admin/router/filters').then((res: any) => {
      const data = res.data || res;
      setFilterOptions({
        models: data.models || [],
        actions: data.actions || [],
        customers: data.customers || [],
        agents: data.agents || [],
      });
      filtersInitialized.current = true;
    }).catch(() => {});
  }, []);

  // Auto-fetch when filters or period change
  useEffect(() => {
    if (!filtersInitialized.current) return;
    setReqOffset(0);
    setVisibleGroups(50);
    loadRequests(0);
  }, [chartPeriod, turnGrouping, reqFilters.model, reqFilters.action_type, reqFilters.status, reqFilters.customer_id, reqFilters.runner_id, reqFilters.agent_id, reqFilters.start_date, reqFilters.end_date]);

  if (loadingState) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp4 }}>
        {[1, 2, 3].map(i => <Card key={i}><SkeletonBar width="60%" height={20} /><SkeletonBar width="80%" /><SkeletonBar width="40%" /></Card>)}
      </div>
    );
  }

  const avgTokensPerHour = realtime?.by_hour?.length
    ? realtime.by_hour.reduce((s, h) => s + h.total_tokens, 0) / realtime.by_hour.length
    : 0;
  const maxTokensInHour = realtime?.by_hour?.length
    ? Math.max(...realtime.by_hour.map(h => h.total_tokens))
    : 0;

  const pillStyle = (active: boolean): any => ({
    padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 20, cursor: 'pointer', border: 'none',
    background: active ? T.primaryBg : T.bgCard,
    color: active ? T.primary : theme.muted,
    transition: 'all 0.15s ease',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp5 }}>
      {/* ─── Alerts Banner ─── */}
      {alerts && alerts.alerts.length > 0 && (
        <Card style={{ border: `1px solid ${alerts.summary.critical > 0 ? T.dangerBg : T.warningBg}` }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, color: alerts.summary.critical > 0 ? theme.error : theme.warning }}>
 Alertas ({alerts.alerts.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {alerts.alerts.map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: theme.text }}>
 <span>{a.severity === 'critical' ? '' : a.severity === 'warning' ? '' : ''}</span>
                <span style={{ flex: 1 }}>{a.message}</span>
                <span style={{ fontSize: 11, color: theme.dimmed }}>{new Date(a.timestamp).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ─── Economy Card ─── */}
      {skipped && (
        <Card style={{ background: `linear-gradient(135deg, ${T.successBgSubtle} 0%, ${T.primaryBgSubtle} 100%)` }}>
 <h3 style={{ margin: '0 0 12px', fontSize: 15, color: theme.success }}>Tokens Economizados (24h)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: theme.sp4 }}>
            <div>
              <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Skipped</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: theme.success }}>{skipped.skipped_24h.toLocaleString()}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Processados</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: theme.primary }}>{skipped.processed_24h.toLocaleString()}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Tokens Salvos</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: theme.success }}>{fmtTokens(skipped.tokens_saved_estimate)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: 1 }}>$ Economizado</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: theme.success }}>{fmtCost(skipped.cost_saved_estimate)}</div>
            </div>
          </div>
          {/* Skip rate bar */}
          {(skipped.skipped_24h + skipped.processed_24h) > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: theme.muted, marginBottom: 4 }}>
                <span>Skip Rate</span>
                <span>{((skipped.skipped_24h / (skipped.skipped_24h + skipped.processed_24h)) * 100).toFixed(1)}%</span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: T.border, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 4, background: theme.success,
                  width: `${(skipped.skipped_24h / (skipped.skipped_24h + skipped.processed_24h)) * 100}%`,
                  transition: 'width 0.5s ease',
                }} />
              </div>
            </div>
          )}
        </Card>
      )}

      {/* ─── Breakdown By Model ─── */}
      {realtime && realtime.by_model.length > 0 && (
        <Card>
 <h3 style={{ margin: '0 0 12px', fontSize: 15, color: theme.text }}>Breakdown por Modelo</h3>
          {/* Visual bars */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            {realtime.by_model.map((m, i) => (
              <div key={m.model} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: theme.muted, minWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.model}>{displayModelName(m.model)}</span>
                <div style={{ flex: 1, height: 20, borderRadius: 4, background: T.bgCard, overflow: 'hidden', position: 'relative' }}>
                  <div style={{ height: '100%', borderRadius: 4, background: CHART_COLORS[i % CHART_COLORS.length], width: `${m.pct}%`, transition: 'width 0.5s ease' }} />
                  <span style={{ position: 'absolute', right: 6, top: 2, fontSize: 11, color: theme.text, fontWeight: 600 }}>{m.pct}%</span>
                </div>
                <span style={{ fontSize: 11, color: theme.dimmed, minWidth: 60, textAlign: 'right' }}>{fmtTokens(m.total_tokens)}</span>
              </div>
            ))}
          </div>
          {/* Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Modelo</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Tokens</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Requests</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Custo</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>%</th>
              </tr>
            </thead>
            <tbody>
              {realtime.by_model.map(m => (
                <tr key={m.model} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <td style={{ padding: '6px 8px', color: theme.text }} title={m.model}>{displayModelName(m.model)}</td>
                  <td style={{ padding: '6px 8px', color: theme.text, textAlign: 'right' }}>{fmtTokens(m.total_tokens)}</td>
                  <td style={{ padding: '6px 8px', color: theme.muted, textAlign: 'right' }}>{m.total_requests.toLocaleString()}</td>
                  <td style={{ padding: '6px 8px', color: theme.warning, textAlign: 'right' }}>{fmtCost(m.total_cost)}</td>
                  <td style={{ padding: '6px 8px', color: theme.primary, textAlign: 'right' }}>{m.pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* ─── Breakdown By Action ─── */}
      {realtime && realtime.by_action.length > 0 && (
        <Card>
 <h3 style={{ margin: '0 0 12px', fontSize: 15, color: theme.text }}>Breakdown por Ação</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            {realtime.by_action.map((a, i) => (
              <div key={a.action_type} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: theme.muted, minWidth: 100 }}>{a.action_type || 'unknown'}</span>
                <div style={{ flex: 1, height: 20, borderRadius: 4, background: T.bgCard, overflow: 'hidden', position: 'relative' }}>
                  <div style={{ height: '100%', borderRadius: 4, background: CHART_COLORS[i % CHART_COLORS.length], width: `${a.pct}%`, transition: 'width 0.5s ease' }} />
                  <span style={{ position: 'absolute', right: 6, top: 2, fontSize: 11, color: theme.text, fontWeight: 600 }}>{a.pct}%</span>
                </div>
                <span style={{ fontSize: 11, color: theme.dimmed, minWidth: 60, textAlign: 'right' }}>{fmtTokens(a.total_tokens)}</span>
              </div>
            ))}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Ação</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Tokens</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Requests</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Custo Médio</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>%</th>
              </tr>
            </thead>
            <tbody>
              {realtime.by_action.map(a => (
                <tr key={a.action_type} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <td style={{ padding: '6px 8px', color: theme.text }}>{a.action_type || 'unknown'}</td>
                  <td style={{ padding: '6px 8px', color: theme.text, textAlign: 'right' }}>{fmtTokens(a.total_tokens)}</td>
                  <td style={{ padding: '6px 8px', color: theme.muted, textAlign: 'right' }}>{a.total_requests.toLocaleString()}</td>
                  <td style={{ padding: '6px 8px', color: theme.warning, textAlign: 'right' }}>{fmtCost(a.total_requests > 0 ? a.total_cost / a.total_requests : 0)}</td>
                  <td style={{ padding: '6px 8px', color: theme.primary, textAlign: 'right' }}>{a.pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* ─── Timeline Chart (24h) ─── */}
      {realtime && realtime.by_hour.length > 0 && (
        <Card>
 <h3 style={{ margin: '0 0 12px', fontSize: 15, color: theme.text }}>Timeline (últimas 24h)</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120, padding: '0 4px' }}>
            {realtime.by_hour.map((h, i) => {
              const heightPct = maxTokensInHour > 0 ? (h.total_tokens / maxTokensInHour) * 100 : 0;
              const isSpike = h.total_tokens > avgTokensPerHour * 2;
              const hourLabel = h.hour.substring(11, 16);
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, position: 'relative' }}
                  title={`${hourLabel} — ${fmtTokens(h.total_tokens)} tokens, ${fmtCost(h.total_cost)}, ${h.total_requests} reqs`}>
                  <div style={{
                    width: '100%', maxWidth: 24, borderRadius: '4px 4px 0 0',
                    background: isSpike ? theme.error : theme.primary,
                    height: `${Math.max(heightPct, 2)}%`,
                    opacity: isSpike ? 1 : 0.7,
                    transition: 'height 0.3s ease',
                  }} />
                  {i % 3 === 0 && <span style={{ fontSize: 9, color: theme.dimmed, whiteSpace: 'nowrap' }}>{hourLabel}</span>}
                </div>
              );
            })}
          </div>
          {/* Average line label */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, color: theme.muted }}>
            <span>Média: {fmtTokens(avgTokensPerHour)}/h</span>
            <span>Pico: {fmtTokens(maxTokensInHour)}</span>
            <span>Total: {fmtTokens(realtime.total_tokens)}</span>
          </div>
        </Card>
      )}

      {/* ─── Top Customers ─── */}
      {realtime && realtime.by_customer.length > 0 && (
        <Card>
 <h3 style={{ margin: '0 0 12px', fontSize: 15, color: theme.text }}>Top Customers</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Customer</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Tokens</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Custo</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>%</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: theme.muted, fontWeight: 600, width: 120 }}>Uso</th>
              </tr>
            </thead>
            <tbody>
              {realtime.by_customer.map(cu => (
                <tr key={cu.customer_id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <td style={{ padding: '6px 8px', color: theme.text, fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cu.customer_email}</td>
                  <td style={{ padding: '6px 8px', color: theme.text, textAlign: 'right' }}>{fmtTokens(cu.total_tokens)}</td>
                  <td style={{ padding: '6px 8px', color: theme.warning, textAlign: 'right' }}>{fmtCost(cu.total_cost)}</td>
                  <td style={{ padding: '6px 8px', color: theme.primary, textAlign: 'right' }}>{cu.pct}%</td>
                  <td style={{ padding: '6px 8px' }}>
                    <div style={{ height: 6, borderRadius: 3, background: T.border, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 3, background: theme.primary, width: `${cu.pct}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* ─── Top Tokens ─── */}
      {realtime && realtime.by_token.length > 0 && (
        <Card>
 <h3 style={{ margin: '0 0 12px', fontSize: 15, color: theme.text }}>Top Tokens</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Token</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Requests</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Custo</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Budget</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: theme.muted, fontWeight: 600, width: 120 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {realtime.by_token.map(tk => {
                const budgetPct = tk.budget_pct;
                const badge = budgetPct !== null
 ? budgetPct > 100 ? '' : budgetPct > 80 ? '' : ''
 : '';
                return (
                  <tr key={tk.key_id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ padding: '6px 8px', color: theme.text, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{tk.token_name || tk.key_id}</td>
                    <td style={{ padding: '6px 8px', color: theme.muted, textAlign: 'right' }}>{tk.total_requests.toLocaleString()}</td>
                    <td style={{ padding: '6px 8px', color: theme.warning, textAlign: 'right' }}>{fmtCost(tk.total_cost)}</td>
                    <td style={{ padding: '6px 8px', color: theme.dimmed, textAlign: 'right' }}>{tk.budget_limit > 0 ? fmtCost(tk.budget_limit) : '—'}</td>
                    <td style={{ padding: '6px 8px' }}>
                      {budgetPct !== null ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span>{badge}</span>
                          <div style={{ flex: 1, height: 6, borderRadius: 3, background: T.border, overflow: 'hidden' }}>
                            <div style={{
                              height: '100%', borderRadius: 3,
                              background: budgetPct > 100 ? theme.error : budgetPct > 80 ? theme.warning : theme.success,
                              width: `${Math.min(budgetPct, 100)}%`,
                            }} />
                          </div>
                          <span style={{ fontSize: 10, color: theme.dimmed }}>{budgetPct.toFixed(0)}%</span>
                        </div>
                      ) : <span style={{ color: theme.dimmed, fontSize: 11 }}>Sem budget</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* ─── Global Filters ─── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: theme.text }}>🔍 Filtros</h3>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {['1h', '24h', '7d', '30d', 'total'].map(p => (
              <Button variant="ghost" key={p} onClick={() => {
                setChartPeriod(p);
                // Atalho: preencher datas automaticamente
                if (p === 'total') {
                  setReqFilters(f => ({ ...f, start_date: '', end_date: '' }));
                } else {
                  const now = new Date();
                  const end = now.toISOString().slice(0, 10);
                  let start: string;
                  if (p === '1h' || p === '24h') {
                    start = end; // mesmo dia
                  } else if (p === '7d') {
                    start = new Date(now.getTime() - 7 * 86400000).toISOString().slice(0, 10);
                  } else {
                    start = new Date(now.getTime() - 30 * 86400000).toISOString().slice(0, 10);
                  }
                  setReqFilters(f => ({ ...f, start_date: start, end_date: end }));
                }
              }} style={pillStyle(chartPeriod === p)}>{p === 'total' ? 'Total' : p}</Button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Select value={reqFilters.model} onChange={(v) => { setReqFilters(f => ({ ...f, model: v })); }}
            options={[{ value: '', label: 'Todos Modelos' }, ...filterOptions.models.map((m: string) => ({ value: m, label: displayModelName(m) }))]}
            style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.borderSolid}`, borderRadius: theme.r2, padding: '4px 8px', fontSize: 12 }} />
          <Select value={reqFilters.action_type} onChange={(v) => { setReqFilters(f => ({ ...f, action_type: v })); }}
            options={[{ value: '', label: 'Todas Ações' }, ...filterOptions.actions.map((a: string) => ({ value: a, label: a }))]}
            style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.borderSolid}`, borderRadius: theme.r2, padding: '4px 8px', fontSize: 12 }} />
          <Select value={reqFilters.status} onChange={(v) => { setReqFilters(f => ({ ...f, status: v })); }}
            options={[{ value: '', label: 'Todos Status' }, { value: 'success', label: 'success' }, { value: 'skipped', label: 'skipped' }, { value: 'error', label: 'error' }]}
            style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.borderSolid}`, borderRadius: theme.r2, padding: '4px 8px', fontSize: 12 }} />
          <Select value={reqFilters.customer_id} onChange={(v) => { setReqFilters(f => ({ ...f, customer_id: v })); }}
            options={[{ value: '', label: 'Todos Usuários' }, ...filterOptions.customers.map((c: any) => ({ value: c.id, label: c.email || c.id.substring(0, 8) }))]}
            style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.borderSolid}`, borderRadius: theme.r2, padding: '4px 8px', fontSize: 12 }} />
          <Input type="text" placeholder="Runner ID" value={reqFilters.runner_id} onChange={(v) => setReqFilters(f => ({ ...f, runner_id: v }))}
            style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.borderSolid}`, borderRadius: theme.r2, padding: '4px 8px', fontSize: 12, width: 160 }} />
          <Select value={reqFilters.agent_id} onChange={(v) => setReqFilters(f => ({ ...f, agent_id: v }))}
            options={[{ value: '', label: 'Todos Agentes' }, ...filterOptions.agents.map((a: any) => ({ value: a.agent_id || a.agent_name, label: a.agent_name }))]}
            style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.borderSolid}`, borderRadius: theme.r2, padding: '4px 8px', fontSize: 12 }} />
          <input type="date" value={reqFilters.start_date} onChange={(e: any) => { setReqFilters(f => ({ ...f, start_date: e.target.value })); setChartPeriod('custom'); }}
            style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.borderSolid}`, borderRadius: theme.r2, padding: '4px 8px', fontSize: 12, width: 130 }} />
          <input type="date" value={reqFilters.end_date} onChange={(e: any) => { setReqFilters(f => ({ ...f, end_date: e.target.value })); setChartPeriod('custom'); }}
            style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.borderSolid}`, borderRadius: theme.r2, padding: '4px 8px', fontSize: 12, width: 130 }} />
          <Button onClick={() => { setReqOffset(0); loadRequests(0); }} variant="primary" size="xs" style={{ padding: '4px 12px', fontSize: 12, borderRadius: theme.r2 }}>
            Filtrar
          </Button>
          {Object.values(reqFilters).some(v => v !== '') && (
            <Button onClick={() => { setReqFilters({ model: '', action_type: '', status: '', customer_id: '', runner_id: '', agent_id: '', start_date: '', end_date: '' }); setChartPeriod('24h'); }} variant="ghost" size="xs" style={{ padding: '4px 8px', fontSize: 11, color: theme.dimmed }}>
              ✕ Limpar
            </Button>
          )}
        </div>
      </Card>

      {/* ─── Summary Cards + Chart ─── */}
      <AnalyticsSummaryChart reqFilters={reqFilters} chartPeriod={chartPeriod} setChartPeriod={setChartPeriod} />

      {/* ─── Request History ─── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 15, color: theme.text }}>Request History</h3>
            <Button onClick={() => { setTurnGrouping(g => !g); setExpandedTurnGroups(new Set()); }} variant={turnGrouping ? 'secondary' : 'ghost'} size="xs"
              style={{ background: turnGrouping ? T.primaryBg : theme.bg, color: turnGrouping ? theme.primary : theme.muted, border: `1px solid ${turnGrouping ? theme.primary : theme.borderSolid}`, borderRadius: theme.r2, padding: '3px 10px', fontSize: 11, fontWeight: turnGrouping ? 700 : 400 }}>
              Agrupar Turnos
            </Button>
          </div>
          <span style={{ fontSize: 11, color: theme.dimmed }}>{reqPagination.total.toLocaleString()} requests</span>
        </div>

        {/* Table */}
        <style>{`
          @media (max-width: 768px) {
            .req-history-table { font-size: 10px !important; }
            .req-history-table th, .req-history-table td { padding: 3px 4px !important; }
            .req-hide-mobile { display: none !important; }
            .adv-report-grid-4 { grid-template-columns: 1fr 1fr !important; }
            .adv-report-grid-2 { grid-template-columns: 1fr !important; }
            .adv-breakdown-table { font-size: 9px !important; }
          }
          @media (max-width: 480px) {
            .adv-report-grid-4 { grid-template-columns: 1fr !important; }
          }
          @keyframes shimmer { 0% { background-position: -200px 0; } 100% { background-position: 200px 0; } }
          .adv-skeleton { background: linear-gradient(90deg, var(--t-white-alpha-3) 25%, var(--t-white-alpha-8) 50%, var(--t-white-alpha-3) 75%); background-size: 400px 100%; animation: shimmer 1.5s infinite; height: 120px; border-radius: 8px; }
        `}</style>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="req-history-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
 {['Mensagem', 'Email', 'Agente', 'Reqs', 'Provedor / Conta', 'Modelo', 'Tempo', 'IN', 'OUT', 'Total', 'Média', '💰 Custo | Fat. | Lucro', 'Status'].map(h => {
 const hideMobile = ['IN', 'OUT', 'Provedor / Conta', 'Agente', 'Média'].includes(h);
 const rightAlign = ['Tempo', 'IN', 'OUT', 'Total', 'Média', '💰 Custo | Fat. | Lucro'].includes(h);
                  return <th key={h} className={hideMobile ? 'req-hide-mobile' : ''} style={{ textAlign: rightAlign ? 'right' : 'left', padding: '6px 6px', color: theme.muted, fontWeight: 600, fontSize: 11 }}>{h}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 ? (
                <tr><td colSpan={13} style={{ padding: 20, textAlign: 'center', color: theme.dimmed }}>Sem requests encontrados</td></tr>
              ) : (() => {
                const rows: any[] = [];

                // ─── Build display list: flat or grouped ───
                // Each item: { r: RequestItem, turnParent?: TurnGroup, turnPos?: number, isLastChild?: boolean }
                // Plus optional turn group parent rows injected as { __turnGroup: TurnGroup }
                type DisplayItem = { type: 'request'; r: RequestItem; indent?: boolean; turnPos?: number; isLastChild?: boolean } | { type: 'turnParent'; tg: TurnGroup };
                const displayList: DisplayItem[] = [];

                if (turnGrouping) {
                  const allItems = computeTurnGroups(requests);
                  // Paginate: show only first `visibleGroups` top-level items
                  const sliced = allItems.slice(0, visibleGroups);
                  sliced.forEach((item) => {
                    if (isTurnGroup(item)) {
                      const tg = item as TurnGroup;
                      displayList.push({ type: 'turnParent', tg });
                      if (expandedTurnGroups.has(tg.id)) {
                        tg.requests.forEach((r, ci) => {
                          displayList.push({ type: 'request', r, indent: true, turnPos: ci + 1, isLastChild: ci === tg.requests.length - 1 });
                        });
                      }
                    } else {
                      displayList.push({ type: 'request', r: item as RequestItem });
                    }
                  });
                } else {
                  requests.forEach(r => displayList.push({ type: 'request', r }));
                }

                displayList.forEach((item) => {
                  if (item.type === 'turnParent') {
                    const tg = item.tg;
                    const isGroupExpanded = expandedTurnGroups.has(tg.id);
                    const tgLucro = tg.totalPriceCharged - tg.totalCostProvider;
                    rows.push(
                      <tr key={`tg-${tg.id}`} onClick={() => setExpandedTurnGroups(prev => { const n = new Set(prev); if (n.has(tg.id)) n.delete(tg.id); else n.add(tg.id); return n; })}
                        style={{ borderBottom: isGroupExpanded ? 'none' : `1px solid ${theme.border}`, cursor: 'pointer', background: T.primaryBg }}
                        onMouseEnter={(e: any) => e.currentTarget.style.background = T.primaryBg}
                        onMouseLeave={(e: any) => e.currentTarget.style.background = T.primaryBg}>
                        <td style={{ padding: '5px 6px', color: tg.userMessage ? theme.text : theme.dimmed, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>
                          <span style={{ cursor: 'pointer', marginRight: 4, fontSize: 10 }}>{isGroupExpanded ? '▼' : '▶'}</span>
                          {tg.userMessage ? (tg.userMessage.length > 40 ? tg.userMessage.substring(0, 40) + '...' : tg.userMessage) : '—'}
                        </td>
                        <td style={{ padding: '5px 6px', color: theme.text, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 10 }}>{tg.customer.email?.split('@')[0] || tg.customer.id?.substring(0, 8)}</td>
                        <td className="req-hide-mobile" style={{ padding: '5px 6px', fontSize: 10 }}>
                          {tg.agentName ? <span style={{ background: T.warningBg, color: T.warning, padding: '1px 6px', borderRadius: 3, fontSize: 10 }}>{tg.agentName}</span> : <span style={{ color: theme.dimmed }}>—</span>}
                        </td>
                        <td style={{ padding: '5px 6px' }}>
                          <span style={{ background: T.primaryBg, color: theme.primary, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
                            {tg.requests.length} reqs
                          </span>
                        </td>
                        <td className="req-hide-mobile" style={{ padding: '5px 6px', color: theme.dimmed, fontSize: 9, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tg.provider ? (tg.keyLabel ? `${tg.provider} / ${tg.keyLabel}` : tg.provider) : '—'}
                        </td>
                        <td style={{ padding: '5px 6px', color: theme.cyan, fontSize: 10 }}>{fmtModel(tg.model).display}</td>
                        <td style={{ padding: '5px 6px', color: theme.text, textAlign: 'right', fontSize: 10 }}>{humanizeTime(tg.totalLatencyMs)}</td>
                        <td className="req-hide-mobile" style={{ padding: '5px 6px', color: theme.dimmed, textAlign: 'right', fontSize: 10 }}>{fmtTokens(tg.totalInputTokens)}</td>
                        <td className="req-hide-mobile" style={{ padding: '5px 6px', color: theme.text, textAlign: 'right', fontSize: 10 }}>{fmtTokens(tg.totalOutputTokens)}</td>
                        <td style={{ padding: '5px 6px', color: theme.text, textAlign: 'right', fontSize: 10, fontWeight: 700 }}>{fmtTokens(tg.totalTokens)}</td>
                        <td className="req-hide-mobile" style={{ padding: '5px 6px', color: theme.dimmed, textAlign: 'right', fontSize: 9 }}>{fmtTokens(Math.round(tg.totalTokens / tg.requests.length))}/r</td>
                        <td style={{ padding: '5px 6px', textAlign: 'right', fontSize: 10, whiteSpace: 'nowrap' }}>
                          <span style={{ color: theme.dimmed }}>{fmtCost(tg.totalCostProvider)}</span>
                          <span style={{ color: theme.muted }}> | </span>
                          <span style={{ color: theme.warning, fontWeight: 700 }}>{fmtCost(tg.totalPriceCharged)}</span>
                          <span style={{ color: theme.muted }}> | </span>
                          <span style={{ color: tgLucro >= 0 ? theme.success : theme.error }}>{tgLucro >= 0 ? '+' : ''}{fmtCost(tgLucro)}</span>
                        </td>
                        <td style={{ padding: '5px 6px' }}>
                          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 600,
                            background: tg.lastStatus === 'success' ? T.successBg : T.dangerBg,
                            color: tg.lastStatus === 'success' ? theme.success : theme.error }}>
                            {tg.lastStatus}
                          </span>
                        </td>
                      </tr>
                    );
                    return;
                  }

                  // ─── Request row (both flat and grouped children) ───
                  const r = item.r;
                  const indent = item.indent;
                  const turnPos = item.turnPos;
                  const isLastChild = item.isLastChild;

                  {/* Original request row rendering — now shared between flat and grouped */}
                  const idx = 0; // unused but kept for compat
                  const m = fmtModel(r.model);
                  const attCount = r.total_attempts || r.attempts?.length || 1;
                  const hasAttempts = attCount > 1;
                  const isExpanded = expandedReqIds.has(r.id);
                  const bgDefault = indent ? (isLastChild ? T.successBg : T.primaryBg) : r.status === 'error' ? T.dangerBg : 'transparent';
                  const bgHover = indent ? T.primaryBg : r.status === 'error' ? T.dangerBg : theme.cardHover;
                  const rCusto = r.cost_provider > 0 ? r.cost_provider : (r.cost > 0 ? r.cost / 1.2 : 0);
                  const rFaturado = r.price_charged > 0 ? r.price_charged : r.cost || 0;
                  const rLucro = rFaturado - rCusto;
                  const rUserMsg = (r as any).user_message;
                  const rAgName = (r as any).agent_name || (r as any).detected_agent_name;
                  rows.push(
                    <tr key={r.id} onClick={() => toggleExpand(r.id)} style={{ borderBottom: isExpanded ? 'none' : `1px solid ${theme.border}`, cursor: 'pointer', background: bgDefault }}
                      onMouseEnter={(e: any) => e.currentTarget.style.background = bgHover}
                      onMouseLeave={(e: any) => e.currentTarget.style.background = bgDefault}>
                      <td style={{ padding: '5px 6px', color: rUserMsg ? theme.text : theme.dimmed, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10, paddingLeft: indent ? 28 : 5 }}>
                        {indent && turnPos ? <span style={{ background: T.primaryBg, color: theme.primary, padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700, marginRight: 4 }}>#{turnPos}</span> : null}
                        {!indent && <span onClick={(e) => { e.stopPropagation(); toggleExpand(r.id); }} style={{ cursor: 'pointer', marginRight: 4, fontSize: 10 }}>{isExpanded ? '▼' : '▶'}</span>}
                        {rUserMsg ? (rUserMsg.length > 40 ? rUserMsg.substring(0, 40) + '...' : rUserMsg) : '—'}
                        {indent && isLastChild ? <span style={{ marginLeft: 4, fontSize: 9, color: theme.success, fontWeight: 600 }}> ← final</span> : null}
                      </td>
                      <td style={{ padding: '5px 6px', color: theme.text, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 10 }}>{indent ? '' : (r.customer.email?.split('@')[0] || r.customer.id?.substring(0, 8))}</td>
                      <td className="req-hide-mobile" style={{ padding: '5px 6px', fontSize: 10 }}>
                        {rAgName ? <span style={{ background: T.warningBg, color: T.warning, padding: '1px 6px', borderRadius: 3, fontSize: 10 }}>{rAgName}</span> : <span style={{ color: theme.dimmed }}>—</span>}
                      </td>
                      <td style={{ padding: '5px 6px', fontSize: 10 }}>
                        {indent && turnPos ? <span style={{ color: theme.dimmed, fontSize: 9 }}>#{turnPos}</span> : ''}
                      </td>
                      <td className="req-hide-mobile" style={{ padding: '5px 6px', color: theme.dimmed, fontSize: 9, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {(r as any).key_label ? `${r.provider || ''} / ${(r as any).key_label}` : (r.provider || '—')}
                      </td>
                      <td style={{ padding: '5px 6px', color: theme.cyan, fontSize: 10 }} title={`${r.request_model || ''} → ${r.model}`}>
                        {m.isMultiple ? <span style={{ background: T.primaryBg, padding: '1px 6px', borderRadius: 3 }}>{m.display}</span> : m.display}
                      </td>
                      <td style={{ padding: '5px 6px', color: theme.text, textAlign: 'right', fontSize: 10 }}>{humanizeTime(r.total_latency_ms || r.latency_ms)}</td>
                      <td className="req-hide-mobile" style={{ padding: '5px 6px', color: theme.dimmed, textAlign: 'right', fontSize: 10 }}>{fmtTokens(r.tokens.input)}</td>
                      <td className="req-hide-mobile" style={{ padding: '5px 6px', color: theme.text, textAlign: 'right', fontSize: 10 }}>{fmtTokens(r.tokens.output)}</td>
                      <td style={{ padding: '5px 6px', color: theme.text, textAlign: 'right', fontSize: 10, fontWeight: 700 }}>{fmtTokens(r.total_tokens || (r.tokens.input + r.tokens.output))}</td>
                      <td className="req-hide-mobile" style={{ padding: '5px 6px', color: theme.dimmed, textAlign: 'right', fontSize: 9 }}>{fmtTokens(r.total_tokens || (r.tokens.input + r.tokens.output))}/r</td>
                      <td style={{ padding: '5px 6px', textAlign: 'right', fontSize: 10, whiteSpace: 'nowrap' }}>
                        <span style={{ color: theme.dimmed }}>{fmtCost(rCusto)}</span>
                        <span style={{ color: theme.muted }}> | </span>
                        <span style={{ color: theme.warning, fontWeight: 700 }}>{fmtCost(rFaturado)}</span>
                        <span style={{ color: theme.muted }}> | </span>
                        <span style={{ color: rLucro >= 0 ? theme.success : theme.error }}>{rLucro >= 0 ? '+' : ''}{fmtCost(rLucro)}</span>
                      </td>
                      <td style={{ padding: '5px 6px' }}>
                        <span style={{
                          fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 600,
                          background: r.status === 'success' ? T.successBg : r.status === 'skipped' ? T.warningBg : T.dangerBg,
                          color: r.status === 'success' ? theme.success : r.status === 'skipped' ? theme.warning : theme.error,
                        }}>
                          {r.status}
                          {r.failure_type ? ` (${r.failure_type})` : ''}
 {r.finish_reason === 'tool_calls' ? ' ' : ''}
                        </span>
                      </td>
                    </tr>
                  );
                  {/* MEGA: Expanded inline detail sub-row with lazy-loaded full data */}
                  if (isExpanded) {
                    const detail = requestDetails[r.id];
                    const attData = expandedAttempts[r.id] || r.attempts || [];
 const errorTypeEmoji: Record<string, string> = { credit: '', auth: '', timeout: '', rate_limit: '', server: '', unknown: '' };
                    const _custo = r.cost_provider > 0 ? r.cost_provider : (detail?.cost_provider > 0 ? detail.cost_provider : (r.cost > 0 ? r.cost / 1.2 : 0));
                    const _faturado = r.price_charged > 0 ? r.price_charged : (detail?.price_charged > 0 ? detail.price_charged : r.cost || 0);
                    const marginVal = _faturado - _custo;
                    const marginPct = _custo > 0 ? ((marginVal / _custo) * 100).toFixed(0) : '—';
                    rows.push(
                      <tr key={`${r.id}-detail`} style={{ borderBottom: `1px solid ${theme.border}` }}>
                        <td colSpan={13} style={{ padding: '12px 16px', background: T.primaryBg }}>
                          {!detail ? (
 <div style={{ padding: 12 }}><div className="adv-skeleton" style={{ marginBottom: 8 }}></div><div style={{ marginTop: 8 }}><ListSkeleton rows={2} /></div></div>
                          ) : (detail as any)._error ? (
                            <div style={{ padding: 16, textAlign: 'center', color: T.danger }}>
 <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Erro ao carregar detalhes</div>
                              <div style={{ fontSize: 11, color: theme.dimmed }}>{(detail as any).error_message}</div>
 <Button onClick={() => { setRequestDetails(prev => { const n = { ...prev }; delete n[r.id]; return n; }); loadRequestDetail(r.id); }} variant="ghost" size="xs" style={{ marginTop: 8, padding: '4px 12px', fontSize: 11, background: theme.border, color: theme.text, borderRadius: 4 }}>Tentar novamente</Button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                              {/* 1. Identificação */}
                              <div>
 <div style={{ fontSize: 12, fontWeight: 600, color: theme.text, marginBottom: 6 }}>Identificação</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px 16px', fontSize: 11 }}>
                                  <div><span style={{ color: theme.dimmed }}>ID:</span> <span style={{ color: theme.text, fontSize: 9 }}>{r.id}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Provider:</span> <span style={{ color: theme.cyan }}>{r.provider}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Pedido:</span> <span style={{ color: theme.text }}>{r.request_model || '—'}</span> → <span style={{ color: theme.cyan }}>{r.model}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Ação:</span> <span style={{ color: theme.text }}>{r.action}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Token:</span> <span style={{ color: theme.primary }}>{detail.token_name || r.token || '—'}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Conta Provedor:</span> <span style={{ color: theme.cyan }}>{detail.key_label || (r as any).key_label || '—'}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Runner:</span> <span style={{ color: theme.indigo }}>{r.runner_id || '—'}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Agent ID (Runtime):</span> <span style={{ color: T.purple, fontWeight: 600 }}>{(detail as any).detected_agent_id || r.agent_id || '—'}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Agent Name (SOUL.md):</span> <span style={{ color: T.warning }}>{(detail as any).detected_agent_name || '—'}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Host:</span> <span style={{ color: '#67e8f9' }}>{(detail as any).detected_host || '—'}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Channel:</span> <span style={{ color: T.warning }}>{(detail as any).detected_channel || '—'}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>IP:</span> <span style={{ color: theme.text }}>{r.ip_address || detail.ip_address || '—'}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Finish:</span> <span style={{ color: theme.text }}>{r.finish_reason || detail.finish_reason || '—'}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Streaming:</span> <span style={{ color: theme.text }}>{detail.request_stream != null ? (detail.request_stream ? 'Sim' : 'Não') : '—'}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Temperature:</span> <span style={{ color: theme.text }}>{detail.request_temperature ?? '—'}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Max Tokens:</span> <span style={{ color: theme.text }}>{detail.request_max_tokens ?? '—'}</span></div>
                                </div>
                              </div>

                              {/* 2. Financeiro */}
                              <div>
 <div style={{ fontSize: 12, fontWeight: 600, color: theme.text, marginBottom: 6 }}>Financeiro</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '4px 16px', fontSize: 11 }}>
                                  <div><span style={{ color: theme.dimmed }}>Custo:</span> <span style={{ color: theme.warning }}>{fmtCost(_custo)}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Faturado:</span> <span style={{ color: theme.warning }}>{fmtCost(_faturado)}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Lucro:</span> <span style={{ color: marginVal >= 0 ? theme.success : theme.error }}>{marginVal >= 0 ? '+' : ''}${marginVal.toFixed(4)} ({marginPct}%)</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Saldo:</span> <span style={{ color: theme.text }}>{r.balance_before != null ? `$${Number(r.balance_before).toFixed(2)}` : '—'} → {r.balance_after != null ? `$${Number(r.balance_after).toFixed(2)}` : '—'}</span></div>
                                </div>
                              </div>

                              {/* 3. Performance */}
                              <div>
 <div style={{ fontSize: 12, fontWeight: 600, color: theme.text, marginBottom: 6 }}>Performance</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '4px 16px', fontSize: 11 }}>
                                  <div><span style={{ color: theme.dimmed }}>Latência total:</span> <span style={{ color: theme.text }}>{humanizeTime(r.total_latency_ms || r.latency_ms)}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Latência LLM:</span> <span style={{ color: theme.text }}>{humanizeTime(r.latency_ms)}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Tentativas:</span> <span style={{ color: theme.text }}>{attCount}</span></div>
                                  <div><span style={{ color: theme.dimmed }}>Tokens:</span> <span style={{ color: theme.text }}>{fmtTokens(r.tokens.input)} in / {fmtTokens(r.tokens.output)} out = {fmtTotalTokens(r.total_tokens || 0)}</span></div>
                                </div>
                              </div>

                              {/* 4. Mensagem Entrada (user_message) */}
                              <div style={{ padding: '8px 12px', background: T.bgCard, borderRadius: 6, border: `1px solid ${theme.border}` }}>
 <div style={{ fontSize: 11, color: theme.dimmed, marginBottom: 4 }}>Mensagem do Usuário:</div>
                                <div style={{ fontSize: 12, color: theme.text, whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto' }}>
                                  {(r as any).user_message === '[reconciled from wallet_transaction]'
 ? 'Registro reconciliado — mensagem original não capturada'
 : (r as any).user_message || 'Sem mensagem registrada'}
                                </div>
                              </div>

                              {/* 5. Resposta LLM */}
                              {/* ═══ ADVANCED DETAILED REPORT ═══ */}
                              {detail && (() => {
                                const pb = detail.pricing_breakdown;
                                const overhead = (r.total_latency_ms || r.latency_ms || 0) - (r.latency_ms || 0);
                                const tokPerSec = r.latency_ms > 0 ? ((r.tokens?.output || detail.output_tokens || 0) / (r.latency_ms / 1000)).toFixed(1) : '—';
                                const totalLat = r.total_latency_ms || r.latency_ms || 0;
                                const llmLat = r.latency_ms || 0;
                                const llmPct = totalLat > 0 ? (llmLat / totalLat * 100) : 100;
 const chargeStatusBadge = (s: string) => s === 'charged' ? 'Cobrado' : s === 'failed' ? 'Falhou' : s === 'negative' ? 'Negativo' : s || '—';
                                const chargeColor = (s: string) => s === 'charged' ? theme.success : s === 'failed' ? theme.error : theme.warning;
                                const copyToClip = (text: string) => { try { navigator.clipboard.writeText(text); } catch {} };
                                const cacheEst = detail.cache_read_tokens && pb ? ((detail.cache_read_tokens / 1_000_000) * (pb.input_cost_per_1m || 0) * 0.9).toFixed(4) : null;
                                const benchmarks: Record<string, number> = { 'opus': 50, 'sonnet': 120, 'haiku': 200, 'gpt-4o': 100, 'gpt-4o-mini': 150 };
                                const modelKey = Object.keys(benchmarks).find(k => (detail.model || r.model || '').includes(k));
                                const bench = modelKey ? benchmarks[modelKey] : null;
                                const tokPerSecNum = parseFloat(String(tokPerSec));
                                const isAdvOpen = advancedOpenIds.has(r.id);
                                return (
                                  <div style={{ marginTop: 8, borderTop: `1px solid ${theme.border}`, paddingTop: 10 }}>
                                    <Button onClick={(e: any) => { e.stopPropagation(); toggleAdvanced(r.id); }} variant="secondary" size="xs" style={{ width: '100%', padding: '8px 14px', fontSize: 12, fontWeight: 700, borderRadius: 6, textAlign: 'left', marginBottom: isAdvOpen ? 10 : 0 }}>
                                      Relatório Detalhado {isAdvOpen ? '▼' : ''}
                                    </Button>

                                    {!isAdvOpen ? null : (<div>

                                    {/* Toggle Compacto / Completo */}
                                    {(() => {
                                      const mode = viewMode[r.id] || 'compact';
                                      const btnBase = { fontSize: 11, padding: '4px 12px', borderRadius: 4, cursor: 'pointer' as const, fontWeight: 600, marginRight: 6 };
                                      const active = { background: T.primaryBg, color: theme.primary, border: `1px solid ${T.primaryBg}` };
                                      const inactive = { background: T.bgCard, color: theme.dimmed, border: '1px solid transparent' };
                                      return (
                                        <div style={{ marginBottom: 10, display: 'flex', gap: 6 }}>
 <Button onClick={(e: any) => { e.stopPropagation(); setViewMode(prev => ({ ...prev, [r.id]: 'compact' })); }} variant={mode === 'compact' ? 'primary' : 'ghost'} size="xs" style={{ ...btnBase, ...(mode === 'compact' ? active : inactive) }}>Compacto</Button>
 <Button onClick={(e: any) => { e.stopPropagation(); setViewMode(prev => ({ ...prev, [r.id]: 'full' })); }} variant={mode === 'full' ? 'primary' : 'ghost'} size="xs" style={{ ...btnBase, ...(mode === 'full' ? active : inactive) }}>Completo</Button>
                                        </div>
                                      );
                                    })()}

                                    {/* ═══ TIMELINE 11 ETAPAS CRONOLÓGICAS ═══ */}
                                    <div style={{ borderLeft: '1px solid #1e293b', marginLeft: 12, paddingLeft: 20 }}>

                                      {/* ETAPA 1: Request Recebido */}
                                      <div style={{ position: 'relative', marginBottom: 10 }}>
                                        <div style={{ position: 'absolute', left: -31, top: 0, width: 24, height: 24, borderRadius: '50%', background: T.border, border: `1px solid ${T.border}`, color: T.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>1</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>1. Request Recebido</div>
                                        <div className="adv-report-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px 12px', fontSize: 11, marginBottom: 8 }}>
                                          <div><span style={{ color: theme.dimmed }}>Timestamp:</span> <span style={{ color: theme.text }}>{new Date(detail.created_at + (detail.created_at?.endsWith?.('Z') ? '' : 'Z')).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</span></div>
                                          <div><span style={{ color: theme.dimmed }}>IP:</span> <span style={{ color: theme.text }}>{detail.ip_address || r.ip_address || '—'}</span></div>
                                          <div><span style={{ color: theme.dimmed }}>Modelo Pedido:</span> <span style={{ color: theme.text }}>{detail.request_model || r.request_model || '—'}</span></div>
                                          <div><span style={{ color: T.textMuted }}>Temperature:</span> <span style={{ color: T.text }}>{detail.request_temperature ?? r.request_temperature ?? '—'}</span></div>
                                          <div><span style={{ color: T.textMuted }}>Max Tokens:</span> <span style={{ color: T.text }}>{detail.request_max_tokens ?? r.request_max_tokens ?? '—'}</span></div>
                                          <div><span style={{ color: theme.dimmed }}>Streaming:</span> <span style={{ color: theme.text }}>{detail.request_stream != null ? (detail.request_stream ? 'Sim' : 'Não') : '—'}</span></div>
                                          <div><span style={{ color: theme.dimmed }}>Action:</span> <span style={{ padding: '1px 6px', borderRadius: 3, background: T.primaryBg, color: theme.primary, fontSize: 10 }}>{detail.action_type || r.action || '—'}</span></div>
                                          <div><span style={{ color: theme.dimmed }}>Total Mensagens:</span> <span style={{ color: theme.text }}>{Array.isArray(detail.request_messages) ? detail.request_messages.length : '—'}</span></div>
                                        </div>
                                        {/* System Prompt */}
                                        {detail.request_system_prompt && (() => {
                                          const mode = viewMode[r.id] || 'compact';
                                          const spLines = detail.request_system_prompt.split('\n');
                                          const spSize = new Blob([detail.request_system_prompt]).size;
                                          const spSizeLabel = spSize > 1024 ? `${(spSize / 1024).toFixed(0)}KB` : `${spSize}B`;
                                          const truncatedSp = mode === 'compact' && spLines.length > 3 ? spLines.slice(0, 3).join('\n') + `\n... (${spSizeLabel})` : detail.request_system_prompt;
                                          return (
                                          <div style={{ position: 'relative', marginBottom: 8 }}>
                                            <div style={{ fontSize: 11, color: theme.dimmed, marginBottom: 4 }}>System Prompt:</div>
 <Button onClick={(e: any) => { e.stopPropagation(); copyWithFeedback(detail.request_system_prompt, `sp-${r.id}`); }} variant="ghost" size="xs" style={{ position: 'absolute', top: 0, right: 0, fontSize: 10, padding: '2px 6px', borderRadius: 4, background: T.primaryBg, color: theme.primary, border: 'none' }}>{copiedId === `sp-${r.id}` ? '' : ''}</Button>
                                            <pre style={{ fontSize: 10, color: T.text, ...(mode === 'full' ? {} : { maxHeight: 80 }), overflow: 'auto', background: T.bgOverlay, padding: 10, borderRadius: 6, whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'monospace' }}>{truncatedSp}</pre>
                                            {mode === 'compact' && spLines.length > 3 && <Button onClick={(e: any) => { e.stopPropagation(); setViewMode(prev => ({ ...prev, [r.id]: 'full' })); }} variant="ghost" size="xs" style={{ fontSize: 10, padding: '2px 8px', marginTop: 4, borderRadius: 4, background: T.primaryBg, color: theme.primary, border: 'none' }}>Ver completo</Button>}
                                          </div>
                                          );
                                        })()}
                                        {/* Messages */}
                                        {detail.request_messages && Array.isArray(detail.request_messages) && detail.request_messages.length > 0 && (() => {
                                          const mode = viewMode[r.id] || 'compact';
                                          if (mode === 'compact') {
                                            const roleCounts: Record<string, number> = {};
                                            detail.request_messages.forEach((msg: any) => { const role = msg.role || 'unknown'; roleCounts[role] = (roleCounts[role] || 0) + 1; });
                                            const summary = Object.entries(roleCounts).map(([role, count]) => `${count} ${role}`).join(', ');
                                            return (
                                              <div style={{ marginBottom: 8 }}>
                                                <div style={{ fontSize: 11, color: theme.dimmed }}>Messages ({detail.request_messages.length}): <span style={{ color: theme.text }}>{summary}</span></div>
                                              </div>
                                            );
                                          }
                                          return (
                                          <div style={{ marginBottom: 8 }}>
                                            <div style={{ fontSize: 11, color: theme.dimmed, marginBottom: 6 }}>Messages ({detail.request_messages.length}):</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                              {detail.request_messages.map((msg: any, mi: number) => {
                                                const role = msg.role || 'unknown';
                                                const roleColors: Record<string, string> = { user: T.primaryBg, assistant: T.successBg, system: T.bgHover, tool: T.purpleBg };
                                                const roleBorders: Record<string, string> = { user: T.primaryBg, assistant: T.successBg, system: T.bgHover, tool: T.purpleBg };
 const roleIcons: Record<string, string> = { user: '', assistant: '', system: '', tool: '' };
                                                return (
                                                  <div key={mi} style={{ padding: '6px 10px', background: roleColors[role] || T.bgCard, borderRadius: 6, borderLeft: `3px solid ${roleBorders[role] || theme.border}` }}>
 <div style={{ fontSize: 10, fontWeight: 600, color: theme.dimmed, marginBottom: 2 }}>{roleIcons[role] || ''} {role}</div>
                                                    <div style={{ fontSize: 11, color: theme.text, whiteSpace: 'pre-wrap' }}>{typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2)}</div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                            {JSON.stringify(detail.request_messages).length > 95000 && (
 <div style={{ marginTop: 6, padding: '4px 10px', background: T.warningBg, borderRadius: 4, color: T.warning, fontSize: 10, border: `1px solid ${T.warningBg}` }}>Mensagens truncadas no armazenamento (limite 100KB)</div>
                                            )}
                                          </div>
                                          );
                                        })()}
                                        {/* Tools */}
                                        {detail.request_tools && Array.isArray(detail.request_tools) && detail.request_tools.length > 0 && (() => {
                                          const mode = viewMode[r.id] || 'compact';
                                          if (mode === 'compact') {
                                            return (
                                              <div style={{ marginBottom: 8 }}>
                                                <div style={{ fontSize: 11, color: theme.dimmed, marginBottom: 4 }}>Tools ({detail.request_tools.length}):</div>
                                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                  {detail.request_tools.map((tool: any, ti: number) => {
                                                    const fn = tool.function || tool;
 return <span key={ti} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: T.purpleBg, color: T.purple }}> {fn.name || `tool_${ti}`}</span>;
                                                  })}
                                                </div>
                                              </div>
                                            );
                                          }
                                          return (
                                          <div style={{ marginBottom: 8 }}>
                                            <div style={{ fontSize: 11, color: theme.dimmed, marginBottom: 4 }}>Tools ({detail.request_tools.length}):</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                              {detail.request_tools.map((tool: any, ti: number) => {
                                                const fn = tool.function || tool;
                                                const name = fn.name || `tool_${ti}`;
                                                const desc = fn.description || '';
                                                const params = fn.parameters ? JSON.stringify(fn.parameters, null, 2) : null;
                                                return (
                                                  <div key={ti} style={{ padding: '6px 10px', background: T.purpleBg, borderRadius: 6, borderLeft: `3px solid ${T.purpleBg}` }}>
 <div style={{ fontSize: 11, fontWeight: 600, color: T.purple, marginBottom: 2 }}> {name}</div>
                                                    {desc && <div style={{ fontSize: 10, color: theme.dimmed, marginBottom: params ? 4 : 0 }}>{desc}</div>}
                                                    {params && <pre style={{ fontSize: 9, color: T.textSecondary, background: T.shadowSm, padding: 6, borderRadius: 4, margin: 0, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{params}</pre>}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                            <div style={{ fontSize: 11, color: theme.dimmed, marginTop: 4 }}>Tool Choice: <span style={{ color: theme.text }}>{detail.request_tool_choice || '—'}</span></div>
                                          </div>
                                          );
                                        })()}
                                      </div>

                                      {/* ETAPA 2: Token Validado */}
                                      {(() => {
                                        const isTokenError = r.status === 'rejected' && (detail.failure_type || '').includes('token');
                                        return (
                                          <div style={{ position: 'relative', marginBottom: 10 }}>
                                            <div style={{ position: 'absolute', left: -31, top: 0, width: 24, height: 24, borderRadius: '50%', background: T.border, border: isTokenError ? `1px solid ${T.danger}` : `1px solid ${T.border}`, color: isTokenError ? T.danger : T.textSecondary, display: `flex`, alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>2</div>
                                            <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>2. Token Validado</div>
                                            <div className="adv-report-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 11, marginBottom: 6 }}>
                                              <div><span style={{ color: theme.dimmed }}>Token:</span> <span style={{ color: theme.primary }}>{detail.token_name || '—'}</span></div>
 <div><span style={{ color: theme.dimmed }}>Token ID:</span> <span style={{ color: theme.text, cursor: 'pointer', textDecoration: 'underline dotted' }} onClick={(e) => { e.stopPropagation(); copyWithFeedback(detail.token_id || '', `tl-tid-${r.id}`); }}>{copiedId === `tl-tid-${r.id}` ? 'Copiado!' : (detail.token_id || '—')}</span></div>
                                              <div><span style={{ color: theme.dimmed }}>Customer:</span> <span style={{ color: theme.text }}>{detail.customer_email || '—'}</span></div>
 <div><span style={{ color: theme.dimmed }}>Customer ID:</span> <span style={{ color: theme.text, cursor: 'pointer', textDecoration: 'underline dotted' }} onClick={(e) => { e.stopPropagation(); copyWithFeedback(detail.customer_id || '', `tl-cid-${r.id}`); }}>{copiedId === `tl-cid-${r.id}` ? 'Copiado!' : (detail.customer_id || '—')}</span></div>
                                            </div>
                                            {isTokenError
 ? <div style={{ padding: '4px 10px', background: T.dangerBg, borderRadius: 4, color: theme.error, fontSize: 11, fontWeight: 600 }}>Token Rejeitado</div>
 : <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: T.successBg, color: T.success }}>Válido</span>
                                            }
                                          </div>
                                        );
                                      })()}

                                      {/* ETAPA 3: Verificação de Saldo */}
                                      {(() => {
                                        const isBalanceError = detail.failure_type === 'insufficient_balance';
                                        return (
                                          <div style={{ position: 'relative', marginBottom: 16 }}>
                                            <div style={{ position: 'absolute', left: -31, top: 0, width: 24, height: 24, borderRadius: '50%', background: T.border, border: isBalanceError ? `1px solid ${T.danger}` : `1px solid ${T.border}`, color: isBalanceError ? T.danger : T.textSecondary, display: `flex`, alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>3</div>
                                            <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>3. Verificação de Saldo</div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 11, marginBottom: 6 }}>
                                              <div><span style={{ color: theme.dimmed }}>Saldo no Momento:</span> <span style={{ color: theme.text }}>{detail.balance_before != null ? `$${Number(detail.balance_before).toFixed(2)}` : (r.balance_before != null ? `$${Number(r.balance_before).toFixed(2)}` : '—')}</span></div>
                                              <div><span style={{ color: theme.dimmed }}>Resultado:</span> {isBalanceError
 ? <span style={{ padding: '1px 6px', borderRadius: 3, background: T.dangerBg, color: theme.error, fontWeight: 600 }}>Insuficiente</span>
 : <span style={{ padding: '1px 6px', borderRadius: 3, background: T.successBg, color: T.success, fontWeight: 600 }}>Suficiente</span>
                                              }</div>
                                            </div>
 {isBalanceError && <div style={{ padding: '4px 10px', background: T.dangerBg, borderRadius: 4, color: theme.error, fontSize: 11, fontWeight: 600 }}>Request bloqueado por saldo insuficiente</div>}
                                            {detail.balance_before == null && r.balance_before == null && !isBalanceError && <div style={{ fontSize: 10, color: T.textMuted, fontStyle: 'italic' }}>Dados de saldo não disponíveis (registro antigo)</div>}
                                          </div>
                                        );
                                      })()}

                                      {/* ETAPA 4: Preparação */}
                                      <div style={{ position: 'relative', marginBottom: 10 }}>
                                        <div style={{ position: 'absolute', left: -31, top: 0, width: 24, height: 24, borderRadius: '50%', background: T.border, border: `1px solid ${T.border}`, color: T.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>4</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>4. Preparação</div>
                                        {Array.isArray(detail.observations) && detail.observations.includes('truncated')
 ? <div style={{ fontSize: 11 }}><span style={{ padding: '2px 8px', borderRadius: 4, background: T.warningBg, color: T.warning, fontWeight: 600 }}>Sim — Truncado</span> <span style={{ color: theme.dimmed, marginLeft: 6 }}>Conversa truncada para caber no limite</span></div>
 : <div style={{ fontSize: 11 }}><span style={{ padding: '2px 8px', borderRadius: 4, background: T.successBg, color: T.success, fontWeight: 600 }}>Não</span> <span style={{ color: theme.dimmed, marginLeft: 6 }}>Conversa completa enviada (sem truncamento)</span></div>
                                        }
                                      </div>

                                      {/* ETAPA 5: Classificação */}
                                      <div style={{ position: 'relative', marginBottom: 16 }}>
                                        <div style={{ position: 'absolute', left: -31, top: 0, width: 24, height: 24, borderRadius: '50%', background: T.border, border: `1px solid ${T.border}`, color: T.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>5</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>5. Classificação</div>
                                        {(() => {
 const actionIcons: Record<string, string> = { chat: '', code: '', summarize: '', translate: '', analyze: '', creative: '' };
                                          const at = detail.action_type || r.action || '—';
 const icon = actionIcons[at] || '';
                                          return <span style={{ fontSize: 14, padding: '4px 16px', borderRadius: 8, background: T.dangerBg, color: T.purple, fontWeight: 700 }}>{icon} {at}</span>;
                                        })()}
                                      </div>

                                      {/* ETAPA 6: Roteamento */}
                                      <div style={{ position: 'relative', marginBottom: 10 }}>
                                        <div style={{ position: 'absolute', left: -31, top: 0, width: 24, height: 24, borderRadius: '50%', background: T.border, border: `1px solid ${T.border}`, color: T.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>6</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>6. Roteamento</div>
                                        <div className="adv-report-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px 12px', fontSize: 11, marginBottom: 8 }}>
                                          <div><span style={{ color: theme.dimmed }}>Provider:</span> <span style={{ padding: '1px 6px', borderRadius: 3, background: T.primaryBg, color: theme.cyan }}>{detail.provider || r.provider}</span></div>
                                          <div><span style={{ color: theme.dimmed }}>Chave:</span> <span style={{ color: theme.text }}>{detail.key_label || detail.successful_key_label || '—'}</span></div>
 <div><span style={{ color: theme.dimmed }}>Key ID:</span> <span style={{ color: theme.text, cursor: 'pointer', textDecoration: 'underline dotted' }} onClick={(e) => { e.stopPropagation(); copyWithFeedback(detail.key_id || '', `tl-kid-${r.id}`); }}>{copiedId === `tl-kid-${r.id}` ? 'Copiado!' : (detail.key_id || '—')}</span></div>
 <div><span style={{ color: theme.dimmed }}>Via Bridge:</span> <span style={{ color: theme.text }}>{Array.isArray(detail.observations) && detail.observations.includes('via_bridge') ? 'Sim' : 'Não'}</span></div>
 <div><span style={{ color: theme.dimmed }}>Fallback:</span> <span style={{ color: theme.text }}>{Array.isArray(detail.observations) && detail.observations.includes('fallback_used') ? 'Sim' : 'Não'}</span></div>
                                          <div><span style={{ color: theme.dimmed }}>Tentativas:</span> <span style={{ color: theme.text }}>{detail.total_attempts || 1}</span></div>
                                        </div>
                                        {/* Attempts table */}
                                        {detail.attempts && detail.attempts.length > 0 && ((viewMode[r.id] || 'compact') === 'full' || detail.attempts.length > 1) && (
                                          <div style={{ marginBottom: 8 }}>
                                            <div style={{ fontSize: 11, fontWeight: 600, color: theme.text, marginBottom: 4 }}>Tentativas ({detail.attempts.length}):</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                              {detail.attempts.map((a: any, ai: number) => (
                                                <div key={ai} style={{ padding: '5px 10px', borderRadius: 6, fontSize: 10, background: a.status === 'success' ? T.successBg : T.dangerBg, borderLeft: `3px solid ${a.status === 'success' ? T.success : T.danger}` }}>
                                                  <span style={{ fontWeight: 600 }}>#{a.attempt_order || ai + 1}</span>
                                                  {' '}<span style={{ color: theme.cyan }}>{a.provider}/{a.model}</span>
                                                  {' '}<span style={{ color: theme.dimmed }}>Key:</span> <span style={{ color: theme.primary }}>{a.key_label || a.key_id || '—'}</span>
 {' '}<span style={{ color: a.status === 'success' ? theme.success : theme.error }}>{a.status === 'success' ? '' : ''}</span>
 {a.error_type && <span style={{ padding: '1px 5px', borderRadius: 3, background: T.dangerBg, color: theme.error, fontSize: 9, marginLeft: 4 }}>{({'credit':'','auth':'','timeout':'','rate_limit':'','server':''} as Record<string,string>)[a.error_type] || ''} {a.error_type}</span>}
                                                  {a.error_message && <span style={{ color: theme.error }}> — {a.error_message}</span>}
                                                  {a.latency_ms != null && <span style={{ color: theme.dimmed }}> ({humanizeTime(a.latency_ms)})</span>}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {detail.attempts?.length === 1 && detail.attempts[0].status === 'success' && (
 <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 4, background: T.successBg, color: T.success, fontWeight: 600 }}>Primeira tentativa — sem fallback</span>
                                        )}
                                      </div>

                                      {/* ETAPA 7: Chamada Enviada */}
                                      <div style={{ position: 'relative', marginBottom: 16 }}>
                                        <div style={{ position: 'absolute', left: -31, top: 0, width: 24, height: 24, borderRadius: '50%', background: T.border, border: `1px solid ${T.border}`, color: T.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>7</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>7. Chamada Enviada</div>
                                        <div className="adv-report-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px 12px', fontSize: 11 }}>
                                          <div><span style={{ color: theme.dimmed }}>Modelo Pedido:</span> <span style={{ color: theme.text }}>{detail.request_model || r.request_model || '—'}</span></div>
                                          <div><span style={{ color: theme.dimmed }}>Modelo Resolvido:</span> <span style={{ color: theme.cyan }}>{detail.model || r.model || '—'}</span></div>
                                          <div><span style={{ color: theme.dimmed }}>Provider:</span> <span style={{ color: theme.text }}>{detail.provider || r.provider}</span></div>
                                          <div><span style={{ color: T.textMuted }}>Latência LLM:</span> <span style={{ color: T.text }}>{humanizeTime(detail.latency_ms ?? r.latency_ms)}</span></div>
 <div>{(() => { const lat = detail.latency_ms || r.latency_ms || 0; return <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 10, background: lat > 10000 ? T.dangerBg : lat < 2000 ? T.successBg : T.warningBg, color: lat > 10000 ? theme.error : lat < 2000 ? T.success : theme.warning }}>{lat > 10000 ? 'Lento' : lat < 2000 ? 'Rápido' : 'Normal'}</span>; })()}</div>
                                        </div>
                                      </div>

                                      {/* ETAPA 8: Resposta Recebida */}
                                      {(() => {
                                        const noResponse = !detail.response_content && r.status === 'success';
                                        return (
                                          <div style={{ position: 'relative', marginBottom: 10 }}>
                                            <div style={{ position: 'absolute', left: -31, top: 0, width: 24, height: 24, borderRadius: '50%', background: T.border, border: r.status === 'error' ? `1px solid ${T.danger}` : `1px solid ${T.border}`, color: r.status === 'error' ? T.danger : T.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>8</div>
                                            <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>8. Resposta Recebida</div>
                                            <div className="adv-report-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px 12px', fontSize: 11, marginBottom: 8 }}>
                                              <div><span style={{ color: theme.dimmed }}>Input:</span> <span style={{ color: theme.text }}>{(detail.input_tokens || 0).toLocaleString()}</span></div>
                                              <div><span style={{ color: theme.dimmed }}>Output:</span> <span style={{ color: theme.text }}>{(detail.output_tokens || 0).toLocaleString()}</span></div>
                                              <div><span style={{ color: theme.dimmed }}>Total:</span> <span style={{ color: theme.text }}>{((detail.input_tokens || 0) + (detail.output_tokens || 0)).toLocaleString()}</span></div>
                                              <div><span style={{ color: theme.dimmed }}>Finish:</span> <span style={{ padding: '1px 6px', borderRadius: 3, background: T.primaryBg, color: theme.primary }}>{detail.finish_reason || '—'}</span></div>
                                              <div><span style={{ color: theme.dimmed }}>Stop:</span> <span style={{ padding: '1px 6px', borderRadius: 3, background: T.primaryBg, color: theme.primary }}>{detail.stop_reason || '—'}</span></div>
 <div><span style={{ color: theme.dimmed }}>Tokens/s:</span> <span style={{ color: theme.success, fontWeight: 600 }}>{tokPerSec}</span>{bench && tokPerSec !== '—' && <span style={{ fontSize: 8, marginLeft: 4 }}>{tokPerSecNum > bench ? ' acima' : ' abaixo'} ~{bench} t/s</span>}</div>
                                            </div>
                                            {/* Token Bar */}
                                            <div style={{ marginBottom: 8 }}>
                                              <div style={{ display: 'flex', height: 12, borderRadius: 4, overflow: 'hidden', background: T.bgCard }}>
                                                <div style={{ width: `${(detail.input_tokens || 0) / ((detail.input_tokens || 0) + (detail.output_tokens || 1)) * 100}%`, background: T.primary, minWidth: 2 }} title={`Input: ${detail.input_tokens}`}></div>
                                                <div style={{ flex: 1, background: T.success, minWidth: 2 }} title={`Output: ${detail.output_tokens}`}></div>
                                              </div>
                                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: theme.dimmed, marginTop: 2 }}>
 <span>Input: {(detail.input_tokens || 0).toLocaleString()}</span>
 <span>Output: {(detail.output_tokens || 0).toLocaleString()}</span>
                                              </div>
                                            </div>
                                            {/* Response text */}
                                            {(() => {
                                              const mode = viewMode[r.id] || 'compact';
                                              const content = detail.response_content || '';
                                              const contentLines = content.split('\n');
                                              const isCompactTruncated = mode === 'compact' && contentLines.length > 3;
                                              const displayContent = isCompactTruncated ? contentLines.slice(0, 3).join('\n') + '\n...' : content;
                                              return (
                                            <div style={{ padding: '8px 12px', background: T.successBg, borderRadius: 6, border: `1px solid ${theme.border}`, marginBottom: 8 }}>
                                              <div style={{ fontSize: 11, color: theme.dimmed, marginBottom: 4 }}>Resposta LLM:</div>
                                              <div style={{ fontSize: 12, color: theme.text, whiteSpace: 'pre-wrap', ...(mode === 'full' ? {} : { maxHeight: 100 }), overflow: 'auto' }}>
 {content ? displayContent : (noResponse ? 'Anterior ao sistema de captura' : 'Sem resposta')}
                                              </div>
                                              {isCompactTruncated && <Button onClick={(e: any) => { e.stopPropagation(); setViewMode(prev => ({ ...prev, [r.id]: 'full' })); }} variant="ghost" size="xs" style={{ fontSize: 10, padding: '2px 8px', marginTop: 4, borderRadius: 4, background: T.primaryBg, color: theme.primary, border: 'none' }}>Ver mais</Button>}
                                            </div>
                                              );
                                            })()}
                                            {/* Tool Calls */}
                                            {detail.response_tool_calls && detail.response_tool_calls.length > 0 && (() => {
                                              const mode = viewMode[r.id] || 'compact';
                                              if (mode === 'compact') {
                                                return (
                                                  <div style={{ padding: '8px 12px', background: T.primaryBg, borderRadius: 6, border: `1px solid ${theme.border}`, marginBottom: 8 }}>
                                                    <div style={{ fontSize: 11, color: theme.dimmed }}>Tool Calls: <span style={{ color: theme.text, fontWeight: 600 }}>{detail.response_tool_calls.length}</span> {detail.response_tool_calls.map((tc: any, i: number) => <span key={i} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: T.primaryBg, color: theme.cyan, marginLeft: 4 }}>{tc.function?.name || tc.name || `call_${i}`}</span>)}</div>
                                                  </div>
                                                );
                                              }
                                              return (
                                              <div style={{ padding: '8px 12px', background: T.primaryBg, borderRadius: 6, border: `1px solid ${theme.border}`, marginBottom: 8 }}>
                                                <div style={{ fontSize: 11, color: theme.dimmed, marginBottom: 6 }}>Tool Calls ({detail.response_tool_calls.length}):</div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                  {detail.response_tool_calls.map((tc: any, tci: number) => (
                                                    <div key={tci} style={{ padding: '6px 10px', background: T.primaryBg, borderRadius: 6, border: `1px solid rgba(98,208,255,0.15)` }}>
 <div style={{ fontSize: 11, fontWeight: 600, color: theme.cyan, marginBottom: 3 }}> {tc.function?.name || tc.name || `call_${tci}`}</div>
                                                      <pre style={{ fontSize: 10, color: theme.text, margin: 0, whiteSpace: 'pre-wrap', overflow: 'auto' }}>{(() => { try { return JSON.stringify(JSON.parse(tc.function?.arguments || tc.arguments || '{}'), null, 2); } catch { return tc.function?.arguments || tc.arguments || '—'; } })()}</pre>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                              );
                                            })()}
                                            {/* Cache */}
                                            <div style={{ display: 'flex', gap: 12, fontSize: 11, flexWrap: 'wrap' }}>
 <span><span style={{ color: theme.dimmed }}>Cache Hit:</span> {detail.cache_hit ? '' : ''}</span>
                                              <span><span style={{ color: theme.dimmed }}>Cache Read:</span> {detail.cache_read_tokens?.toLocaleString() || 0}</span>
                                              <span><span style={{ color: theme.dimmed }}>Cache Creation:</span> {detail.cache_creation_tokens?.toLocaleString() || 0}</span>
                                              {cacheEst && <span><span style={{ color: theme.dimmed }}>Economia:</span> <span style={{ color: theme.success }}>~${cacheEst}</span></span>}
                                            </div>
                                          </div>
                                        );
                                      })()}

                                      {/* ETAPA 9: Cobrança */}
                                      <div style={{ position: 'relative', marginBottom: 16 }}>
                                        <div style={{ position: 'absolute', left: -31, top: 0, width: 24, height: 24, borderRadius: '50%', background: T.border, border: detail.charge_status === 'failed' ? `1px solid ${T.danger}` : `1px solid ${T.border}`, color: detail.charge_status === 'failed' ? T.danger : T.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>9</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>9. Cobrança</div>
                                        {/* 4 financial cards */}
                                        <div className="adv-report-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                                          {[
 { label: 'Custo', val: `$${_custo.toFixed(4)}`, color: theme.warning },
 { label: 'Cobrado', val: `$${_faturado.toFixed(4)}`, color: theme.cyan },
 { label: 'Lucro', val: `${marginVal >= 0 ? '+' : ''}$${marginVal.toFixed(4)}`, color: marginVal >= 0 ? theme.success : theme.error },
 { label: 'Margem', val: `${marginPct}%`, color: theme.primary },
                                          ].map((c, ci) => (
                                            <div key={ci} style={{ padding: '8px 10px', background: T.bgCard, borderRadius: 6, border: '1px solid #1e293b', textAlign: 'center' }}>
                                              <div style={{ fontSize: 10, color: theme.dimmed }}>{c.label}</div>
                                              <div style={{ fontSize: 16, fontWeight: 700, color: c.color }}>{c.val}</div>
                                            </div>
                                          ))}
                                        </div>
                                        {/* Token Breakdown */}
                                        {pb && (viewMode[r.id] || 'compact') === 'full' && (
                                          <div style={{ marginBottom: 10 }}>
                                            <div style={{ fontSize: 11, fontWeight: 600, color: theme.text, marginBottom: 4 }}>Breakdown de Tokens</div>
                                            <table className="adv-breakdown-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                                              <thead>
                                                <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                                                  {['Tipo', 'Tokens', 'Custo/1M', 'Cobrado/1M', 'Custo Total', 'Cobrado Total', 'Lucro'].map(h => (
                                                    <th key={h} style={{ textAlign: 'left', padding: '4px 6px', color: theme.dimmed, fontWeight: 600 }}>{h}</th>
                                                  ))}
                                                </tr>
                                              </thead>
                                              <tbody>
                                                <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                                                  <td style={{ padding: '4px 6px', color: T.primary }}>Input</td>
                                                  <td style={{ padding: '4px 6px', color: theme.text }}>{(detail.input_tokens || 0).toLocaleString()}</td>
                                                  <td style={{ padding: '4px 6px', color: theme.text }}>${pb.input_cost_per_1m?.toFixed(2) ?? '—'}</td>
                                                  <td style={{ padding: '4px 6px', color: theme.text }}>${pb.input_price_per_1m?.toFixed(2) ?? '—'}</td>
                                                  <td style={{ padding: '4px 6px', color: theme.warning }}>${pb.input_cost?.toFixed(4) ?? '—'}</td>
                                                  <td style={{ padding: '4px 6px', color: theme.cyan }}>${pb.input_billed?.toFixed(4) ?? '—'}</td>
                                                  <td style={{ padding: '4px 6px', color: theme.success }}>${((pb.input_billed || 0) - (pb.input_cost || 0)).toFixed(4)}</td>
                                                </tr>
                                                <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                                                  <td style={{ padding: '4px 6px', color: T.success }}>Output</td>
                                                  <td style={{ padding: '4px 6px', color: theme.text }}>{(detail.output_tokens || 0).toLocaleString()}</td>
                                                  <td style={{ padding: '4px 6px', color: theme.text }}>${pb.output_cost_per_1m?.toFixed(2) ?? '—'}</td>
                                                  <td style={{ padding: '4px 6px', color: theme.text }}>${pb.output_price_per_1m?.toFixed(2) ?? '—'}</td>
                                                  <td style={{ padding: '4px 6px', color: theme.warning }}>${pb.output_cost?.toFixed(4) ?? '—'}</td>
                                                  <td style={{ padding: '4px 6px', color: theme.cyan }}>${pb.output_billed?.toFixed(4) ?? '—'}</td>
                                                  <td style={{ padding: '4px 6px', color: theme.success }}>${((pb.output_billed || 0) - (pb.output_cost || 0)).toFixed(4)}</td>
                                                </tr>
                                                <tr style={{ fontWeight: 700 }}>
                                                  <td style={{ padding: '4px 6px', color: theme.text }}>Total</td>
                                                  <td style={{ padding: '4px 6px', color: theme.text }}>{((detail.input_tokens || 0) + (detail.output_tokens || 0)).toLocaleString()}</td>
                                                  <td style={{ padding: '4px 6px' }}></td><td style={{ padding: '4px 6px' }}></td>
                                                  <td style={{ padding: '4px 6px', color: theme.warning }}>${pb.total_cost?.toFixed(4) ?? '—'}</td>
                                                  <td style={{ padding: '4px 6px', color: theme.cyan }}>${pb.total_billed?.toFixed(4) ?? '—'}</td>
                                                  <td style={{ padding: '4px 6px', color: pb.profit >= 0 ? theme.success : theme.error }}>${pb.profit?.toFixed(4) ?? '—'}</td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                        )}
                                        {/* Balance + charge */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px 12px', fontSize: 11, marginBottom: 6 }}>
                                          <div><span style={{ color: T.textMuted }}>Saldo Antes:</span> <span style={{ color: T.text }}>{(detail.balance_before ?? r.balance_before) != null ? `$${Number(detail.balance_before ?? r.balance_before).toFixed(2)}` : '—'}</span></div>
                                          <div><span style={{ color: T.textMuted }}>Saldo Depois:</span> <span style={{ color: T.text }}>{(detail.balance_after ?? r.balance_after) != null ? `$${Number(detail.balance_after ?? r.balance_after).toFixed(2)}` : '—'}</span></div>
                                          <div><span style={{ color: theme.dimmed }}>Charge Status:</span> <span style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 700, fontSize: 11, background: `${chargeColor(detail.charge_status)}15`, color: chargeColor(detail.charge_status) }}>{chargeStatusBadge(detail.charge_status)}</span></div>
                                        </div>
                                        {detail.charge_status === 'failed' && (
 <div style={{ padding: '6px 10px', background: T.dangerBg, borderRadius: 6, color: theme.error, fontSize: 11, fontWeight: 600 }}>COBRANÇA FALHOU{detail.error_message ? `: ${detail.error_message}` : ''}</div>
                                        )}
                                      </div>

                                      {/* ETAPA 10: Resposta Entregue */}
                                      <div style={{ position: 'relative', marginBottom: 10 }}>
                                        <div style={{ position: 'absolute', left: -31, top: 0, width: 24, height: 24, borderRadius: '50%', background: T.border, border: r.status === 'error' ? `1px solid ${T.danger}` : `1px solid ${T.border}`, color: r.status === 'error' ? T.danger : T.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>10</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>10. Resposta Entregue</div>
                                        <div className="adv-report-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6, marginBottom: 8 }}>
                                          <div style={{ padding: '6px 8px', background: T.primaryBg, borderRadius: 6, textAlign: 'center' }}>
                                            <div style={{ fontSize: 9, color: theme.dimmed }}>Latência Total</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{humanizeTime(totalLat)}</div>
                                          </div>
                                          <div style={{ padding: '6px 8px', background: T.primaryBg, borderRadius: 6, textAlign: 'center' }}>
                                            <div style={{ fontSize: 9, color: theme.dimmed }}>Latência LLM</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: T.primary }}>{humanizeTime(llmLat)}</div>
                                          </div>
                                          <div style={{ padding: '6px 8px', background: T.warningBg, borderRadius: 6, textAlign: 'center' }}>
                                            <div style={{ fontSize: 9, color: theme.dimmed }}>Overhead Router</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: theme.warning }}>{humanizeTime(overhead)}</div>
                                          </div>
                                          <div style={{ padding: '6px 8px', borderRadius: 6, textAlign: 'center', background: r.status === 'success' ? T.successBg : r.status === 'error' ? T.dangerBg : r.status === 'rejected' ? T.warningBg : T.bgHover }}>
                                            <div style={{ fontSize: 9, color: theme.dimmed }}>Status</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: r.status === 'success' ? theme.success : r.status === 'error' ? theme.error : r.status === 'rejected' ? theme.warning : theme.dimmed }}>{r.status || detail.status}</div>
                                          </div>
                                        </div>
                                        {/* Latency bar */}
                                        <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: T.bgCard }}>
                                          <div style={{ width: `${llmPct}%`, background: T.primary }} title={`LLM: ${llmLat}ms`}></div>
                                          <div style={{ flex: 1, background: T.warning }} title={`Overhead: ${overhead}ms`}></div>
                                        </div>
                                      </div>

                                      {/* ETAPA 11: Registro */}
                                      <div style={{ position: 'relative', marginBottom: 16 }}>
                                        <div style={{ position: 'absolute', left: -31, top: 0, width: 24, height: 24, borderRadius: '50%', background: T.border, border: `1px solid ${T.border}`, color: T.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>11</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>11. Registro</div>
                                        {/* Failure Type badge */}
                                        {detail.failure_type && (() => {
 const ftMap: Record<string, { emoji: string; color: string }> = { insufficient_balance: { emoji: '', color: T.danger }, no_keys: { emoji: '', color: T.warning }, all_keys_failed: { emoji: '', color: T.danger }, budget_exceeded: { emoji: '', color: T.warning }, router_error: { emoji: '', color: T.textMuted } };
 const ft = ftMap[detail.failure_type] || { label: 'Erro', color: theme.error };
                                          return <div style={{ marginBottom: 8 }}><span style={{ padding: '4px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, background: `${ft.color}15`, color: ft.color, border: `1px solid ${ft.color}40` }}>{ft.label}</span></div>;
                                        })()}
                                        {/* IDs grid */}
                                        <div className="adv-report-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 12px', fontSize: 10, marginBottom: 8 }}>
                                          {[
                                            ['Request ID', r.id, `tl-rid-${r.id}`],
                                            ['Customer ID', (r as any).customer_id || detail.customer_id, `tl-mcid-${r.id}`],
                                            ['Token ID', detail.token_id, `tl-mtid-${r.id}`],
                                            ['Key ID', detail.key_id, `tl-mkid-${r.id}`],
                                            ['Successful Key', detail.successful_key_label || detail.successful_key_id || '—', `tl-sk-${r.id}`],
                                            ['Agent', detail.agent_id || '—', `tl-ag-${r.id}`],
                                            ['Runner', detail.runner_id || r.runner_id || '—', `tl-rn-${r.id}`],
                                            ['Session', detail.session_id || '—', `tl-ss-${r.id}`],
                                            ['Task Type', detail.task_type || '—', `tl-tt-${r.id}`],
                                            ['Failure Type', detail.failure_type || '—', `tl-ft-${r.id}`],
                                          ].map(([label, val, cid]) => (
                                            <div key={label as string} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                              <span style={{ color: theme.dimmed }}>{label}:</span>
 <span style={{ color: theme.text, cursor: val && val !== '—' ? 'pointer' : 'default', textDecoration: val && val !== '—' ? 'underline dotted' : 'none' }} title="Clique para copiar" onClick={(e) => { e.stopPropagation(); val && val !== '—' && copyWithFeedback(String(val), String(cid)); }}>{copiedId === cid ? 'Copiado!' : (val || '—')}</span>
                                            </div>
                                          ))}
                                        </div>
                                        {/* Observations */}
                                        {(r.observations && r.observations.length > 0) && (
                                          <div style={{ marginBottom: 8 }}>
                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                              {r.observations.map((ob: string) => {
 const info = obsIcons[ob] || { icon: '', color: theme.dimmed, label: ob };
                                                return <span key={ob} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: `${info.color}15`, color: info.color }}>{info.icon} {info.label}</span>;
                                              })}
                                            </div>
                                          </div>
                                        )}
                                        {/* Error block */}
                                        {detail.error_message && (
                                          <div style={{ padding: '6px 10px', background: T.dangerBg, borderRadius: 6, color: theme.error, fontSize: 11, marginBottom: 8 }}>
 <span style={{ fontWeight: 600 }}>Erro:</span> {detail.error_message}
                                          </div>
                                        )}
                                        {/* Copy buttons */}
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                          {detail.response_content && (
 <Button onClick={(e: any) => { e.stopPropagation(); copyWithFeedback(detail.response_content, `cp-resp-${r.id}`); }} variant="secondary" size="xs" style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, background: T.primaryBg }}>{copiedId === `cp-resp-${r.id}` ? 'Copiado!' : 'Copiar Resposta'}</Button>
                                          )}
 <Button onClick={(e: any) => { e.stopPropagation(); copyWithFeedback(JSON.stringify(detail, null, 2), `cp-json-${r.id}`); }} variant="secondary" size="xs" style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, background: T.primaryBg }}>{copiedId === `cp-json-${r.id}` ? 'Copiado!' : 'Copiar JSON Completo'}</Button>
 <Button onClick={(e: any) => { e.stopPropagation(); copyWithFeedback(`${window.location.origin}/admin/elon-router?tab=analytics&request=${r.id}`, `cp-link-${r.id}`); }} variant="secondary" size="xs" style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, background: T.primaryBg }}>{copiedId === `cp-link-${r.id}` ? 'Copiado!' : 'Copiar Link Direto'}</Button>
                                        </div>
                                      </div>

                                    </div>

                                    </div>)}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  }
                });
                return rows;
              })()}
            </tbody>
          </table>
        </div>

        {/* Ver mais */}
        {turnGrouping ? (
          (() => {
            const allItems = computeTurnGroups(requests);
            const totalGroups = allItems.length;
            // Show "ver mais" for groups OR load more raw requests
            const hasMoreGroups = visibleGroups < totalGroups;
            const hasMoreRequests = requests.length < reqPagination.total;
            return (hasMoreGroups || hasMoreRequests) ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 12 }}>
                <Button onClick={() => {
                  if (hasMoreGroups) {
                    setVisibleGroups(v => v + 50);
                  }
                  if (!hasMoreGroups || visibleGroups + 50 >= totalGroups) {
                    // Need more raw data — fetch next batch
                    if (hasMoreRequests) {
                      const o = requests.length;
                      setReqOffset(o);
                      loadRequests(o, true);
                    }
                  }
                }} variant="ghost" size="xs" disabled={loadingMore}
                  style={{ background: T.primaryBg, color: T.primary, border: `1px solid ${theme.primary}`, borderRadius: theme.r2, padding: '6px 24px', fontSize: 12, fontWeight: 600 }}>
                  {loadingMore ? '⏳ Carregando...' : `Ver mais (${Math.min(visibleGroups, totalGroups)} de ${reqPagination.total.toLocaleString()} turnos)`}
                </Button>
              </div>
            ) : null;
          })()
        ) : requests.length < reqPagination.total ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 12 }}>
            <Button onClick={() => { const o = requests.length; setReqOffset(o); loadRequests(o, true); }} variant="ghost" size="xs" disabled={loadingMore}
              style={{ background: T.primaryBg, color: T.primary, border: `1px solid ${theme.primary}`, borderRadius: theme.r2, padding: '6px 24px', fontSize: 12, fontWeight: 600 }}>
              {loadingMore ? '⏳ Carregando...' : `Ver mais (${requests.length} de ${reqPagination.total.toLocaleString()})`}
            </Button>
          </div>
        ) : null}
      </Card>

      {/* Modal removed — inline expand replaces it */}

      {/* Empty state */}
      {!realtime && !loadingState && (
        <Card>
          <div style={{ textAlign: 'center', padding: theme.sp8, color: theme.dimmed }}>
 <div style={{ fontSize: 36, marginBottom: 8 }}></div>
            <p style={{ fontSize: 14, marginBottom: 4 }}>Sem dados de analytics</p>
            <p style={{ fontSize: 12 }}>Dados aparecem quando requests são feitos pelo Elon Router</p>
          </div>
        </Card>
      )}
    </div>
  );
}

// ═══ Pricing Tab ═══

interface PricingItem {
  id: string;
  provider: string;
  model: string;
  cost_input_per_1m: number;
  cost_output_per_1m: number;
  markup_pct: number;
  active: number;
  available: boolean;
  total_requests_7d: number;
  total_cost_7d: number;
  total_requests_30d: number;
  total_cost_30d: number;
}

interface PricingSummary {
  total_cost_7d: number;
  total_cost_30d: number;
  avg_cost_per_request: number;
  total_requests_30d: number;
}

interface BreakdownData {
  by_provider: Array<{ provider: string; requests: number; total_cost: number; percentage: number }>;
  by_model: Array<{ model: string; requests: number; cost: number }>;
  by_day: Array<{ date: string; cost: number; requests: number }>;
}

function PricingTab() {
  const [pricing, setPricing] = useState<PricingItem[]>([]);
  const [summary, setSummary] = useState<PricingSummary | null>(null);
  const [breakdown, setBreakdown] = useState<BreakdownData | null>(null);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ cost_input_per_1m: string; cost_output_per_1m: string; markup_pct: string }>({ cost_input_per_1m: '', cost_output_per_1m: '', markup_pct: '' });
  const [simModel, setSimModel] = useState('');
  const [simInput, setSimInput] = useState(1000);
  const [simOutput, setSimOutput] = useState(500);
  const [aliases, setAliases] = useState<Array<{ provider: string; alias: string; canonical: string }>>([]);
  const [showAliases, setShowAliases] = useState(false);

  const loadPricing = async () => {
    setLoadingPricing(true);
    try {
      const [pRes, bRes, aRes] = await Promise.all([
        api.get<any>('/admin/router/pricing'),
        api.get<any>('/admin/router/pricing/breakdown?period=30'),
        api.get<any>('/admin/router/pricing/aliases').catch(() => ({ aliases: [] })),
      ]);
      const pData = pRes.data || pRes;
      const bData = bRes.data || bRes;
      const aData = aRes.data || aRes;
      setPricing(pData.pricing || []);
      setSummary(pData.summary || null);
      setBreakdown(bData || null);
      setAliases(aData.aliases || []);
    } catch (e: any) {
      showToast('error', `Pricing load failed: ${e.message}`);
    }
    setLoadingPricing(false);
  };

  useEffect(() => { loadPricing(); }, []);

  const syncPricing = async () => {
    setSyncing(true);
    try {
      const res = await api.post<any>('/admin/router/pricing/sync', {});
      const data = res.data || res;
      showToast('success', `Synced ${data.synced || 0} pricing entries`);
      await loadPricing();
    } catch (e: any) {
      showToast('error', `Sync failed: ${e.message}`);
    }
    setSyncing(false);
  };

  const startEdit = (item: PricingItem) => {
    setEditingId(item.id);
    setEditValues({
      cost_input_per_1m: String(item.cost_input_per_1m),
      cost_output_per_1m: String(item.cost_output_per_1m),
      markup_pct: String((item as any).markup_pct ?? -1),
      markup_pct_in: String((item as any).markup_pct_in ?? -1),
      markup_pct_out: String((item as any).markup_pct_out ?? -1),
      markup_fixed_in: String((item as any).markup_fixed_in ?? 0),
      markup_fixed_out: String((item as any).markup_fixed_out ?? 0),
    });
  };

  const saveEdit = async (item: PricingItem) => {
    try {
      await api.put(`/admin/router/pricing/${item.id}`, {
        cost_input_per_1m: parseFloat(editValues.cost_input_per_1m),
        cost_output_per_1m: parseFloat(editValues.cost_output_per_1m),
        markup_pct: parseFloat(editValues.markup_pct),
        markup_pct_in: parseFloat((editValues as any).markup_pct_in),
        markup_pct_out: parseFloat((editValues as any).markup_pct_out),
        markup_fixed_in: parseFloat((editValues as any).markup_fixed_in),
        markup_fixed_out: parseFloat((editValues as any).markup_fixed_out),
      });
      showToast('success', `Updated ${item.provider}/${item.model}`);
      setEditingId(null);
      await loadPricing();
    } catch (e: any) {
      showToast('error', `Update failed: ${e.message}`);
    }
  };

  const exportCSV = () => {
    const headers = ['Provider', 'Model', 'Cost Input/1M', 'Cost Output/1M', 'Requests 30d', 'Cost 30d'];
    const rows = pricing.map(p => [p.provider, p.model, p.cost_input_per_1m, p.cost_output_per_1m, p.total_requests_30d, p.total_cost_30d.toFixed(4)]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `llm-pricing-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Simulator calculation
  const simItem = pricing.find(p => p.id === simModel || `${p.provider}:${p.model}` === simModel);
  const simCost = simItem
    ? (simInput / 1_000_000) * simItem.cost_input_per_1m + (simOutput / 1_000_000) * simItem.cost_output_per_1m
    : 0;

  if (loadingPricing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp4 }}>
        {[1, 2, 3].map(i => <Card key={i}><SkeletonBar width="60%" height={20} /><SkeletonBar width="80%" /></Card>)}
      </div>
    );
  }

  // Group by provider
  const providers = [...new Set(pricing.map(p => p.provider))];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp5 }}>
      {/* Action buttons */}
      <div style={{ display: 'flex', gap: theme.sp3, flexWrap: 'wrap', alignItems: 'center' }}>
        <PrimaryButton onClick={syncPricing} disabled={syncing}>
          {syncing ? 'Sincronizando...' : 'Sincronizar Preços'}
        </PrimaryButton>
        <SecondaryButton onClick={() => setShowAliases(!showAliases)}>
          {showAliases ? 'Ocultar Aliases' : `Aliases (${aliases.length})`}
        </SecondaryButton>
        <SecondaryButton onClick={exportCSV}>Exportar CSV</SecondaryButton>
      </div>

      {/* Aliases Table */}
      {showAliases && aliases.length > 0 && (
        <Card>
          <SectionHeader title="Aliases de Modelos" subtitle={`${aliases.length} aliases → modelos canônicos. Requests com alias são cobrados pelo preço do modelo canônico.`} />
          <div style={{ overflowX: 'auto', maxHeight: 400 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                  {['Provider', 'Alias (nome recebido)', '→', 'Canônico (cobrado)'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: theme.muted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {aliases.map((a, i) => {
                  const prov = PROVIDERS[a.provider];
                  return (
                    <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <td style={{ padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 14 }}>{prov?.icon || ''}</span>
                        <span style={{ color: prov?.color || theme.text, fontWeight: 600, fontSize: 11 }}>{prov?.name || a.provider}</span>
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <code style={{ fontSize: 11, color: theme.dimmed, fontFamily: 'monospace' }}>{a.alias}</code>
                      </td>
                      <td style={{ padding: '6px 10px', color: theme.dimmed, fontSize: 11 }}>→</td>
                      <td style={{ padding: '6px 10px' }}>
                        <code style={{ fontSize: 11, color: theme.cyan, fontWeight: 600, fontFamily: 'monospace' }}>{a.canonical}</code>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: theme.sp3 }}>
          {[
 { label: 'Total Gasto (7d)', value: fmtCost(summary.total_cost_7d), color: theme.warning, icon: '' },
 { label: 'Total Gasto (30d)', value: fmtCost(summary.total_cost_30d), color: theme.warning, icon: '' },
 { label: 'Custo Médio/Req', value: `$${summary.avg_cost_per_request.toFixed(6)}`, color: theme.primary, icon: '' },
 { label: 'Requests (30d)', value: summary.total_requests_30d.toLocaleString(), color: theme.success, icon: '' },
          ].map((s, i) => (
            <div key={i} style={{
              padding: '16px 18px', borderRadius: theme.r4,
              background: theme.card, border: `1px solid ${theme.borderSolid}`,
            }}>
              <div style={{ fontSize: 11, color: theme.dimmed, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span>{s.icon}</span> {s.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Pricing Table */}
      <Card>
 <SectionHeader title="Tabela de Preços LLM" subtitle="Custo Provider → + Markup % → Preço final calculado automaticamente" />
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                {['Status', 'Provider', 'Modelo', 'Custo In', 'Custo Out', 'Markup % In', 'Markup $ In', 'Markup % Out', 'Markup $ Out', 'Reqs', ''].map(h => (
                  <th key={h} style={{ textAlign: ['Provider', 'Modelo', '', 'Status'].includes(h) ? 'left' : 'right', padding: '8px 5px', color: theme.muted, fontWeight: 600, fontSize: 10, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pricing.map(p => {
                const prov = PROVIDERS[p.provider];
                const isEditing = editingId === p.id;
                const em = (p as any).effective_markup || { pct_in: 0, pct_out: 0, fixed_in: 0, fixed_out: 0 };
                const srcIn = (p as any).markup_source_in === 'default' ? '' : '';
                const srcOut = (p as any).markup_source_out === 'default' ? '' : '';
                return (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${theme.border}`, opacity: p.available === false ? 0.5 : 1 }}>
                    <td style={{ padding: '6px 8px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
                        borderRadius: 10, fontSize: 10, fontWeight: 700,
                        background: p.available !== false ? T.successBg : T.dangerBg,
                        color: p.available !== false ? T.success : T.danger,
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: p.available !== false ? T.success : T.danger }} />
                        {p.available !== false ? 'Ativo' : 'Indisponível'}
                      </span>
                    </td>
                    <td style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 14 }}>{prov?.icon || ''}</span>
                      <span style={{ color: prov?.color || theme.text, fontWeight: 600, fontSize: 11 }}>{prov?.name || p.provider}</span>
                    </td>
                    <td style={{ padding: '6px 8px', color: theme.cyan }}>
                      <code style={{ fontSize: 11 }}>{p.model}</code>
                    </td>
                    {/* Custo In */}
                    <td style={{ padding: '6px 5px', textAlign: 'right' }}>
                      {isEditing ? (
                        <Input value={editValues.cost_input_per_1m} onChange={(v) => setEditValues(prev => ({ ...prev, cost_input_per_1m: v }))}
                          style={{ width: 65, padding: '3px 4px', borderRadius: 4, background: theme.bg, border: `1px solid ${theme.primary}`, color: theme.text, fontSize: 11, textAlign: 'right' }} />
                      ) : (
                        <span style={{ color: theme.text, fontSize: 12 }}>${p.cost_input_per_1m.toFixed(3)}</span>
                      )}
                    </td>
                    {/* Custo Out */}
                    <td style={{ padding: '6px 5px', textAlign: 'right' }}>
                      {isEditing ? (
                        <Input value={editValues.cost_output_per_1m} onChange={(v) => setEditValues(prev => ({ ...prev, cost_output_per_1m: v }))}
                          style={{ width: 65, padding: '3px 4px', borderRadius: 4, background: theme.bg, border: `1px solid ${theme.primary}`, color: theme.text, fontSize: 11, textAlign: 'right' }} />
                      ) : (
                        <span style={{ color: theme.text, fontSize: 12 }}>${p.cost_output_per_1m.toFixed(3)}</span>
                      )}
                    </td>
                    {/* Markup % In */}
                    <td style={{ padding: '6px 5px', textAlign: 'right' }}>
                      {isEditing ? (
                        <Input value={(editValues as any).markup_pct_in ?? ''} onChange={(v) => setEditValues(prev => ({ ...prev, markup_pct_in: v }))}
                          placeholder="%" style={{ width: 50, padding: '3px 4px', borderRadius: 4, background: theme.bg, border: `1px solid ${theme.warning}`, color: theme.warning, fontSize: 11, textAlign: 'right' }} />
                      ) : (
                        <span style={{ color: theme.warning, fontSize: 12, fontWeight: 600 }}>{srcIn} {em.pct_in}%</span>
                      )}
                    </td>
                    {/* Markup $ In */}
                    <td style={{ padding: '6px 5px', textAlign: 'right' }}>
                      {isEditing ? (
                        <Input value={(editValues as any).markup_fixed_in ?? ''} onChange={(v) => setEditValues(prev => ({ ...prev, markup_fixed_in: v }))}
                          placeholder="$0" style={{ width: 50, padding: '3px 4px', borderRadius: 4, background: theme.bg, border: `1px solid ${theme.dimmed}`, color: theme.text, fontSize: 11, textAlign: 'right' }} />
                      ) : (
                        <span style={{ color: em.fixed_in > 0 ? theme.success : theme.dimmed, fontSize: 12, fontWeight: em.fixed_in > 0 ? 600 : 400 }}>
                          ${em.fixed_in.toFixed(2)}
                        </span>
                      )}
                    </td>
                    {/* Markup % Out */}
                    <td style={{ padding: '6px 5px', textAlign: 'right' }}>
                      {isEditing ? (
                        <Input value={(editValues as any).markup_pct_out ?? ''} onChange={(v) => setEditValues(prev => ({ ...prev, markup_pct_out: v }))}
                          placeholder="%" style={{ width: 50, padding: '3px 4px', borderRadius: 4, background: theme.bg, border: `1px solid ${theme.warning}`, color: theme.warning, fontSize: 11, textAlign: 'right' }} />
                      ) : (
                        <span style={{ color: theme.warning, fontSize: 12, fontWeight: 600 }}>{srcOut} {em.pct_out}%</span>
                      )}
                    </td>
                    {/* Markup $ Out */}
                    <td style={{ padding: '6px 5px', textAlign: 'right' }}>
                      {isEditing ? (
                        <Input value={(editValues as any).markup_fixed_out ?? ''} onChange={(v) => setEditValues(prev => ({ ...prev, markup_fixed_out: v }))}
                          placeholder="$0" style={{ width: 50, padding: '3px 4px', borderRadius: 4, background: theme.bg, border: `1px solid ${theme.dimmed}`, color: theme.text, fontSize: 11, textAlign: 'right' }} />
                      ) : (
                        <span style={{ color: em.fixed_out > 0 ? theme.success : theme.dimmed, fontSize: 12, fontWeight: em.fixed_out > 0 ? 600 : 400 }}>
                          ${em.fixed_out.toFixed(2)}
                        </span>
                      )}
                    </td>
                    {/* Reqs */}
                    <td style={{ padding: '6px 5px', textAlign: 'right', color: theme.muted, fontSize: 12 }}>{p.total_requests_30d.toLocaleString()}</td>
                    {/* Edit button */}
                    <td style={{ padding: '6px 5px' }}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Button onClick={() => saveEdit(p)} variant="success" size="xs" style={{ padding: '4px 10px', fontSize: 11, borderRadius: 6, fontWeight: 600 }}>Salvar</Button>
                          <Button onClick={() => setEditingId(null)} variant="ghost" size="xs" style={{ background: theme.dimmed, color: T.text, padding: '4px 8px', fontSize: 11, borderRadius: 6 }}></Button>
                        </div>
                      ) : (
                        <Button onClick={() => startEdit(p)} variant="ghost" size="xs" style={{ background: T.border, border: `1px solid ${theme.border}`, color: theme.muted, fontSize: 11, padding: '4px 10px', borderRadius: 6, fontWeight: 500 }}>Editar</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Breakdown by Provider */}
      {breakdown && breakdown.by_provider.length > 0 && (
        <Card>
 <SectionHeader title="Breakdown por Provider (30d)" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {breakdown.by_provider.map((bp, i) => {
              const prov = PROVIDERS[bp.provider];
              return (
                <div key={bp.provider} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
 <span style={{ fontSize: 14, minWidth: 24 }}>{prov?.icon || ''}</span>
                  <span style={{ fontSize: 12, color: prov?.color || theme.text, fontWeight: 600, minWidth: 90 }}>{prov?.name || bp.provider}</span>
                  <div style={{ flex: 1, height: 24, borderRadius: 4, background: T.bgCard, overflow: 'hidden', position: 'relative' }}>
                    <div style={{ height: '100%', borderRadius: 4, background: CHART_COLORS[i % CHART_COLORS.length], width: `${bp.percentage}%`, transition: 'width 0.5s' }} />
                    <span style={{ position: 'absolute', right: 8, top: 4, fontSize: 11, color: theme.text, fontWeight: 600 }}>{bp.percentage}%</span>
                  </div>
                  <span style={{ fontSize: 12, color: theme.warning, fontWeight: 600, minWidth: 70, textAlign: 'right' }}>{fmtCost(bp.total_cost)}</span>
                  <span style={{ fontSize: 11, color: theme.dimmed, minWidth: 60, textAlign: 'right' }}>{bp.requests.toLocaleString()} reqs</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Cost Timeline */}
      {breakdown && breakdown.by_day.length > 0 && (
        <Card>
 <SectionHeader title="Custo Diário (30d)" />
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120, padding: '0 4px' }}>
            {(() => {
              const maxCost = Math.max(...breakdown.by_day.map(d => d.cost), 0.01);
              return breakdown.by_day.slice().reverse().map((d, i) => {
                const heightPct = (d.cost / maxCost) * 100;
                return (
                  <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
                    title={`${d.date}: ${fmtCost(d.cost)} (${d.requests} reqs)`}>
                    <div style={{
                      width: '100%', maxWidth: 20, borderRadius: '4px 4px 0 0',
                      background: theme.primary, height: `${Math.max(heightPct, 2)}%`, opacity: 0.8,
                    }} />
                    {i % 5 === 0 && <span style={{ fontSize: 8, color: theme.dimmed, whiteSpace: 'nowrap' }}>{d.date.substring(5)}</span>}
                  </div>
                );
              });
            })()}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, color: theme.muted }}>
            <span>Total: {fmtCost(breakdown.by_day.reduce((s, d) => s + d.cost, 0))}</span>
            <span>Média: {fmtCost(breakdown.by_day.reduce((s, d) => s + d.cost, 0) / (breakdown.by_day.length || 1))}/dia</span>
          </div>
        </Card>
      )}

      {/* Cost Simulator */}
      <Card>
 <SectionHeader title="Simulador de Custo" subtitle="Calcule quanto custaria usar X tokens com um modelo" />
        <div style={{ display: 'flex', gap: theme.sp3, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label style={{ fontSize: 11, color: theme.muted, display: 'block', marginBottom: 4 }}>Modelo</label>
            <Select value={simModel} onChange={(v) => setSimModel(v)}
              options={[{ value: '', label: 'Selecione...' }, ...pricing.map((p: any) => ({ value: p.id, label: `${p.provider}/${p.model}` }))]}
              style={{ width: '100%', padding: '8px 10px', borderRadius: theme.r2, background: theme.bg, border: `1px solid ${theme.borderSolid}`, color: theme.text, fontSize: 12 }} />
          </div>
          <div style={{ minWidth: 120 }}>
            <label style={{ fontSize: 11, color: theme.muted, display: 'block', marginBottom: 4 }}>Input tokens</label>
            <Input type="number" value={simInput} onChange={(v) => setSimInput(Number(v))}
              style={{ width: '100%', padding: '8px 10px', borderRadius: theme.r2, background: theme.bg, border: `1px solid ${theme.borderSolid}`, color: theme.text, fontSize: 12, boxSizing: 'border-box' }} />
          </div>
          <div style={{ minWidth: 120 }}>
            <label style={{ fontSize: 11, color: theme.muted, display: 'block', marginBottom: 4 }}>Output tokens</label>
            <Input type="number" value={simOutput} onChange={(v) => setSimOutput(Number(v))}
              style={{ width: '100%', padding: '8px 10px', borderRadius: theme.r2, background: theme.bg, border: `1px solid ${theme.borderSolid}`, color: theme.text, fontSize: 12, boxSizing: 'border-box' }} />
          </div>
          <div style={{
            padding: '10px 18px', borderRadius: theme.r3,
            background: simItem ? T.successBg : T.bgCard,
            border: `1px solid ${simItem ? T.successBg : theme.border}`,
            minWidth: 100, textAlign: 'center',
          }}>
            <div style={{ fontSize: 10, color: theme.dimmed, marginBottom: 2 }}>Custo estimado</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: simItem ? theme.success : theme.dimmed }}>
              ${simCost.toFixed(6)}
            </div>
          </div>
        </div>
        {simItem && (
          <div style={{ marginTop: theme.sp3, fontSize: 11, color: theme.dimmed }}>
 Input: {simInput.toLocaleString()} tokens × ${simItem.cost_input_per_1m}/1M = ${((simInput / 1_000_000) * simItem.cost_input_per_1m).toFixed(6)}
            {' | '}
 Output: {simOutput.toLocaleString()} tokens × ${simItem.cost_output_per_1m}/1M = ${((simOutput / 1_000_000) * simItem.cost_output_per_1m).toFixed(6)}
          </div>
        )}
      </Card>

      {/* Top Models by Cost */}
      {breakdown && breakdown.by_model.length > 0 && (
        <Card>
 <SectionHeader title="Top Modelos por Custo (30d)" />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Modelo</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Requests</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Custo Total</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>Custo/Req</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.by_model.slice(0, 10).map(m => (
                <tr key={m.model} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <td style={{ padding: '6px 8px', color: theme.cyan }}><code style={{ fontSize: 11 }}>{m.model}</code></td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: theme.muted }}>{m.requests.toLocaleString()}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: theme.warning }}>{fmtCost(m.cost)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: theme.dimmed }}>${m.requests > 0 ? (m.cost / m.requests).toFixed(6) : '0'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ═══ Aliases Tab ═══

interface AliasCanonicalEntry {
  canonical: boolean;
  aliases: string[];
  api_model_name: string;
  cost_input: number;
  cost_output: number;
  price_input: number;
  price_output: number;
}

interface AliasesResponse {
  aliases: Record<string, Record<string, AliasCanonicalEntry>>;
  orphans: string[];
  stats: { total_canonical: number; total_aliases: number; total_routing_aliases: number; providers: number };
}

function AliasesTab() {
  const [data, setData] = useState<AliasesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<any>('/admin/router/pricing/aliases');
      setData(res);
    } catch (e: any) {
      showToast('error', `Falha ao carregar aliases: ${e.message}`);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Card><SkeletonBar width="60%" height={20} /><SkeletonBar width="80%" /><SkeletonBar width="40%" /></Card>;
  if (!data) return <Card><p style={{ color: theme.error }}>Erro ao carregar dados</p></Card>;

  const chipStyle: any = {
    display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11,
    background: T.primaryBg, color: theme.primary, margin: '2px 3px',
    border: `1px solid ${theme.primary}25`,
  };

  // Flatten for table
  const rows: Array<{ provider: string; model: string; entry: AliasCanonicalEntry }> = [];
  for (const [provider, models] of Object.entries(data.aliases)) {
    for (const [model, entry] of Object.entries(models)) {
      rows.push({ provider, model, entry });
    }
  }

  const filtered = filter
    ? rows.filter(r => r.model.includes(filter) || r.provider.includes(filter) || r.entry.aliases.some(a => a.includes(filter)) || (r.entry.api_model_name || '').includes(filter))
    : rows;

  const codeStyle: any = { color: theme.text, background: T.border, padding: '1px 5px', borderRadius: 4, fontFamily: 'monospace', fontSize: 11 };
  const arrowStyle: any = { color: theme.primary, fontWeight: 700, fontSize: 16, margin: '0 8px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp4 }}>
      {/* Section 1: Fluxo de Resolução */}
      <Card>
        <SectionHeader title="Fluxo de Resolução" subtitle="Como o sistema resolve o nome de um modelo" />
        <div style={{ padding: '16px 20px', borderRadius: theme.r3, background: T.primaryBg, border: `1px solid ${theme.primary}20` }}>
          {/* Flow diagram */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, marginBottom: 14 }}>
            <div style={{ padding: '6px 14px', borderRadius: 8, background: T.border, border: `1px solid ${theme.borderSolid}`, fontSize: 12 }}>
              <div style={{ fontSize: 9, color: theme.dimmed, marginBottom: 2 }}>Nome Recebido</div>
              <div style={{ fontWeight: 600, color: theme.text }}>qualquer-alias</div>
            </div>
            <span style={arrowStyle}>→</span>
            <div style={{ padding: '6px 14px', borderRadius: 8, background: T.primaryBg, border: `1px solid ${theme.primary}30`, fontSize: 12 }}>
              <div style={{ fontSize: 9, color: theme.dimmed, marginBottom: 2 }}>Alias Billing</div>
              <div style={{ fontWeight: 600, color: theme.primary }}>MODEL_PRICING_ALIASES</div>
            </div>
            <span style={arrowStyle}>→</span>
            <div style={{ padding: '6px 14px', borderRadius: 8, background: T.successBg, border: `1px solid ${theme.success}30`, fontSize: 12 }}>
              <div style={{ fontSize: 9, color: theme.dimmed, marginBottom: 2 }}>Nome Canônico</div>
              <div style={{ fontWeight: 700, color: theme.success }}>nome-limpo</div>
            </div>
            <span style={arrowStyle}>→</span>
            <div style={{ padding: '6px 14px', borderRadius: 8, background: T.warningBg, border: `1px solid ${T.warningBg}`, fontSize: 12 }}>
              <div style={{ fontSize: 9, color: theme.dimmed, marginBottom: 2 }}>Alias Routing</div>
              <div style={{ fontWeight: 600, color: T.warning }}>MODEL_ALIASES</div>
            </div>
            <span style={arrowStyle}>→</span>
            <div style={{ padding: '6px 14px', borderRadius: 8, background: T.bgCard, border: `1px solid ${theme.borderSolid}`, fontSize: 12 }}>
              <div style={{ fontSize: 9, color: theme.dimmed, marginBottom: 2 }}>Nome API Provedor</div>
              <div style={{ fontWeight: 600, color: theme.muted, fontFamily: 'monospace' }}>model-id-real</div>
            </div>
          </div>
          {/* Example */}
          <div style={{ fontSize: 12, color: theme.muted, lineHeight: 1.8 }}>
            <strong style={{ color: theme.text }}>Exemplo:</strong>{' '}
            <code style={codeStyle}>claude-opus-4</code>
            <span style={{ color: theme.primary, margin: '0 6px' }}>→</span>
            <code style={{ ...codeStyle, color: theme.success }}>claude-opus-4.6</code>
            <span style={{ color: theme.primary, margin: '0 6px' }}>→</span>
            <code style={{ ...codeStyle, color: theme.dimmed }}>claude-opus-4-20250514</code>
          </div>
        </div>
      </Card>

      {/* Section 3: Stats */}
      <Card>
        <SectionHeader title="Resumo" subtitle="Estatísticas do mapeamento" />
        <div style={{ display: 'flex', gap: theme.sp4, flexWrap: 'wrap' }}>
          {[
            { label: 'Modelos Canônicos', value: data.stats.total_canonical, color: theme.primary },
            { label: 'Aliases Billing', value: data.stats.total_aliases, color: theme.success },
            { label: 'Aliases Routing', value: data.stats.total_routing_aliases || 0, color: T.warning },
            { label: 'Provedores', value: data.stats.providers, color: theme.cyan || '#06B6D4' },
            { label: 'Modelos Órfãos', value: data.orphans.length, color: data.orphans.length > 0 ? theme.error : theme.success },
          ].map((s, i) => (
            <div key={i} style={{
              flex: 1, minWidth: 120, padding: '16px 20px', borderRadius: theme.r3,
              background: `${s.color}08`, border: `1px solid ${s.color}20`, textAlign: 'center',
            }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: theme.muted, marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Section 4: Orphans */}
      {data.orphans.length > 0 && (
        <Card>
          <div style={{ padding: '14px 18px', borderRadius: theme.r3, background: T.dangerBg, border: `1px solid ${theme.error}20` }}>
            <SectionHeader title="Modelos Órfãos" subtitle={`${data.orphans.length} modelo(s) nos logs sem alias mapeado — adicione em llm-billing.service.ts`} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {data.orphans.map(o => (
                <span key={o} style={{
                  ...chipStyle, background: T.dangerBg, color: theme.error,
                  border: `1px solid ${theme.error}30`, fontSize: 12, padding: '4px 10px',
                }}>{o}</span>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Section 2: Tabela de Conversão Completa */}
      <Card>
        <SectionHeader title="Tabela de Conversão Completa" subtitle={`${rows.length} modelos canônicos — cadeia completa de resolução`} />
        <Input
          type="text" placeholder="Filtrar por nome, alias, provedor ou API name..."
          value={filter} onChange={(v) => setFilter(v.toLowerCase())}
          style={{
            width: '100%', padding: '8px 12px', borderRadius: theme.r2, marginBottom: theme.sp3,
            background: theme.bg, border: `1px solid ${theme.borderSolid}`, color: theme.text, fontSize: 12,
            outline: 'none', boxSizing: 'border-box',
          }}
        />
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.borderSolid}` }}>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: theme.muted, fontWeight: 600 }}>Nome Limpo (Canônico)</th>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: theme.muted, fontWeight: 600 }}>Aliases</th>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: theme.muted, fontWeight: 600 }}>Nome na API do Provedor</th>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: theme.muted, fontWeight: 600 }}>Provedor</th>
                <th style={{ textAlign: 'right', padding: '10px 8px', color: theme.muted, fontWeight: 600 }}>Custo In/1M</th>
                <th style={{ textAlign: 'right', padding: '10px 8px', color: theme.muted, fontWeight: 600 }}>Custo Out/1M</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const apiName = r.entry.api_model_name || r.model;
                const isSame = apiName === r.model;
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${theme.borderSolid}30` }}>
                    <td style={{ padding: '10px 8px' }}>
                      <span style={{ fontWeight: 700, color: theme.success }}>{r.model}</span>
                    </td>
                    <td style={{ padding: '10px 8px' }}>
                      {r.entry.aliases.length === 0
                        ? <span style={{ color: theme.dimmed, fontStyle: 'italic', fontSize: 11 }}>Nenhum</span>
                        : r.entry.aliases.map(a => <span key={a} style={chipStyle}>{a}</span>)
                      }
                    </td>
                    <td style={{ padding: '10px 8px' }}>
                      {isSame
                        ? <span style={{ color: theme.dimmed, fontStyle: 'italic', fontFamily: 'monospace', fontSize: 11 }}>Mesmo</span>
                        : <span style={{ color: theme.muted, fontFamily: 'monospace', fontSize: 11 }}>{apiName}</span>
                      }
                    </td>
                    <td style={{ padding: '10px 8px', color: theme.text, textTransform: 'capitalize' }}>{r.provider}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', color: theme.text, fontFamily: 'monospace' }}>${r.entry.cost_input.toFixed(2)}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', color: theme.text, fontFamily: 'monospace' }}>${r.entry.cost_output.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ═══ Dashboard Tab ═══

// ═══ Health Chaves Tab ═══

interface HealthKey {
  key_id: string;
  provider: string;
  total_attempts: number;
  successes: number;
  success_rate: number;
  avg_latency: number;
  last_error: string | null;
  last_error_at: string | null;
}

function HealthTab() {
  const [health, setHealth] = useState<HealthKey[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/router/attempts/health'),
      api.get('/admin/router/attempts/stats'),
    ]).then(([hRes, sRes]: any[]) => {
      const hData = hRes.data || hRes;
      const sData = sRes.data || sRes;
      setHealth(hData.keys || []);
      setStats(sData);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <Card><SkeletonBar width="60%" height={20} /><SkeletonBar width="80%" /><SkeletonBar width="40%" /></Card>;
  }

  const totalAttempts7d = health.reduce((s, k) => s + k.total_attempts, 0);
  const totalSuccesses7d = health.reduce((s, k) => s + k.successes, 0);
  const overallSuccessRate = totalAttempts7d > 0 ? (100 * totalSuccesses7d / totalAttempts7d).toFixed(1) : '0';
  const avgLatency = health.length > 0 ? Math.round(health.reduce((s, k) => s + k.avg_latency, 0) / health.length) : 0;

 const errorTypeEmoji: Record<string, string> = { credit: '', auth: '', timeout: '', rate_limit: '', server: '', unknown: '' };
  const errorTypes = stats?.error_types || [];
  const byProvider = stats?.by_provider || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp5 }}>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: theme.sp4 }}>
        <Card>
          <div style={{ fontSize: 11, color: theme.dimmed }}>Total Tentativas (7d)</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: theme.text }}>{totalAttempts7d.toLocaleString()}</div>
        </Card>
        <Card>
          <div style={{ fontSize: 11, color: theme.dimmed }}>Success Rate Geral</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: parseFloat(overallSuccessRate) >= 90 ? theme.success : parseFloat(overallSuccessRate) >= 70 ? theme.warning : theme.error }}>{overallSuccessRate}%</div>
        </Card>
        <Card>
          <div style={{ fontSize: 11, color: theme.dimmed }}>Avg Latency</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: theme.cyan }}>{avgLatency}ms</div>
        </Card>
      </div>

      {/* Error Type Distribution */}
      {errorTypes.length > 0 && (
        <Card>
 <h3 style={{ margin: '0 0 12px', fontSize: 14, color: theme.text }}>Distribuição de Erros (7d)</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {errorTypes.map((et: any) => (
              <span key={et.error_type} style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                background: et.error_type === 'credit' ? T.warningBg : et.error_type === 'auth' ? T.dangerBg : T.bgHover,
                color: et.error_type === 'credit' ? theme.warning : et.error_type === 'auth' ? theme.error : theme.muted,
 }}>{errorTypeEmoji[et.error_type] || ''} {et.error_type}: {et.cnt}</span>
            ))}
          </div>
        </Card>
      )}

      {/* Provider Success Rate */}
      {byProvider.length > 0 && (
        <Card>
 <h3 style={{ margin: '0 0 12px', fontSize: 14, color: theme.text }}>Success Rate por Provider (7d)</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                  {['Provider', 'Total', 'Successos', 'Success Rate', 'Avg Latency'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {byProvider.map((p: any) => (
                  <tr key={p.provider} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ padding: '6px 8px', color: theme.primary, fontWeight: 600 }}>{p.provider}</td>
                    <td style={{ padding: '6px 8px', color: theme.text }}>{p.total}</td>
                    <td style={{ padding: '6px 8px', color: theme.success }}>{p.successes}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 60, height: 6, background: theme.bg, borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${p.success_rate}%`, height: '100%', background: p.success_rate >= 90 ? theme.success : p.success_rate >= 70 ? theme.warning : theme.error, borderRadius: 3 }} />
                        </div>
                        <span style={{ color: p.success_rate >= 90 ? theme.success : p.success_rate >= 70 ? theme.warning : theme.error, fontWeight: 700 }}>{p.success_rate}%</span>
                      </div>
                    </td>
                    <td style={{ padding: '6px 8px', color: theme.muted }}>{p.avg_latency}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Per-Key Health Table */}
      <Card>
 <h3 style={{ margin: '0 0 12px', fontSize: 14, color: theme.text }}>Health por Chave (7d)</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                {['Key ID', 'Provider', 'Success Rate', 'Avg Latency', 'Last Error', 'Total'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {health.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: theme.dimmed }}>Sem dados de attempts nos últimos 7 dias</td></tr>
              ) : health.map((k) => (
                <tr key={k.key_id} style={{
                  borderBottom: `1px solid ${theme.border}`,
                  background: k.success_rate < 50 ? T.dangerBg : 'transparent',
                }}>
                  <td style={{ padding: '6px 8px', color: theme.text, fontFamily: 'monospace', fontSize: 10 }}>{k.key_id?.substring(0, 12)}...</td>
                  <td style={{ padding: '6px 8px', color: theme.primary, fontWeight: 600 }}>{k.provider}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 60, height: 6, background: theme.bg, borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${k.success_rate}%`, height: '100%', background: k.success_rate >= 90 ? theme.success : k.success_rate >= 70 ? theme.warning : theme.error, borderRadius: 3 }} />
                      </div>
                      <span style={{ color: k.success_rate >= 90 ? theme.success : k.success_rate >= 70 ? theme.warning : theme.error, fontWeight: 700 }}>{k.success_rate}%</span>
                    </div>
                  </td>
                  <td style={{ padding: '6px 8px', color: theme.muted }}>{Math.round(k.avg_latency)}ms</td>
                  <td style={{ padding: '6px 8px', color: theme.error, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }} title={k.last_error || ''}>
                    {k.last_error ? k.last_error.substring(0, 40) : '—'}
                    {k.last_error_at && <div style={{ fontSize: 9, color: theme.dimmed }}>{new Date(k.last_error_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>}
                  </td>
                  <td style={{ padding: '6px 8px', color: theme.text, fontWeight: 600 }}>{k.total_attempts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Códigos de Erro — Referência Suporte */}
      <Card>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, color: theme.text }}>🔖 Códigos de Erro — Referência</h3>
        <p style={{ margin: '0 0 12px', fontSize: 11, color: theme.dimmed }}>
          Quando o sistema retorna erro, o usuário vê uma mensagem amigável com um código. Use esta tabela para diagnosticar.
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                {['Código', 'Tipo', 'Causa', 'Ação Recomendada'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: theme.muted, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { code: 'E-001', type: 'Budget', emoji: '💰', cause: 'Limite de orçamento/saldo excedido', action: 'Recarregar wallet do cliente', color: theme.warning },
                { code: 'E-002', type: 'Sem Chaves', emoji: '🔑', cause: 'Nenhuma chave LLM ativa configurada', action: 'Ativar chave na aba Keys', color: theme.error },
                { code: 'E-003', type: 'Chaves Falharam', emoji: '❌', cause: 'Todas as chaves retornaram erro', action: 'Verificar health das chaves; rotacionar se necessário', color: theme.error },
                { code: 'E-004', type: 'Rate Limit', emoji: '⏱️', cause: 'Provedor retornou 429 (rate limit)', action: 'Aguardar cooldown ou trocar para chave com mais quota', color: theme.warning },
                { code: 'E-005', type: 'Sobrecarregado', emoji: '🔥', cause: 'Provedor retornou 529 (overloaded)', action: 'Aguardar; o provedor está sobrecarregado', color: theme.warning },
                { code: 'E-006', type: 'Timeout', emoji: '⏳', cause: 'Requisição demorou mais de 30s', action: 'Verificar status do provedor; pode ser instabilidade temporária', color: theme.muted },
                { code: 'E-099', type: 'Genérico', emoji: '⚠️', cause: 'Erro inesperado não categorizado', action: 'Verificar logs detalhados na aba Error Logs', color: theme.dimmed },
              ].map(row => (
                <tr key={row.code} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontWeight: 700, color: row.color, fontSize: 13 }}>{row.emoji} {row.code}</td>
                  <td style={{ padding: '6px 8px', color: theme.text, fontWeight: 600 }}>{row.type}</td>
                  <td style={{ padding: '6px 8px', color: theme.text }}>{row.cause}</td>
                  <td style={{ padding: '6px 8px', color: theme.muted, fontSize: 11 }}>{row.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function DashboardTab() {
  const [billingStats, setBillingStats] = useState<any>(null);
  const [customers, setCustomers] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [period, setPeriod] = useState('30d');
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [skipStats, setSkipStats] = useState<any>(null);

  async function loadBilling() {
    setLoadingBilling(true);
    try {
      const params = new URLSearchParams({ period });
      if (selectedCustomer) params.set('customer_id', selectedCustomer);
      const [statsRes, custRes, skipRes] = await Promise.all([
        api.get<{ stats: any }>(`/admin/router/billing/stats?${params}`).catch(() => ({ stats: null })),
        api.get<{ customers: any[] }>('/admin/router/billing/customers').catch(() => ({ customers: [] })),
        api.get<any>('/admin/router/analytics/skipped').catch(() => null),
      ]);
      setBillingStats(statsRes.stats);
      setCustomers(custRes.customers || []);
      setSkipStats(skipRes);
    } catch { /* */ }
    setLoadingBilling(false);
  }

  useEffect(() => { loadBilling(); }, [period, selectedCustomer]);

  const s = billingStats || { total_cost_provider: 0, total_revenue: 0, total_margin: 0, total_requests: 0, total_input_tokens: 0, total_output_tokens: 0, by_model: [] };
  const marginPct = s.total_revenue > 0 ? (s.total_margin / s.total_revenue * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp5 }}>
      {/* Filters */}
      <Card style={{ padding: '14px 18px' }}>
        <div style={{ display: 'flex', gap: theme.sp3, alignItems: 'center', flexWrap: 'wrap' }}>
 <span style={{ fontSize: 13, color: theme.muted, fontWeight: 600 }}>Período:</span>
          <div style={{ display: 'flex', gap: 4, background: T.bgSubtle, borderRadius: theme.r2, padding: 2 }}>
            {['today', '7d', '30d', 'all'].map(p => (
              <Button variant="ghost" key={p} onClick={() => setPeriod(p)} style={{
                padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: period === p ? T.primaryBg : 'transparent',
                color: period === p ? theme.primary : theme.muted, fontSize: 12, fontWeight: 600, fontFamily: theme.font,
                transition: 'all 0.15s',
              }}>
                {p === 'today' ? 'Hoje' : p === '7d' ? '7 dias' : p === '30d' ? '30 dias' : 'Total'}
              </Button>
            ))}
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{ fontSize: 12, color: theme.dimmed }}></span>
            <Select value={selectedCustomer} onChange={(v) => setSelectedCustomer(v)} style={{
              padding: '6px 12px', borderRadius: theme.r2, border: `1px solid ${theme.borderSolid}`,
              background: theme.bg, color: theme.text, fontSize: 12, fontFamily: theme.font, minWidth: 160,
            }} options={[{ value: '', label: 'Todos os usuários' }, ...customers.map((c: any) => ({
              value: c.customer_id,
              label: `${c.customer_id.substring(0, 8)}... — $${c.total_spent.toFixed(2)} (${c.total_requests} req)`
            }))]} />
          </div>
        </div>
      </Card>

      {/* Financial Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: theme.sp3 }}>
        {[
 { label: 'Gasto Providers', value: `$${s.total_cost_provider.toFixed(2)}`, icon: '', color: T.danger, sub: 'Custo real das APIs' },
 { label: 'Receita', value: `$${s.total_revenue.toFixed(2)}`, icon: '', color: theme.success, sub: 'Cobrado dos usuários' },
 { label: 'Lucro', value: `$${s.total_margin.toFixed(2)}`, icon: '', color: T.success, sub: `Margem ${marginPct.toFixed(1)}%` },
 { label: 'Requests', value: s.total_requests.toLocaleString(), icon: '', color: theme.primary, sub: `${((s.total_input_tokens + s.total_output_tokens) / 1_000_000).toFixed(2)}M tokens` },
        ].map((card, i) => (
          <Card key={i} style={{ padding: theme.sp5 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: theme.sp3 }}>
              <span style={{ fontSize: 18 }}>{card.icon}</span>
              <span style={{ fontSize: 10, color: theme.dimmed, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{card.label}</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: card.color }}>{card.value}</div>
            <div style={{ fontSize: 11, color: theme.dimmed, marginTop: 4 }}>{card.sub}</div>
          </Card>
        ))}
      </div>

      {/* Token Economy Card */}
      {skipStats && (
        <Card style={{ border: `1px solid ${T.successBg}`, background: T.successBg }}>
 <SectionHeader title="Tokens Economizados (24h)" subtitle="Heartbeats idle pulados + histórico truncado" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: theme.sp3, marginTop: theme.sp3 }}>
            <div>
              <div style={{ fontSize: 10, color: theme.dimmed, textTransform: 'uppercase', fontWeight: 600 }}>Heartbeats Skipped</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: theme.success }}>{(skipStats.skipped_24h || 0).toLocaleString()}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: theme.dimmed, textTransform: 'uppercase', fontWeight: 600 }}>Processados</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: theme.primary }}>{(skipStats.processed_24h || 0).toLocaleString()}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: theme.dimmed, textTransform: 'uppercase', fontWeight: 600 }}>Tokens Economizados</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: theme.success }}>
                {((skipStats.tokens_saved_estimate || 0) / 1_000_000).toFixed(1)}M
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: theme.dimmed, textTransform: 'uppercase', fontWeight: 600 }}>$ Economizado</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: theme.success }}>${(skipStats.cost_saved_estimate || 0).toFixed(2)}</div>
            </div>
          </div>
          {skipStats.skipped_24h > 0 && skipStats.processed_24h > 0 && (
            <div style={{ marginTop: theme.sp3, display: 'flex', height: 24, borderRadius: theme.r2, overflow: 'hidden', background: T.bgCard }}>
              <div style={{
                width: `${Math.max(5, skipStats.processed_24h / (skipStats.processed_24h + skipStats.skipped_24h) * 100)}%`,
                background: T.primaryBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: 10, color: T.text, fontWeight: 700 }}>Processados</span>
              </div>
              <div style={{
                flex: 1, background: T.successBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: 10, color: T.text, fontWeight: 700 }}>Skipped (economia)</span>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Profit Bar Visual */}
      {s.total_revenue > 0 && (
        <Card>
 <SectionHeader title="Resultado Financeiro" />
          <div style={{ display: 'flex', height: 36, borderRadius: theme.r2, overflow: 'hidden', background: T.bgCard }}>
            <div style={{
              width: `${Math.max(5, s.total_cost_provider / s.total_revenue * 100)}%`,
              background: T.dangerBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'width 0.5s',
            }}>
              <span style={{ fontSize: 11, color: T.text, fontWeight: 700, whiteSpace: 'nowrap', padding: '0 8px' }}>Custo ${s.total_cost_provider.toFixed(2)}</span>
            </div>
            <div style={{
              flex: 1, background: T.successBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontSize: 11, color: T.text, fontWeight: 700, whiteSpace: 'nowrap' }}>Lucro ${s.total_margin.toFixed(2)} ({marginPct.toFixed(0)}%)</span>
            </div>
          </div>
        </Card>
      )}

      {/* By Model Breakdown */}
      <Card>
 <SectionHeader title="Detalhamento por Modelo" />
        {s.by_model.length === 0 ? (
 <UiEmptyState title="Nenhum uso registrado" description="Sem dados para o período selecionado" />
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 700 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                  {['Provider', 'Modelo', 'Requests', 'Input', 'Output', 'Custo', 'Receita', 'Lucro'].map(h => (
                    <th key={h} style={{
                      padding: '10px 12px', textAlign: 'left', color: theme.dimmed,
                      fontWeight: 600, fontSize: 10, textTransform: 'uppercase',
                      letterSpacing: '0.5px', whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {s.by_model.map((row: any, i: number) => (
                  <tr key={i} style={{
                    borderBottom: `1px solid ${theme.border}`,
                    transition: 'background 0.15s',
                  }}>
                    <td style={{ padding: '10px 12px', color: theme.muted }}>{row.provider}</td>
                    <td style={{ padding: '10px 12px', color: theme.text, fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>{row.model}</td>
                    <td style={{ padding: '10px 12px', color: theme.text }}>{row.requests.toLocaleString()}</td>
                    <td style={{ padding: '10px 12px', color: theme.muted }}>{(row.input_tokens / 1000).toFixed(1)}K</td>
                    <td style={{ padding: '10px 12px', color: theme.muted }}>{(row.output_tokens / 1000).toFixed(1)}K</td>
                    <td style={{ padding: '10px 12px', color: T.danger, fontWeight: 600 }}>${row.cost_provider.toFixed(4)}</td>
                    <td style={{ padding: '10px 12px', color: theme.success, fontWeight: 600 }}>${row.revenue.toFixed(4)}</td>
                    <td style={{ padding: '10px 12px', color: T.success, fontWeight: 700 }}>${row.margin.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Selected Customer Details */}
      {selectedCustomer && (
        <div style={{
          padding: theme.sp6, borderRadius: theme.r5,
          background: T.primaryBg, border: `1px solid ${T.primaryBg}`,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.primary, marginBottom: theme.sp3 }}>
 Cliente: {selectedCustomer.substring(0, 12)}...
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: theme.sp3 }}>
            <div>
              <div style={{ fontSize: 11, color: theme.dimmed }}>Gasto no período</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: theme.text }}>${s.total_revenue.toFixed(4)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: theme.dimmed }}>Requests</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: theme.text }}>{s.total_requests}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: theme.dimmed }}>Tokens usados</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: theme.text }}>{((s.total_input_tokens + s.total_output_tokens) / 1000).toFixed(1)}K</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══ Global CSS (injected once) ═══

function GlobalStyles() {
  return (
    <style>{`
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      @keyframes slideInRight {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      /* Responsive tab labels */
      @media (max-width: 768px) {
        .tab-label { display: none !important; }
      }
      /* Scrollbar styling for tab bar */
      .elon-router-tabs::-webkit-scrollbar { height: 0; }
      .elon-router-tabs { -ms-overflow-style: none; scrollbar-width: none; }
      /* Focus visible for accessibility */
      button:focus-visible, input:focus-visible, select:focus-visible {
        outline: 2px solid ${T.primary};
        outline-offset: 2px;
      }
      /* Table row hover */
      table tbody tr:hover {
        background: var(--t-white-alpha-2);
      }
    `}</style>
  );
}

// ═══ Main Page ═══

// ═══ Error Logs Tab ═══

interface ErrorLog {
  id: string;
  timestamp: string;
  source: string;
  severity: string;
  error_type: string;
  message: string;
  details: any;
  customer_id: string;
  model: string;
  key_id: string;
  request_id: string;
  resolved: number;
}

function ErrorLogsTab() {
  const [logs, setLogs] = useState<ErrorLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [severity, setSeverity] = useState('all');
  const [source, setSource] = useState('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (severity !== 'all') params.set('severity', severity);
      if (source !== 'all') params.set('source', source);
      const res = await api.get<{ logs: ErrorLog[]; total: number; page: number }>(`/admin/router/error-logs?${params}`);
      setLogs(res.logs || []);
      setTotal(res.total || 0);
    } catch { setLogs([]); }
    setLoading(false);
  };

  const fetchCount = async () => {
    try {
      const res = await api.get<{ count: number }>('/admin/router/error-logs/count');
      errorLogCount.value = res.count || 0;
    } catch {}
  };

  useEffect(() => { fetchLogs(); fetchCount(); }, [page, severity, source]);
  useEffect(() => {
    const iv = setInterval(() => { fetchLogs(); fetchCount(); }, 30000);
    return () => clearInterval(iv);
  }, [page, severity, source]);

  const severityColor = (s: string) => s === 'error' ? T.danger : s === 'warning' ? T.warning : T.primary;

  const resolveAll = async () => {
    try {
      await api.post('/admin/router/error-logs/resolve', {});
      fetchLogs();
      fetchCount();
    } catch {}
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Select value={severity} onChange={(v) => { setSeverity(v); setPage(1); }}
          options={[{ value: 'all', label: 'Todas Severidades' }, { value: 'error', label: 'Error' }, { value: 'warning', label: 'Warning' }, { value: 'info', label: 'Info' }]}
          style={{ background: T.border, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
        <Select value={source} onChange={(v) => { setSource(v); setPage(1); }}
          options={[{ value: 'all', label: 'Todas Fontes' }, { value: 'bridge', label: 'Bridge' }, { value: 'proxy', label: 'Proxy' }, { value: 'billing', label: 'Billing' }, { value: 'routing', label: 'Routing' }, { value: 'callAnthropic', label: 'Anthropic' }, { value: 'callOpenAI', label: 'OpenAI' }, { value: 'callGoogle', label: 'Google' }]}
          style={{ background: T.border, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
        <Button onClick={resolveAll} variant="ghost" size="sm" style={{ background: T.border, color: T.textSecondary, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 12px', fontSize: 13 }}>
          Limpar Resolvidos
        </Button>
        <span style={{ color: T.textMuted, fontSize: 12, marginLeft: 'auto' }}>{total} registros • Auto-refresh 30s</span>
      </div>

      {loading && <ListSkeleton rows={3} />}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              <th style={{ padding: '8px 6px', textAlign: 'left', color: T.textSecondary }}>Timestamp</th>
              <th style={{ padding: '8px 6px', textAlign: 'left', color: T.textSecondary }}>Sev</th>
              <th style={{ padding: '8px 6px', textAlign: 'left', color: T.textSecondary }}>Fonte</th>
              <th style={{ padding: '8px 6px', textAlign: 'left', color: T.textSecondary }}>Tipo</th>
              <th style={{ padding: '8px 6px', textAlign: 'left', color: T.textSecondary }}>Mensagem</th>
              <th style={{ padding: '8px 6px', textAlign: 'left', color: T.textSecondary }}>Modelo</th>
              <th style={{ padding: '8px 6px', textAlign: 'left', color: T.textSecondary }}>Customer</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(log => (
              <>
                <tr key={log.id} onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                  style={{ borderBottom: '1px solid #1e293b', cursor: 'pointer', background: expanded === log.id ? T.border : 'transparent' }}>
                  <td style={{ padding: '6px', color: T.textSecondary, whiteSpace: 'nowrap' }}>{new Date(log.timestamp + 'Z').toLocaleString('pt-BR')}</td>
                  <td style={{ padding: '6px' }}><span style={{ color: severityColor(log.severity), fontWeight: 600 }}>{log.severity.toUpperCase()}</span></td>
                  <td style={{ padding: '6px', color: T.text }}>{log.source}</td>
                  <td style={{ padding: '6px', color: T.textSecondary }}>{log.error_type || '-'}</td>
                  <td style={{ padding: '6px', color: T.text, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.message}</td>
                  <td style={{ padding: '6px', color: T.textSecondary }}>{log.model || '-'}</td>
                  <td style={{ padding: '6px', color: T.textSecondary, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.customer_id ? log.customer_id.substring(0, 8) + '...' : '-'}</td>
                </tr>
                {expanded === log.id && (
                  <tr key={log.id + '-details'}>
                    <td colSpan={7} style={{ padding: '8px 12px', background: T.bgSecondary, borderBottom: `1px solid ${T.border}` }}>
                      <pre style={{ color: T.textSecondary, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
                        {JSON.stringify(log.details, null, 2) || 'Sem detalhes'}
                      </pre>
                      {log.request_id && <div style={{ color: T.textMuted, fontSize: 11, marginTop: 4 }}>Request ID: {log.request_id}</div>}
                      {log.key_id && <div style={{ color: T.textMuted, fontSize: 11 }}>Key ID: {log.key_id}</div>}
                    </td>
                  </tr>
                )}
              </>
            ))}
            {logs.length === 0 && !loading && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: T.textMuted }}>Nenhum log encontrado</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {total > 50 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <Button disabled={page <= 1} onClick={() => setPage(p => p - 1)} variant="ghost" size="xs"
            style={{ background: T.border, color: T.textSecondary, border: `1px solid ${T.border}`, borderRadius: 6, padding: '4px 12px' }}>← Anterior</Button>
          <span style={{ color: T.textMuted, fontSize: 12, alignSelf: 'center' }}>Página {page} de {Math.ceil(total / 50)}</span>
          <Button disabled={page >= Math.ceil(total / 50)} onClick={() => setPage(p => p + 1)} variant="ghost" size="xs"
            style={{ background: T.border, color: T.textSecondary, border: `1px solid ${T.border}`, borderRadius: 6, padding: '4px 12px' }}>Próxima →</Button>
        </div>
      )}
    </div>
  );
}

// ═══ Settings Tab ═══

interface SettingField {
  key: string;
  label: string;
  description: string;
  type: 'text' | 'number' | 'select' | 'toggle';
  options?: { value: string; label: string }[];
  default: string;
  unit?: string;
  group: string;
}

const SETTINGS_FIELDS: SettingField[] = [
  // ── Roteamento ──
  { key: 'strategy', label: 'Estratégia de Roteamento', description: 'Como os requests são distribuídos entre providers', type: 'select', options: [
    { value: 'balanced', label: 'Balanceado — Equilíbrio custo × qualidade' },
    { value: 'cost', label: 'Menor Custo — Prioriza providers mais baratos' },
    { value: 'quality', label: 'Melhor Qualidade — Prioriza modelos mais capazes' },
    { value: 'speed', label: 'Mais Rápido — Prioriza menor latência' },
    { value: 'random', label: 'Aleatório — Distribui aleatoriamente entre chaves' },
    { value: 'round_robin', label: 'Round Robin — Distribui igualmente' },
  ], default: 'balanced', group: 'routing' },
  { key: 'auto_routing_profile', label: 'Perfil Auto-Routing', description: 'Perfil usado quando o cliente não especifica modelo', type: 'select', options: [
    { value: 'balanced', label: 'Balanceado' },
    { value: 'quality', label: 'Qualidade' },
    { value: 'speed', label: 'Velocidade' },
  ], default: 'balanced', group: 'routing' },
  { key: 'max_retries', label: 'Máximo de Retentativas', description: 'Quantas vezes tentar outro provider se o primeiro falhar', type: 'number', default: '3', unit: 'tentativas', group: 'routing' },
  { key: 'timeout_ms', label: 'Timeout por Request', description: 'Tempo máximo de espera por resposta do provider', type: 'number', default: '30000', unit: 'ms', group: 'routing' },
  { key: 'max_history_messages', label: 'Máx Mensagens no Histórico', description: 'Limite de mensagens de contexto enviadas ao modelo', type: 'number', default: '50', unit: 'mensagens', group: 'routing' },
  { key: 'oauth_mode', label: 'OAuth Mode (info)', description: 'V1 (/api/v1/llm/v1/) usa Bridge VPS. V2 (/api/v1/llm/v2/) usa SDK direto no Worker. Endpoints separados — V1 não é afetada pela V2.', type: 'select', options: [
    { value: 'v1_bridge', label: 'V1 e V2 coexistem (URLs separadas)' },
  ], default: 'v1_bridge', group: 'routing' },
  { key: 'default_max_output_tokens', label: 'Limite de Output (default)', description: 'Máximo de tokens que o modelo pode gerar por resposta. Clientes herdam este valor. 0 = fallback 4096.', type: 'number', default: '0', unit: 'tokens', group: 'routing' },
  // ── Billing & Markup ──
  { key: 'default_markup_pct_in', label: 'Markup % Input (default)', description: 'Percentual adicionado ao custo de input do provider', type: 'number', default: '0', unit: '%', group: 'billing' },
  { key: 'default_markup_pct_out', label: 'Markup % Output (default)', description: 'Percentual adicionado ao custo de output do provider', type: 'number', default: '0', unit: '%', group: 'billing' },
  { key: 'default_markup_fixed_in', label: 'Markup Fixo Input (default)', description: 'Valor fixo em USD adicionado ao preço de input por 1M tokens', type: 'number', default: '0', unit: '$/1M', group: 'billing' },
  { key: 'default_markup_fixed_out', label: 'Markup Fixo Output (default)', description: 'Valor fixo em USD adicionado ao preço de output por 1M tokens', type: 'number', default: '0', unit: '$/1M', group: 'billing' },
  { key: 'default_markup_pct', label: 'Markup % Legado (fallback)', description: 'Usado como fallback se markup_pct_in/out não estiverem definidos', type: 'number', default: '0', unit: '%', group: 'billing' },
  { key: 'allow_negative_balance', label: 'Permitir Saldo Negativo', description: 'Se desativado, bloqueia uso de LLM quando a carteira chega a zero', type: 'toggle', default: 'false', group: 'billing' },
  { key: 'budget_limit_daily', label: 'Orçamento Diário Global', description: 'Limite de gasto diário total da plataforma com providers (segurança)', type: 'number', default: '50', unit: 'USD', group: 'billing' },
  { key: 'min_balance_warning', label: 'Alerta Saldo Baixo', description: 'Avisar o cliente quando o saldo ficar abaixo deste valor', type: 'number', default: '1', unit: 'USD', group: 'billing' },
  // ── Segurança & Limites ──
  { key: 'default_rpm_limit', label: 'RPM Padrão (por cliente)', description: 'Requests por minuto por cliente (0 = sem limite)', type: 'number', default: '60', unit: 'req/min', group: 'security' },
  { key: 'default_rph_limit', label: 'RPH Padrão (por cliente)', description: 'Requests por hora por cliente (0 = sem limite)', type: 'number', default: '0', unit: 'req/hr', group: 'security' },
  { key: 'default_rpd_limit', label: 'RPD Padrão (por cliente)', description: 'Requests por dia por cliente (0 = sem limite)', type: 'number', default: '0', unit: 'req/day', group: 'security' },
  { key: 'default_spend_limit_per_hour', label: 'Limite Gasto/Hora (por cliente)', description: 'Gasto máximo por hora por cliente (0 = sem limite)', type: 'number', default: '0', unit: 'USD/hr', group: 'security' },
  // ── Logs & Retenção ──
  { key: 'log_retention_days', label: 'Retenção de Logs', description: 'Quantos dias manter logs de requests no banco', type: 'number', default: '90', unit: 'dias', group: 'logs' },
  { key: 'error_log_retention_days', label: 'Retenção de Logs de Erro', description: 'Quantos dias manter logs de erros', type: 'number', default: '30', unit: 'dias', group: 'logs' },
  { key: 'analytics_retention_days', label: 'Retenção de Analytics', description: 'Quantos dias manter dados de analytics detalhados', type: 'number', default: '90', unit: 'dias', group: 'logs' },
];

const SETTING_GROUPS: { id: string; label: string; icon: string; description: string }[] = [
  { id: 'routing', label: 'Roteamento', icon: '', description: 'Estratégia, retentativas, timeouts e auto-routing' },
  { id: 'billing', label: 'Billing & Markup', icon: '', description: 'Markup, orçamento diário, saldo negativo' },
  { id: 'security', label: 'Limites & Segurança', icon: '', description: 'Rate limits padrão por cliente' },
  { id: 'logs', label: 'Logs & Retenção', icon: '', description: 'Retenção de logs, erros e analytics' },
];

function SettingsTab() {
  const [config, setConfig] = useState<Record<string, string>>({});
  const [edited, setEdited] = useState<Record<string, string>>({});
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keySummary, setKeySummary] = useState<{ provider: string; count: number; active: number; models: string[] }[]>([]);

  const loadSettings = async () => {
    setLoadingSettings(true);
    try {
      const [cfgRes, keysRes] = await Promise.all([
        api.get<any>('/admin/router/config'),
        api.get<any>('/admin/router/keys'),
      ]);
      const cfgData = cfgRes.data || cfgRes;
      const keysData = keysRes.data || keysRes;
      setConfig(cfgData.config || {});
      setEdited({});

      // Build key summary by provider
      const keysList = keysData.keys || [];
      const provMap: Record<string, { count: number; active: number; models: string[] }> = {};
      for (const k of keysList) {
        if (!provMap[k.provider]) provMap[k.provider] = { count: 0, active: 0, models: [] };
        provMap[k.provider].count++;
        if (k.is_active) provMap[k.provider].active++;
        if (k.model_prefix && !provMap[k.provider].models.includes(k.model_prefix)) {
          provMap[k.provider].models.push(k.model_prefix);
        }
      }
      setKeySummary(Object.entries(provMap).map(([provider, data]) => ({ provider, ...data })));
    } catch (e: any) {
      showToast('error', `Erro ao carregar configurações: ${e.message}`);
    }
    setLoadingSettings(false);
  };

  useEffect(() => { loadSettings(); }, []);

  const getValue = (field: SettingField) => {
    if (edited[field.key] !== undefined) return edited[field.key];
    if (config[field.key] !== undefined) return config[field.key];
    return field.default;
  };

  const handleChange = (key: string, value: string) => {
    setEdited(prev => ({ ...prev, [key]: value }));
  };

  const hasChanges = Object.keys(edited).length > 0;

  const saveAll = async () => {
    if (!hasChanges) return;
    setSaving(true);
    try {
      await api.put('/admin/router/config', edited);
      showToast('success', `${Object.keys(edited).length} configurações salvas`);
      setConfig(prev => ({ ...prev, ...edited }));
      setEdited({});
    } catch (e: any) {
      showToast('error', `Erro ao salvar: ${e.message}`);
    }
    setSaving(false);
  };

  const resetField = (key: string) => {
    setEdited(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  if (loadingSettings) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp4 }}>
        {[1, 2, 3].map(i => <Card key={i}><SkeletonBar width="60%" height={20} /><SkeletonBar width="80%" /></Card>)}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp5 }}>
      {/* Save bar */}
      {hasChanges && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 50,
          padding: '12px 20px', borderRadius: theme.r4,
          background: T.primaryBg, border: `1px solid ${theme.primary}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          backdropFilter: 'blur(12px)',
        }}>
          <span style={{ color: theme.text, fontSize: 13, fontWeight: 600 }}>
            {Object.keys(edited).length} alteração(ões) não salva(s)
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <SecondaryButton onClick={() => setEdited({})}>Descartar</SecondaryButton>
            <PrimaryButton onClick={saveAll} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar Tudo'}
            </PrimaryButton>
          </div>
        </div>
      )}

      {/* Keys Summary */}
      <Card>
        <SectionHeader title="Chaves Ativas por Provider" subtitle="Resumo das chaves LLM configuradas e ativas" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: theme.sp3, marginTop: theme.sp3 }}>
          {keySummary.length === 0 ? (
            <div style={{ color: theme.muted, fontSize: 13, padding: 16 }}>Nenhuma chave configurada</div>
          ) : keySummary.map(ks => {
            const prov = PROVIDERS[ks.provider];
            return (
              <div key={ks.provider} style={{
                padding: '14px 16px', borderRadius: theme.r4,
                background: ks.active > 0 ? T.successBg : T.dangerBg,
                border: `1px solid ${ks.active > 0 ? T.successBg : T.dangerBg}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 18 }}>{prov?.icon || ''}</span>
                  <span style={{ fontWeight: 700, color: prov?.color || theme.text, fontSize: 14 }}>{prov?.name || ks.provider}</span>
                </div>
                <div style={{ fontSize: 12, color: theme.muted }}>
                  <span style={{ color: T.success, fontWeight: 700 }}>{ks.active}</span> ativa(s) de {ks.count} total
                </div>
                {ks.models.length > 0 && (
                  <div style={{ fontSize: 11, color: theme.dimmed, marginTop: 4 }}>
                    Prefixos: {ks.models.join(', ')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Settings groups */}
      {SETTING_GROUPS.map(group => {
        const fields = SETTINGS_FIELDS.filter(f => f.group === group.id);
        return (
          <Card key={group.id}>
            <SectionHeader title={group.label} subtitle={group.description} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: theme.sp3 }}>
              {fields.map(field => {
                const value = getValue(field);
                const isEdited = edited[field.key] !== undefined;
                return (
                  <div key={field.key} style={{
                    display: 'flex', flexDirection: 'column', gap: 6,
                    padding: '12px 16px', borderRadius: theme.r3,
                    background: isEdited ? T.primaryBg : 'transparent',
                    border: isEdited ? `1px solid ${T.primaryBgStrong}` : `1px solid ${theme.border}`,
                    transition: 'all 0.15s',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{field.label}</div>
                        <div style={{ fontSize: 11, color: theme.dimmed, marginTop: 2 }}>{field.description}</div>
                      </div>
                      {isEdited && (
                        <Button onClick={() => resetField(field.key)} variant="ghost" size="xs" style={{
                          background: 'none', border: 'none', color: theme.muted, fontSize: 11,
                          padding: '2px 6px', borderRadius: 4,
                        }}>Resetar</Button>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                      {field.type === 'select' ? (
                        <Select
                          value={value}
                          onChange={(v) => handleChange(field.key, v)}
                          options={field.options || []}
                          style={{
                            flex: 1, maxWidth: 400, padding: '8px 12px', borderRadius: theme.r3,
                            background: theme.bg, border: `1px solid ${theme.borderSolid}`, color: theme.text,
                            fontSize: 13, fontFamily: theme.font,
                          }}
                        />
                      ) : field.type === 'toggle' ? (
                        <Button variant="ghost"
                          onClick={() => handleChange(field.key, value === 'true' ? 'false' : 'true')}
                          style={{
                            width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                            background: value === 'true' ? theme.success : T.bgActive,
                            position: 'relative', transition: 'background 0.2s',
                          }}
                        >
                          <span style={{
                            position: 'absolute', top: 3, left: value === 'true' ? 25 : 3,
                            width: 20, height: 20, borderRadius: '50%', background: '#fff',
                            transition: 'left 0.2s', boxShadow: `0 1px 3px ${T.shadowSm}`,
                          }} />
                        </Button>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Input
                            type={field.type === 'number' ? 'number' : 'text'}
                            value={value}
                            onChange={(v) => handleChange(field.key, v)}
                            style={{
                              width: field.type === 'number' ? 120 : 300, padding: '8px 12px',
                              borderRadius: theme.r3, background: theme.bg, border: `1px solid ${theme.borderSolid}`,
                              color: theme.text, fontSize: 13, fontFamily: theme.font,
                            }}
                          />
                          {field.unit && <span style={{ fontSize: 11, color: theme.dimmed }}>{field.unit}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ─── Pipeline Tab ───────────────────────────────────────────────────
function PipelineTab() {
  const S = {
    card: { background: T.bgCard, border: `1px solid ${T.bgHover}`, borderRadius: 12, padding: '20px 24px', marginBottom: 16 } as const,
    step: (active?: boolean) => ({
      display: 'flex', gap: 16, padding: '16px 20px', borderRadius: 10,
      background: active ? T.primaryBg : T.bgSubtle,
      border: `1px solid ${active ? T.primaryBg : T.border}`,
      marginBottom: 8,
    } as const),
    num: (color: string) => ({
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
      background: color, color: T.text, fontSize: 13, fontWeight: 700,
    } as const),
    arrow: { textAlign: 'center' as const, padding: '4px 0', color: T.textDisabled, fontSize: 18, letterSpacing: 2 },
    code: { background: T.shadowSm, borderRadius: 6, padding: '12px 16px', fontSize: 12, fontFamily: 'monospace', color: T.text, overflowX: 'auto' as const, whiteSpace: 'pre' as const, marginTop: 8, marginBottom: 8, border: `1px solid ${T.border}` },
    badge: (bg: string, color: string) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: bg, color, marginLeft: 8 }),
    h2: { fontSize: 18, fontWeight: 700, color: T.text, margin: '0 0 4px' },
    h3: { fontSize: 15, fontWeight: 600, color: T.text, margin: '0 0 6px' },
    p: { fontSize: 13, color: T.textSecondary, margin: '0 0 4px', lineHeight: 1.6 },
    detail: { fontSize: 12, color: T.textMuted, margin: '4px 0', lineHeight: 1.5 },
    warn: { fontSize: 12, color: T.warning, background: T.warningBg, border: `1px solid ${T.warningBg}`, borderRadius: 6, padding: '8px 12px', marginTop: 8 },
    ok: { fontSize: 12, color: T.success, background: T.successBg, border: `1px solid ${T.successBg}`, borderRadius: 6, padding: '8px 12px', marginTop: 8 },
    err: { fontSize: 12, color: T.danger, background: T.dangerBg, border: `1px solid ${T.dangerBg}`, borderRadius: 6, padding: '8px 12px', marginTop: 8 },
    table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 12, marginTop: 8 },
    th: { textAlign: 'left' as const, padding: '8px 12px', borderBottom: `1px solid ${T.bgActive}`, color: T.textSecondary, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' as const },
    td: { padding: '8px 12px', borderBottom: `1px solid ${T.bgCardHover}`, color: T.text },
  };

  return (
    <div>
      <h2 style={S.h2}>Pipeline de Processamento</h2>
      <p style={{ ...S.p, marginBottom: 20 }}>Visualizacao completa de como cada request e processado pelo Elon Router, desde a chamada do usuario ate a resposta do modelo.</p>

      {/* ═══ PIPELINE 1: API KEY (Direto) ═══ */}
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <h3 style={{ ...S.h3, fontSize: 17, margin: 0 }}>Pipeline 1: API Key (Direto)</h3>
          <span style={S.badge(T.successBg, T.success)}>SIMPLES</span>
        </div>
        <p style={S.detail}>Usado quando a chave LLM e uma API key normal (sk-ant-api03-..., sk-...). O Worker chama a API do provedor diretamente sem intermediarios.</p>

        <div style={S.step()}>
          <div style={S.num(T.primary)}>1</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Usuario envia request</div>
            <p style={S.detail}>O usuario (browser, API client, CMS) faz uma chamada para o Elon Router com um token de autenticacao (ert_live_xxx).</p>
            <div style={S.code}>{`POST ${window.location.origin}/api/v1/llm/v1/chat/completions
Headers:
  Authorization: Bearer ert_live_29ca3c6ead...
  Content-Type: application/json
Body:
  { "model": "openai/gpt-4o-mini", "messages": [...], "max_tokens": 1000 }`}</div>
          </div>
        </div>

        <div style={S.arrow}>|</div>

        <div style={S.step()}>
          <div style={S.num(T.primary)}>2</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Elon Router valida token</div>
            <p style={S.detail}>O Worker (Cloudflare) valida o token ert_live_xxx na tabela router_tokens do D1. Verifica: token existe? Esta ativo? Tem creditos?</p>
            <div style={S.code}>{`SELECT * FROM router_tokens WHERE token_hash = SHA256('ert_live_29ca3c6e...')
→ customer_id, balance, is_active, rate_limit`}</div>
            <p style={S.detail}>Se token invalido ou sem creditos → retorna 401/402 imediatamente.</p>
          </div>
        </div>

        <div style={S.arrow}>|</div>

        <div style={S.step()}>
          <div style={S.num(T.primary)}>3</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Resolve modelo e provedor</div>
            <p style={S.detail}>O Router extrai o provedor do nome do modelo (openai/gpt-4o-mini → provedor: openai, modelo: gpt-4o-mini). Aplica aliases se necessario (MODEL_ALIASES).</p>
            <div style={S.code}>{`Entrada: "openai/gpt-4o-mini"
→ provider = "openai"
→ model = "gpt-4o-mini"

Entrada: "claude-sonnet-4.5"  (alias)
→ MODEL_ALIASES["claude-sonnet-4.5"] = "claude-sonnet-4-5"
→ provider = "anthropic" (detectado por prefixo "claude")

Entrada: "auto"
→ classifyAction(messages) → "chat" | "code" | "analysis"
→ resolveAutoModel("chat", "balanced") → "claude-sonnet-4-5"`}</div>
          </div>
        </div>

        <div style={S.arrow}>|</div>

        <div style={S.step()}>
          <div style={S.num(T.primary)}>4</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Busca API key do provedor</div>
            <p style={S.detail}>O Router busca a chave API ativa com maior prioridade para o provedor na tabela llm_keys. A chave esta encriptada (AES-256-GCM) e e descriptografada com MASTER_ENCRYPTION_KEY.</p>
            <div style={S.code}>{`SELECT id, api_key_encrypted, label, auth_type FROM llm_keys
WHERE provider = 'openai' AND is_active = 1
ORDER BY priority ASC LIMIT 1

→ decrypt(api_key_encrypted, MASTER_KEY) → "sk-proj-abc123..."
→ auth_type = "api" (chave normal, NAO OAuth)`}</div>
            <div style={S.warn}>Se auth_type = "oauth" → NAO usa esta pipeline. Vai para Pipeline 2 (OAuth Bridge).</div>
          </div>
        </div>

        <div style={S.arrow}>|</div>

        <div style={S.step()}>
          <div style={S.num(T.primary)}>5</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Chama API do provedor diretamente</div>
            <p style={S.detail}>O Worker faz fetch direto para a API do provedor (OpenAI, Google, etc.) com a API key no header.</p>
            <div style={S.code}>{`POST https://api.openai.com/v1/chat/completions
Headers:
  Authorization: Bearer sk-proj-abc123...
  Content-Type: application/json
Body:
  { "model": "gpt-4o-mini", "messages": [...], "max_tokens": 1000 }`}</div>
            <p style={S.detail}>Timeout: 30 segundos. Se falhar, loga erro e retorna ao usuario.</p>
          </div>
        </div>

        <div style={S.arrow}>|</div>

        <div style={S.step()}>
          <div style={S.num(T.primary)}>6</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Processa resposta e retorna</div>
            <p style={S.detail}>O Router normaliza a resposta para formato OpenAI-compatible, calcula custos, loga usage, debita creditos e retorna ao usuario.</p>
            <div style={S.code}>{`1. Normaliza resposta → formato OpenAI (choices, usage, etc.)
2. Calcula custo: input_tokens * cost_per_1k_input / 1000
3. Debita creditos: UPDATE router_tokens SET balance = balance - cost
4. Loga: INSERT INTO router_logs (model, tokens, cost, latency)
5. Adiciona metadata: _elon_router: { provider, cost, latency_ms }
6. Retorna 200 com resposta completa`}</div>
          </div>
        </div>

        <div style={S.ok}>Pipeline API Key: Worker chama provedor diretamente. Rapido, simples, sem intermediarios. Funciona para OpenAI, Google, e Anthropic com API keys normais (sk-ant-api03-...).</div>
      </div>

      {/* ═══ PIPELINE 2: OAuth Bridge ═══ */}
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <h3 style={{ ...S.h3, fontSize: 17, margin: 0 }}>Pipeline 2: OAuth Bridge (Anthropic)</h3>
          <span style={S.badge(T.warningBg, T.warning)}>COMPLEXA</span>
          <span style={S.badge(T.purpleBg, T.purple)}>OBRIGATORIA PARA OAUTH</span>
        </div>
        <p style={S.detail}>Usado quando a chave Anthropic e um token OAuth (sk-ant-oat01-...). Tokens OAuth NAO funcionam via HTTP direto — obrigatorio passar pelo Bridge que usa o SDK Anthropic internamente.</p>
        <div style={S.err}>CRITICO: Tokens OAuth (sk-ant-oat01-...) NAO funcionam via curl/fetch direto para api.anthropic.com. Nenhuma combinacao de headers resolve. Somente o SDK Anthropic consegue negociar OAuth. Por isso o Bridge existe.</div>

        <div style={{ marginTop: 16 }}>
          <div style={S.step(true)}>
            <div style={S.num(T.warning)}>1</div>
            <div style={{ flex: 1 }}>
              <div style={S.h3}>Usuario envia request</div>
              <p style={S.detail}>Identico a Pipeline 1. O usuario chama o Elon Router com token ert_live_xxx e um modelo Anthropic.</p>
              <div style={S.code}>{`POST ${window.location.origin}/api/v1/llm/v1/chat/completions
Headers:
  Authorization: Bearer ert_live_29ca3c6ead...
Body:
  { "model": "claude-opus-4.6", "messages": [...] }`}</div>
            </div>
          </div>

          <div style={S.arrow}>|</div>

          <div style={S.step(true)}>
            <div style={S.num(T.warning)}>2</div>
            <div style={{ flex: 1 }}>
              <div style={S.h3}>Validacao de token (igual Pipeline 1)</div>
              <p style={S.detail}>Valida ert_live_xxx no D1, verifica creditos, rate limit. Identico ao passo 2 da Pipeline 1.</p>
            </div>
          </div>

          <div style={S.arrow}>|</div>

          <div style={S.step(true)}>
            <div style={S.num(T.warning)}>3</div>
            <div style={{ flex: 1 }}>
              <div style={S.h3}>Resolve modelo + detecta Anthropic</div>
              <p style={S.detail}>O Router resolve o alias do modelo e detecta que e Anthropic (prefixo "claude" ou provider "anthropic").</p>
              <div style={S.code}>{`Entrada: "claude-opus-4.6"
→ MODEL_ALIASES["claude-opus-4.6"] = "claude-opus-4-6"
→ Detecta: modelo contem "claude" → provider = anthropic`}</div>
            </div>
          </div>

          <div style={S.arrow}>|</div>

          <div style={S.step(true)}>
            <div style={S.num(T.warning)}>4</div>
            <div style={{ flex: 1 }}>
              <div style={S.h3}>Verifica se precisa de Bridge</div>
              <p style={S.detail}>O Router verifica 2 condicoes no D1: (1) bridge_config habilitado? (2) Existe chave OAuth ativa para Anthropic?</p>
              <div style={S.code}>{`-- Verifica bridge habilitado
SELECT * FROM bridge_config WHERE id = 1 AND enabled = 1
→ url: "${window.location.origin}/bridge", ip: "72.61.60.200"

-- Verifica se tem chave OAuth ativa
SELECT id FROM llm_keys
WHERE provider = 'anthropic' AND auth_type = 'oauth' AND is_active = 1
LIMIT 1
→ Se encontrou → forceViaBridge = true`}</div>
              <div style={S.warn}>Se auth_type = "oauth" em QUALQUER chave Anthropic ativa → Bridge e OBRIGATORIO. Mesmo que bridge_config esteja desabilitado, a presenca de chave OAuth forca o uso do bridge.</div>
            </div>
          </div>

          <div style={S.arrow}>|</div>

          <div style={S.step(true)}>
            <div style={S.num(T.warning)}>5</div>
            <div style={{ flex: 1 }}>
              <div style={S.h3}>Worker envia request para o Bridge</div>
              <p style={S.detail}>O Worker (CF) faz fetch para o Bridge via Cloudflare Tunnel. NAO envia token OAuth no header — o Bridge tem seu proprio token.</p>
              <div style={S.code}>{`POST ${window.location.origin}/bridge/anthropic-bridge/v1/messages
Headers:
  Content-Type: application/json
  X-Key-Label: "nome-da-chave"     ← Apenas metadata (log)
  (SEM Authorization header!)       ← CRITICO!

Body (formato Anthropic nativo):
  {
    "model": "claude-opus-4-6",
    "messages": [{"role": "user", "content": "..."}],
    "system": "...",
    "max_tokens": 4096,
    "temperature": 0.7
  }`}</div>
              <div style={S.err}>NUNCA enviar Authorization: Bearer para o Bridge! As chaves do llm_keys (D1) podem ser diferentes do token do Vault e causar falhas. O Bridge DEVE usar seu proprio token. (Bug descoberto em 2026-03-16, commit 589e3247)</div>
            </div>
          </div>

          <div style={S.arrow}>|</div>

          <div style={S.step(true)}>
            <div style={S.num(T.purple)}>6</div>
            <div style={{ flex: 1 }}>
              <div style={S.h3}>Bridge recebe o request (VPS 72.61.60.200)</div>
              <p style={S.detail}>O servidor Bridge (Node.js + Express, rodando em PM2) recebe o request via Cloudflare Tunnel. Como NAO tem Bearer header, usa o client SDK inicializado com o token do Vault.</p>
              <div style={S.code}>{`// Bridge verifica se tem Bearer no header
const authHeader = req.headers['authorization'] || '';
const bearerToken = authHeader.replace(/^Bearer\\s+/i, '');

if (bearerToken && bearerToken.startsWith('sk-ant-')) {
    // Modo DEPRECATED: usa key do caller (NAO recomendado)
    client = new Anthropic({ authToken: bearerToken, ... });
} else {
    // Modo CORRETO: usa token do Vault (cached)
    client = anthropicClient;  // ← ESTE CAMINHO
}`}</div>
            </div>
          </div>

          <div style={S.arrow}>|</div>

          <div style={S.step(true)}>
            <div style={S.num(T.purple)}>7</div>
            <div style={{ flex: 1 }}>
              <div style={S.h3}>Bridge busca token OAuth do Vault</div>
              <p style={S.detail}>Na inicializacao (e a cada 5 minutos), o Bridge busca o token OAuth do Elon Password Vault via API publica.</p>
              <div style={S.code}>{`GET ${window.location.origin}/api/v1/bridge/oauth-token
→ Response: {
    "oauth_token": "sk-ant-oat01-iwVb4Hz...",
    "source": "elon_password_vault",
    "configured": true
  }

Cache local: 5 minutos (memoria)
Se Vault cai → usa cache
Se cache expira e Vault cai → ERRO (correto!)`}</div>
              <p style={S.detail}>O token esta encriptado no Vault (AES-256-GCM) e descriptografado pelo endpoint usando MASTER_ENCRYPTION_KEY. O Bridge NUNCA ve a chave de encriptacao.</p>
              <div style={S.warn}>FONTE UNICA: O token OAuth vem EXCLUSIVAMENTE do Elon Password Vault. Nunca do .env, nunca do header, nunca do D1 direto. Uma unica fonte de verdade.</div>
            </div>
          </div>

          <div style={S.arrow}>|</div>

          <div style={S.step(true)}>
            <div style={S.num(T.purple)}>8</div>
            <div style={{ flex: 1 }}>
              <div style={S.h3}>Bridge cria SDK client com OAuth</div>
              <p style={S.detail}>O Bridge inicializa o SDK Anthropic no modo OAuth. Este e o "macete" — a configuracao exata que faz OAuth funcionar.</p>
              <div style={S.code}>{`const client = new Anthropic({
    apiKey: null,           // OBRIGATORIO: null (nao undefined!)
    authToken: oauthToken,  // Token sk-ant-oat01-... do Vault
    dangerouslyAllowBrowser: true,
    defaultHeaders: {
        'accept': 'application/json',
        'anthropic-beta': 'claude-code-20250219,oauth-2025-04-20,fine-grained-tool-streaming-2025-05-14',
        'user-agent': 'claude-cli/2.1.2 (external, cli)',
        'x-app': 'cli',
    }
});`}</div>
              <p style={{ ...S.detail, marginTop: 8 }}>Headers obrigatorios para OAuth:</p>
              <table style={S.table}>
                <thead><tr><th style={S.th}>Header</th><th style={S.th}>Valor</th><th style={S.th}>Por que</th></tr></thead>
                <tbody>
                  <tr><td style={S.td}><code>apiKey</code></td><td style={S.td}><code>null</code></td><td style={S.td}>Impede SDK de enviar X-Api-Key (OAuth nao usa x-api-key)</td></tr>
                  <tr><td style={S.td}><code>authToken</code></td><td style={S.td}><code>sk-ant-oat01-...</code></td><td style={S.td}>SDK envia como Authorization: Bearer (modo OAuth)</td></tr>
                  <tr><td style={S.td}><code>anthropic-beta</code></td><td style={S.td}><code>claude-code-20250219, oauth-2025-04-20</code></td><td style={S.td}>Habilita OAuth + identifica como Claude Code</td></tr>
                  <tr><td style={S.td}><code>user-agent</code></td><td style={S.td}><code>claude-cli/2.1.2</code></td><td style={S.td}>Identifica como CLI oficial (obrigatorio para OAuth)</td></tr>
                  <tr><td style={S.td}><code>x-app</code></td><td style={S.td}><code>cli</code></td><td style={S.td}>Marca como aplicacao CLI autorizada</td></tr>
                </tbody>
              </table>
              <div style={S.err}>SEM estes headers, OAuth retorna "Internal server error" para TODOS os modelos. Nao existe combinacao de headers via curl que funcione — SOMENTE o SDK com esta configuracao exata.</div>
            </div>
          </div>

          <div style={S.arrow}>|</div>

          <div style={S.step(true)}>
            <div style={S.num(T.purple)}>9</div>
            <div style={{ flex: 1 }}>
              <div style={S.h3}>SDK faz chamada OAuth para Anthropic</div>
              <p style={S.detail}>O SDK Anthropic v0.73.0 internamente negocia OAuth com a API. Isso inclui handshake TLS especial, token exchange, e metadata de sessao que NAO sao replicaveis via curl.</p>
              <div style={S.code}>{`// Internamente o SDK faz:
1. authHeaders() → buildHeaders([apiKeyAuth(), bearerAuth()])
   → apiKey = null → apiKeyAuth() retorna undefined (sem X-Api-Key)
   → authToken = "sk-ant-oat01-..." → Bearer header

2. buildHeaders() monta:
   - Authorization: Bearer sk-ant-oat01-...
   - anthropic-version: 2023-06-01
   - anthropic-beta: claude-code-20250219,oauth-2025-04-20,...
   - user-agent: claude-cli/2.1.2
   - x-app: cli
   - Content-Type: application/json

3. POST https://api.anthropic.com/v1/messages
   (com todos os headers acima + body do request)`}</div>
              <p style={S.detail}>O SDK pode fazer pre-flight requests ou token exchange interno que curl nao replica. Por isso a necessidade do Bridge.</p>
            </div>
          </div>

          <div style={S.arrow}>|</div>

          <div style={S.step(true)}>
            <div style={S.num(T.success)}>10</div>
            <div style={{ flex: 1 }}>
              <div style={S.h3}>Anthropic processa e responde</div>
              <p style={S.detail}>A API Anthropic valida o OAuth token, processa o request e retorna a resposta no formato nativo Anthropic.</p>
              <div style={S.code}>{`Response (formato Anthropic nativo):
{
    "id": "msg_01XFDUDYJgAACzvnptvVoYEL",
    "type": "message",
    "role": "assistant",
    "content": [{ "type": "text", "text": "Ola! Como posso ajudar?" }],
    "model": "claude-opus-4-6-20260205",
    "stop_reason": "end_turn",
    "usage": {
        "input_tokens": 12,
        "output_tokens": 15,
        "cache_creation_input_tokens": 0,
        "cache_read_input_tokens": 0
    }
}`}</div>
            </div>
          </div>

          <div style={S.arrow}>|</div>

          <div style={S.step(true)}>
            <div style={S.num(T.success)}>11</div>
            <div style={{ flex: 1 }}>
              <div style={S.h3}>Bridge retorna resposta ao Worker</div>
              <p style={S.detail}>O Bridge retorna a resposta Anthropic diretamente (JSON) ao Worker via Cloudflare Tunnel. Nenhuma transformacao — passa a resposta raw.</p>
            </div>
          </div>

          <div style={S.arrow}>|</div>

          <div style={S.step(true)}>
            <div style={S.num(T.success)}>12</div>
            <div style={{ flex: 1 }}>
              <div style={S.h3}>Worker normaliza e retorna ao usuario</div>
              <p style={S.detail}>O Elon Router converte a resposta Anthropic para formato OpenAI-compatible, calcula custos, loga, debita creditos e retorna.</p>
              <div style={S.code}>{`1. Converte Anthropic → OpenAI format:
   content[0].text → choices[0].message.content
   usage.input_tokens → usage.prompt_tokens

2. Calcula custo (baseado em pricing da tabela llm_keys)
3. Debita creditos do token ert_live_xxx
4. Loga em router_logs (model, tokens, cost, latency)
5. Adiciona _elon_router metadata

Response final ao usuario:
{
    "id": "chatcmpl-...",
    "choices": [{ "message": { "content": "Ola!" } }],
    "usage": { "prompt_tokens": 12, "completion_tokens": 15 },
    "_elon_router": { "provider": "anthropic", "cost": 0.0012, "latency_ms": 3200 }
}`}</div>
            </div>
          </div>

          <div style={S.ok}>Pipeline OAuth: Mais complexa (12 passos vs 6), mas obrigatoria para tokens OAuth. Funciona com TODOS os modelos Claude incluindo Opus 4.6.</div>
        </div>
      </div>

      {/* ═══ PIPELINE 3: V2 — SDK Direto no Worker ═══ */}
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <h3 style={{ ...S.h3, fontSize: 17, margin: 0 }}>Pipeline 3: V2 — SDK Direto no Worker</h3>
          <span style={S.badge(T.successBg, T.success)}>RAPIDO</span>
        </div>
        <p style={S.detail}>V2 elimina o Bridge/VPS intermediario. O SDK Anthropic roda DENTRO do CF Worker, chamando a API diretamente. Billing assincrono (waitUntil) reduz latencia percebida.</p>

        <div style={S.step()}>
          <div style={S.num(T.primary)}>1</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Cliente envia request</div>
            <p style={S.detail}>Chamada identica ao V1, mas no endpoint /v2/. Mesmo formato OpenAI-compatible.</p>
            <div style={S.code}>{`POST ${window.location.origin}/api/v1/llm/v2/chat/completions
Headers:
  Authorization: Bearer ert_live_xxx
Body:
  { "model": "claude-sonnet-4.5", "messages": [...], "max_tokens": 4096 }`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step()}>
          <div style={S.num(T.purple)}>2</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Token validation (cached)</div>
            <p style={S.detail}>v2TokenCache: Map em memoria com 60s TTL e max 500 entries. Cache hit pula query D1 (economia ~200ms). Cache miss faz SELECT router_tokens WHERE token_hash = SHA256(token). Eviction: quando ultrapassa 500 entries, deleta a entrada mais antiga.</p>
            <div style={S.code}>{`v2TokenCache (Map<string, { data: TokenRow, ts: number }>)
  TTL: 60s | Max entries: 500
  Hit → retorna TokenRow imediato (0ms D1)
  Miss → SELECT * FROM router_tokens WHERE token_hash = SHA256(?)
  Eviction: if (cache.size > 500) deleteOldest()`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step()}>
          <div style={S.num(T.purple)}>3</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Pre-auth sync (balance check)</div>
            <p style={S.detail}>billing.preAuth(customerId) verifica saldo ANTES de chamar a API. Se balance = 0, retorna 402 Payment Required imediatamente. Bloqueia usuarios sem credito antes de gastar compute na API do provedor.</p>
            <div style={S.code}>{`const preAuth = await billing.preAuth(customerId);
if (preAuth.balance <= 0) {
  return c.json({ error: "Payment Required", balance: 0 }, 402);
}
// Saldo OK → continua para resolucao de modelo`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step()}>
          <div style={S.num(T.purple)}>4</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Resolve modelo V2</div>
            <p style={S.detail}>V2 usa V2_MODEL_ALIASES separado do V1, com resolveV2Model(). Mapeamento de nomes amigaveis para IDs reais da Anthropic API.</p>
            <div style={S.code}>{`V2_MODEL_ALIASES:
  claude-sonnet-4.5  → claude-sonnet-4-5
  claude-opus-4.6    → claude-opus-4-6
  claude-haiku-4.5   → claude-haiku-4-5
  auto               → claude-sonnet-4-5

resolveV2Model(input) → string (ID real da API)`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step(true)}>
          <div style={S.num(T.warning)}>5</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Busca OAuth token do Vault via DB</div>
            <p style={S.detail}>VaultService faz query direta no D1 (NAO self-fetch — CF Workers nao podem fazer fetch para si mesmos). Token OAuth cacheado em memoria com 5min TTL. Token formato: sk-ant-oat01-iwVb4Hz-... do Elon Password Vault.</p>
            <div style={S.code}>{`VaultService.getOAuthToken(env.DB)
  → SELECT decrypted_value FROM vault WHERE key = 'anthropic_oauth'
  Cache: cachedOAuthToken { token: string, ts: number }
  TTL: 5min (300s)
  NAO usa fetch() — query D1 direta (CF Worker limitation)`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step(true)}>
          <div style={S.num(T.warning)}>6</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Cria Anthropic SDK client no Worker</div>
            <p style={S.detail}>Instancia o SDK Anthropic diretamente no CF Worker com authToken do Vault. Client cacheado (cachedAnthropicClient) para reusar entre requests. dangerouslyAllowBrowser: true necessario para ambiente Worker.</p>
            <div style={S.code}>{`const client = new Anthropic({
  authToken: cachedOAuthToken.token,
  dangerouslyAllowBrowser: true
});
// Client cacheado: cachedAnthropicClient
// Reusa entre requests (mesmo isolate)
// SDK roda DENTRO do CF Worker (sem VPS)`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step(true)}>
          <div style={S.num(T.danger)}>7</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>SDK chama Anthropic API</div>
            <p style={S.detail}>client.messages.create() chama api.anthropic.com diretamente. Non-stream first (create()) para menor latencia. Se retorna 500, invalida cache V2 e faz retry 1x.</p>
            <div style={S.code}>{`const response = await client.messages.create({
  model: resolvedModel,  // ex: "claude-sonnet-4-5"
  messages: messages,
  max_tokens: maxTokens
});
// Se 500 → invalidateV2Cache() + retry 1x
// Non-stream: create() (nao stream())`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step()}>
          <div style={S.num(T.success)}>8</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Resposta ao cliente</div>
            <p style={S.detail}>Converte formato nativo Anthropic para OpenAI-compatible. Adiciona _elon_router metadata com version "v2", latencia medida, e modelo usado. cost_provider: "pending" porque billing ainda nao completou (async).</p>
            <div style={S.code}>{`// Anthropic format → OpenAI-compatible
{
  choices: [{ message: { content: response.content[0].text } }],
  model: resolvedModel,
  _elon_router: {
    version: "v2",
    latency_ms: Date.now() - startTime,
    model: resolvedModel,
    cost_provider: "pending"  // billing async
  }
}`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step()}>
          <div style={S.num(T.success)}>9</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Billing ASYNC (background)</div>
            <p style={S.detail}>c.executionCtx.waitUntil(billingPromise) cobra creditos DEPOIS de enviar resposta ao cliente. Se billing falha, retry 1x. Resultado: cliente recebe resposta mais rapido pois nao espera billing completar.</p>
            <div style={S.code}>{`// Resposta ja enviada ao cliente!
c.executionCtx.waitUntil(async () => {
  try {
    await billing.charge(customerId, usage);
  } catch (e) {
    await billing.charge(customerId, usage); // retry 1x
  }
});
// Cliente NAO espera billing — latencia reduzida`}</div>
          </div>
        </div>

        <div style={S.ok}>Pipeline V2: 9 passos, sem intermediarios. SDK roda no Worker. Billing async. Latencia: Haiku ~2-3s, Sonnet ~3-8s, Opus ~4-5s. Reliability: 100% estavel.</div>
      </div>

      {/* ═══ PIPELINE 4: V3 — Kubernetes Proxy ═══ */}
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <h3 style={{ ...S.h3, fontSize: 17, margin: 0 }}>Pipeline 4: V3 — Kubernetes Proxy</h3>
          <span style={S.badge(T.primaryBg, T.primary)}>ULTRA RAPIDO</span>
        </div>
        <p style={S.detail}>V3 usa um pod Kubernetes com SDK client WARM (TCP connections persistentes). CF Worker age como proxy leve, delegando processamento LLM ao pod. Auto-scaling HPA de 2 a 50 pods.</p>

        <div style={S.step()}>
          <div style={S.num(T.primary)}>1</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Cliente envia request</div>
            <p style={S.detail}>Mesmo formato dos outros pipelines, endpoint /v3/.</p>
            <div style={S.code}>{`POST ${window.location.origin}/api/v1/llm/v3/chat/completions
Headers:
  Authorization: Bearer ert_live_xxx
Body:
  { "model": "claude-sonnet-4.5", "messages": [...], "max_tokens": 4096 }`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step()}>
          <div style={S.num(T.purple)}>2</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Token validation (compartilha v2TokenCache)</div>
            <p style={S.detail}>Mesmo cache do V2 — Map em memoria, 60s TTL, max 500 entries. Compartilhado entre V2 e V3 no mesmo Worker isolate.</p>
            <div style={S.code}>{`// Reusa v2TokenCache (mesmo Map)
// Cache hit → 0ms | Cache miss → SELECT D1
// TTL: 60s | Max: 500 entries`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step()}>
          <div style={S.num(T.purple)}>3</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Pre-auth sync (balance check)</div>
            <p style={S.detail}>Mesmo billing.preAuth() do V2. Verifica saldo antes de fazer proxy para o pod K8s. 402 se sem credito.</p>
            <div style={S.code}>{`const preAuth = await billing.preAuth(customerId);
if (preAuth.balance <= 0) return c.json({ error: "Payment Required" }, 402);`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step(true)}>
          <div style={S.num(T.warning)}>4</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>CF Worker faz proxy para K8s pod</div>
            <p style={S.detail}>Worker faz fetch para proxy-v3.yourdomain.com/v3/messages. Worker age como proxy leve — NAO processa LLM, apenas encaminha request e resposta.</p>
            <div style={S.code}>{`const proxyResponse = await fetch(
  'https://proxy-v3.yourdomain.com/v3/messages',
  {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Internal-Key': internalKey },
    body: JSON.stringify({ model, messages, max_tokens })
  }
);
// Worker = proxy leve (NAO processa LLM)`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step(true)}>
          <div style={S.num(T.warning)}>5</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Pod K8s recebe request</div>
            <p style={S.detail}>Express server em proxy-v3/src/index.js. SDK client WARM com TCP connections reutilizadas (persistent). Token OAuth do Vault refreshed automaticamente a cada 5min.</p>
            <div style={S.code}>{`// proxy-v3/src/index.js (Express server no pod K8s)
// SDK client WARM — TCP connections reutilizadas
// Token OAuth refresh automatico: setInterval(refreshToken, 5 * 60 * 1000)
// Connection pool: keep-alive TCP para api.anthropic.com`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step(true)}>
          <div style={S.num(T.danger)}>6</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>SDK chama Anthropic (warm connection)</div>
            <p style={S.detail}>client.messages.create() com TCP connection ja estabelecida (warm) — sem handshake TLS a cada request. Retry 3x com backoff (1s, 2s) em caso de 500.</p>
            <div style={S.code}>{`const response = await client.messages.create({
  model, messages, max_tokens
});
// TCP connection ja estabelecida (warm) — sem TLS handshake
// Retry: 3x com backoff (1s, 2s) em caso de 500
// Latencia significativamente menor que cold start`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step()}>
          <div style={S.num(T.success)}>7</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Resposta volta</div>
            <p style={S.detail}>Pod retorna resposta ao CF Worker que adiciona _elon_router metadata (version: "v3") e encaminha ao cliente. Cadeia: Pod K8s retorna ao CF Worker retorna ao Cliente.</p>
            <div style={S.code}>{`// Pod → CF Worker → Cliente
// Worker adiciona metadata:
{
  _elon_router: {
    version: "v3",
    latency_ms: totalLatency,
    proxy_latency_ms: proxyLatency
  }
}`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step()}>
          <div style={S.num(T.success)}>8</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Billing ASYNC</div>
            <p style={S.detail}>Mesmo pattern V2 — waitUntil() no Worker. Cobra creditos depois de enviar resposta. Cliente nao espera billing.</p>
            <div style={S.code}>{`c.executionCtx.waitUntil(billing.charge(customerId, usage));
// Mesmo pattern V2 — async, retry 1x`}</div>
          </div>
        </div>
        <div style={S.arrow}>---</div>

        <div style={S.step()}>
          <div style={S.num(T.success)}>9</div>
          <div style={{ flex: 1 }}>
            <div style={S.h3}>Auto-scaling (HPA)</div>
            <p style={S.detail}>HorizontalPodAutoscaler escala de 2 a 50 pods baseado em CPU. Scale up em 70% CPU utilization. Scale down stabilization: 300s para evitar flapping.</p>
            <div style={S.code}>{`HorizontalPodAutoscaler:
  minReplicas: 2
  maxReplicas: 50
  metrics:
    - type: Resource
      resource:
        name: cpu
        target:
          type: Utilization
          averageUtilization: 70
  behavior:
    scaleDown:
      stabilizationWindowSeconds: 300`}</div>
          </div>
        </div>

        <div style={S.ok}>Pipeline V3: 9 passos, K8s pod com warm connections. Latencia: Haiku ~1s, Sonnet ~2-4s, Opus ~3-4s. Reliability: 100% (warm + retry + multi-pod). Auto-scaling 2-50 pods.</div>
      </div>

      {/* ═══ COMPARACAO ═══ */}
      <div style={S.card}>
        <h3 style={{ ...S.h3, marginBottom: 12 }}>Comparacao: Todos os Pipelines</h3>
        <div style={{ overflowX: 'auto' as const }}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Aspecto</th>
              <th style={S.th}>P1: API Key</th>
              <th style={S.th}>P2: OAuth Bridge (V1)</th>
              <th style={S.th}>P3: V2 SDK Worker</th>
              <th style={S.th}>P4: V3 K8s Proxy</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={S.td}>Passos</td><td style={S.td}>6</td><td style={S.td}>12</td><td style={S.td}>9</td><td style={S.td}>9</td></tr>
            <tr><td style={S.td}>Intermediarios</td><td style={S.td}>Nenhum</td><td style={S.td}>Bridge + Tunnel</td><td style={S.td}>Nenhum (SDK no Worker)</td><td style={S.td}>K8s Pod</td></tr>
            <tr><td style={S.td}>Latencia Haiku</td><td style={S.td}>~1s</td><td style={S.td}>~5s</td><td style={S.td}>~2-3s</td><td style={S.td}>~1s</td></tr>
            <tr><td style={S.td}>Latencia Sonnet</td><td style={S.td}>~2s</td><td style={S.td}>~6-12s</td><td style={S.td}>~3-8s</td><td style={S.td}>~2-4s</td></tr>
            <tr><td style={S.td}>Latencia Opus</td><td style={S.td}>~3s</td><td style={S.td}>~9-10s</td><td style={S.td}>~4-5s</td><td style={S.td}>~3-4s</td></tr>
            <tr><td style={S.td}>Reliability</td><td style={S.td}>99%+</td><td style={S.td}>~80-95%</td><td style={S.td}>100%</td><td style={S.td}>100%</td></tr>
            <tr><td style={S.td}>Billing</td><td style={S.td}>Sincrono</td><td style={S.td}>Sincrono</td><td style={S.td}>ASYNC (waitUntil)</td><td style={S.td}>ASYNC (waitUntil)</td></tr>
            <tr><td style={S.td}>Token source</td><td style={S.td}>llm_keys (D1)</td><td style={S.td}>Vault (via Bridge)</td><td style={S.td}>Vault (via DB)</td><td style={S.td}>Vault (refresh 5min)</td></tr>
            <tr><td style={S.td}>Infra</td><td style={S.td}>CF Worker</td><td style={S.td}>CF Worker + VPS + Tunnel</td><td style={S.td}>CF Worker</td><td style={S.td}>CF Worker + K8s</td></tr>
            <tr><td style={S.td}>Escalabilidade</td><td style={S.td}>Infinita</td><td style={S.td}>Limitada (1 VPS)</td><td style={S.td}>Infinita</td><td style={S.td}>2-50 pods (HPA)</td></tr>
            <tr><td style={S.td}>TCP</td><td style={S.td}>Cold (stateless)</td><td style={S.td}>Warm (VPS)</td><td style={S.td}>Cold (stateless)</td><td style={S.td}>Warm (persistent)</td></tr>
            <tr><td style={S.td}>Provedores</td><td style={S.td}>Todos</td><td style={S.td}>Anthropic</td><td style={S.td}>Anthropic</td><td style={S.td}>Anthropic</td></tr>
          </tbody>
        </table>
        </div>
      </div>

      {/* ═══ DIAGRAMA VISUAL ═══ */}
      <div style={S.card}>
        <h3 style={{ ...S.h3, marginBottom: 12 }}>Diagrama Visual</h3>
        <div style={S.code}>{`Pipeline 1 (API Key):
  Usuario → CF Worker → API Provedor → Resposta
    [1]       [2-4]        [5]          [6]

Pipeline 2 (OAuth Bridge — V1):
  Usuario → CF Worker → CF Tunnel → Bridge (VPS) → SDK → Anthropic → Resposta
    [1]       [2-5]       [5]        [6-8]       [9]     [10]       [11-12]

Pipeline 3 (V2 — SDK Worker):
  Usuario → CF Worker (SDK direto) → Anthropic → Resposta
    [1]       [2-6]                    [7]        [8-9]

Pipeline 4 (V3 — K8s Proxy):
  Usuario → CF Worker → K8s Pod (SDK warm) → Anthropic → Resposta
    [1]       [2-3]       [4-6]                [6]        [7-9]

Detalhamento do Bridge (passos 6-9):
  ┌──────────────────────────────────────────────┐
  │ Bridge VPS (72.61.60.200:3000)               │
  │                                              │
  │  [6] Recebe request (sem Bearer!)            │
  │       ↓                                      │
  │  [7] Busca token do Vault (cache 5min)       │
  │       GET yourdomain.com/api/v1/bridge/       │
  │           oauth-token                        │
  │       ↓                                      │
  │  [8] Cria SDK Anthropic:                     │
  │       apiKey: null                           │
  │       authToken: "sk-ant-oat01-..."          │
  │       anthropic-beta: claude-code-20250219   │
  │       user-agent: claude-cli/2.1.2           │
  │       ↓                                      │
  │  [9] SDK chama api.anthropic.com             │
  │       (handshake OAuth interno)              │
  └──────────────────────────────────────────────┘`}</div>
      </div>

      {/* ═══ BUGS HISTORICOS ═══ */}
      <div style={S.card}>
        <h3 style={{ ...S.h3, marginBottom: 12 }}>Bugs Historicos e Licoes Aprendidas</h3>

        <div style={{ ...S.step(), flexDirection: 'column' as const, gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={S.badge(T.dangerBg, T.danger)}>BUG 1</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Worker enviava Bearer para o Bridge (2026-03-16)</span>
          </div>
          <p style={S.detail}>O Elon Router enviava Authorization: Bearer com a chave do llm_keys para o Bridge. Essa chave era DIFERENTE do token no Vault. Resultado: Opus 4.6 retornava "Internal server error", Haiku as vezes funcionava.</p>
          <p style={S.detail}><strong>Fix:</strong> Remover Authorization header da chamada Worker → Bridge. Bridge usa seu proprio token do Vault. Commit: 589e3247</p>
        </div>

        <div style={{ ...S.step(), flexDirection: 'column' as const, gap: 8, marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={S.badge(T.dangerBg, T.danger)}>BUG 2</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Endpoint do Vault usava /admin/ (auth required)</span>
          </div>
          <p style={S.detail}>Bridge chamava /api/v1/admin/bridge/oauth-token que requer sessao admin (cookie). Como Bridge nao tem sessao, retornava 401.</p>
          <p style={S.detail}><strong>Fix:</strong> Criado endpoint publico /api/v1/bridge/oauth-token (sem auth). Commit: bridge-public.handler.ts</p>
        </div>

        <div style={{ ...S.step(), flexDirection: 'column' as const, gap: 8, marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={S.badge(T.dangerBg, T.danger)}>BUG 3</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>model='auto' nao roteava para Bridge</span>
          </div>
          <p style={S.detail}>Quando modelo era 'auto', o Router nao detectava como Anthropic e tentava chamar direto. OAuth falhava.</p>
          <p style={S.detail}><strong>Fix:</strong> Se forceViaBridge=true e modelo='auto', resolve para claude-sonnet-4-5 antes de chamar bridge. Commit: d974a544</p>
        </div>

        <div style={{ ...S.step(), flexDirection: 'column' as const, gap: 8, marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={S.badge(T.dangerBg, T.danger)}>BUG 4</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>OAuth via curl/fetch retorna "Internal server error"</span>
          </div>
          <p style={S.detail}>NAO e bug — e comportamento esperado. Tokens OAuth (sk-ant-oat01-...) NAO funcionam via HTTP direto. Testamos TODAS as combinacoes de headers possiveis. Somente o SDK Anthropic consegue negociar OAuth.</p>
          <p style={S.detail}><strong>Solucao:</strong> Bridge com SDK. Nao tentar curl direto para Anthropic com OAuth.</p>
        </div>
      </div>
    </div>
  );
}

// ═══ Sticky Key Assignments Tab ═══
// Aba para visualizar e gerenciar atribuições sticky de chaves por token

function StickyKeyAssignmentsTab() {
  const [assignments, setAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/admin/router/key-assignments', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || ''}` },
      });
      const data = await res.json();
      setAssignments(data.data?.assignments || []);
    } catch (err) {
      console.error('[StickyKeys] Erro ao carregar:', err);
    }
    setLoading(false);
  };

  useEffect(() => { loadAssignments(); }, []);

  const handleReassign = async (tokenId: string) => {
    try {
      await fetch(`/api/v1/admin/router/key-assignments/${tokenId}/reassign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || ''}` },
      });
      loadAssignments();
    } catch (err) {
      console.error('[StickyKeys] Erro ao reatribuir:', err);
    }
  };

  const handleDelete = async (tokenId: string) => {
    try {
      await fetch(`/api/v1/admin/router/key-assignments/${tokenId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token') || ''}` },
      });
      loadAssignments();
    } catch (err) {
      console.error('[StickyKeys] Erro ao remover:', err);
    }
  };

  if (loading) return <div style={{ padding: 20, textAlign: 'center' }}>Carregando atribuições...</div>;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>🔑 Sticky Key Assignments</h3>
        <Button variant="outline" size="sm" onClick={loadAssignments}>🔄 Atualizar</Button>
      </div>
      <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
        Cada token (agente/cliente) é atribuído a uma chave Anthropic fixa para maximizar cache hits do Prompt Caching.
      </p>
      {assignments.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
          Nenhuma atribuição sticky ainda. Atribuições são criadas automaticamente no primeiro request de cada token.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ textAlign: 'left', padding: '8px 6px' }}>Token</th>
              <th style={{ textAlign: 'left', padding: '8px 6px' }}>Agente</th>
              <th style={{ textAlign: 'left', padding: '8px 6px' }}>Chave Atribuída</th>
              <th style={{ textAlign: 'center', padding: '8px 6px' }}>Requests</th>
              <th style={{ textAlign: 'center', padding: '8px 6px' }}>Fallbacks</th>
              <th style={{ textAlign: 'left', padding: '8px 6px' }}>Última Request</th>
              <th style={{ textAlign: 'center', padding: '8px 6px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {assignments.map((a: any) => (
              <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '6px', fontFamily: 'monospace', fontSize: 11 }}>
                  {a.token_name || a.token_id?.substring(0, 12) + '...'}
                </td>
                <td style={{ padding: '6px' }}>{a.agent_name || '—'}</td>
                <td style={{ padding: '6px' }}>
                  <span style={{ background: '#e3f2fd', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>
                    {a.key_label || a.key_id?.substring(0, 12)}
                  </span>
                </td>
                <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600 }}>{a.request_count || 0}</td>
                <td style={{ padding: '6px', textAlign: 'center', color: (a.fallback_count || 0) > 0 ? '#e65100' : '#888' }}>
                  {a.fallback_count || 0}
                </td>
                <td style={{ padding: '6px', fontSize: 11, color: '#666' }}>
                  {a.last_used_at ? new Date(a.last_used_at).toLocaleString('pt-BR') : '—'}
                </td>
                <td style={{ padding: '6px', textAlign: 'center' }}>
                  <Button variant="ghost" size="sm" onClick={() => handleReassign(a.token_id)} title="Re-atribuir chave">
                    🔄
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(a.token_id)} title="Remover sticky">
                    🗑️
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const ROUTER_TAB_IDS = ['keys', 'routing', 'users', 'tokens', 'dashboard', 'billing', 'bridge', 'monitoring', 'analytics', 'pricing', 'aliases', 'health', 'logs', 'settings', 'pipeline', 'sticky'] as const;

export function ElonRouterPage() {
  useTabRoute(activeTab, '/admin/elon-router', 'keys', ROUTER_TAB_IDS);
  useEffect(() => { loadData(); }, []);

  return (
    <div style={{ padding: '24px 16px', maxWidth: 1100, margin: '0 auto', fontFamily: theme.font }}>
      <GlobalStyles />
      <ToastContainer />

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: theme.sp4, marginBottom: theme.sp6,
        padding: '18px 22px', borderRadius: theme.r6, background: theme.gradientSubtle,
        border: `1px solid ${theme.borderSolid}`, flexWrap: 'wrap',
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: theme.r5, flexShrink: 0,
          background: theme.gradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, boxShadow: theme.shadowGlow,
 }}></div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: theme.text }}>
            Elon Router
          </h1>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: theme.muted }}>
            Roteamento inteligente de LLMs — menor custo, melhor resultado
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%', background: theme.success,
            boxShadow: `0 0 8px ${theme.success}`,
          }} />
          <span style={{ fontSize: 12, color: theme.success, fontWeight: 600 }}>Online</span>
        </div>
      </div>

      {/* Tabs — horizontal scroll on mobile */}
      <div className="elon-router-tabs" style={{
        display: 'flex', gap: 4, marginBottom: theme.sp6,
        background: T.bgSubtle, padding: 4, borderRadius: theme.r4,
        overflowX: 'auto', WebkitOverflowScrolling: 'touch',
      }}>
 <TabButton id="keys" label="Chaves LLM" icon="" count={keys.value.length} />
 <TabButton id="routing" label="Roteamento" icon="" />
 <TabButton id="users" label="Usuários" icon="" count={userOverrides.value.length} />
 <TabButton id="tokens" label="Meus Tokens" icon="" count={tokens.value.length} />
 <TabButton id="analytics" label="Analytics" icon="" />
 <TabButton id="health" label="Health Chaves" icon="" />
 <TabButton id="logs" label="Logs" icon="" count={errorLogCount.value || undefined} />
 <TabButton id="pricing" label="Custos LLM" icon="" />
 <TabButton id="aliases" label="Aliases" icon="" />
 <TabButton id="dashboard" label="Dashboard" icon="" />
 <TabButton id="billing" label="Billing" icon="" />
 <TabButton id="bridge" label="Ponte OAuth" icon="" />
 <TabButton id="monitoring" label="Monitoramento" icon="" />
 <TabButton id="pipeline" label="Pipeline" icon="" />
 <TabButton id="sticky" label="🔑 Key Assignments" icon="" />
 <TabButton id="settings" label="Configurações" icon="" />
      </div>

      {/* Tab content */}
      {loading.value ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp4 }}>
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.sp3 }}>
                <SkeletonBar width="40%" height={18} />
                <SkeletonBar width="70%" />
                <SkeletonBar width="55%" />
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <>
          {activeTab.value === 'keys' && <KeysTab />}
          {activeTab.value === 'routing' && <RoutingTab />}
          {activeTab.value === 'users' && <UsersTab />}
          {activeTab.value === 'tokens' && <TokensTab />}
          {activeTab.value === 'analytics' && <AnalyticsTab />}
          {activeTab.value === 'pricing' && <PricingTab />}
          {activeTab.value === 'aliases' && <AliasesTab />}
          {activeTab.value === 'dashboard' && <DashboardTab />}
          {activeTab.value === 'billing' && <BillingTab />}
          {activeTab.value === 'bridge' && <BridgeTab />}
          {activeTab.value === 'monitoring' && <BridgeMonitoringTab />}
          {activeTab.value === 'health' && <HealthTab />}
          {activeTab.value === 'logs' && <ErrorLogsTab />}
          {activeTab.value === 'pipeline' && <PipelineTab />}
          {activeTab.value === 'sticky' && <StickyKeyAssignmentsTab />}
          {activeTab.value === 'settings' && <SettingsTab />}
        </>
      )}
    </div>
  );
}
